/**
 * 简单的国际化工具
 * 提供语言切换和文本翻译功能
 */
import {reactive, ref, watch} from 'vue'
import {ElMessage} from "element-plus";

// 语言存储键名
const STORAGE_KEY = 'APP_LANGUAGE'

// 可用语言
export const LANGUAGES = {
    ZH: 'zh',
    EN: 'en'
}

// 默认语言
const DEFAULT_LANGUAGE = LANGUAGES.ZH

// 当前语言
const currentLanguage = ref(
    localStorage.getItem(STORAGE_KEY) ||
    (navigator.language.toLowerCase().includes('zh') ? LANGUAGES.ZH : LANGUAGES.EN)
)

// 监听语言变化，保存到localStorage
watch(currentLanguage, (newLang) => {
    localStorage.setItem(STORAGE_KEY, newLang)
    document.documentElement.setAttribute('lang', newLang)
})

// 语言显示名称
export const languageNames = {
    [LANGUAGES.ZH]: '中文-简体',
    [LANGUAGES.EN]: 'English'
}

/**
 * 翻译映射表
 * 格式: { [key]: { [lang]: value } }
 */
const translations = reactive({

    'register.formInvalid':{
        [LANGUAGES.ZH]: '注册信息有误，重新检查填写',
        [LANGUAGES.EN]: 'Registration information is incorrect, please check and re-enter'
    },

    'register.inviterId': {
        [LANGUAGES.ZH]: '邀请ID（选填）',
        [LANGUAGES.EN]: 'Inviter ID (Optional)'
    },
    'register.inviterIdPlaceholder': {
        [LANGUAGES.ZH]: '请输入邀请ID',
        [LANGUAGES.EN]: 'Please input inviter ID'
    },

    'user.notLoggedIn': {
        [LANGUAGES.ZH]: '用户未登录',
        [LANGUAGES.EN]: 'User not logged in'
    },
    'dashboard.load.user.failed': {
        [LANGUAGES.ZH]: '获取用户消息失败',
        [LANGUAGES.EN]: 'Failed to load user messages'
    },
    'detail.cancel.content': {
        [LANGUAGES.ZH]: '取消',
        [LANGUAGES.EN]: 'cancel'
    },
    'detail.confirm.content': {
        [LANGUAGES.ZH]: '确认',
        [LANGUAGES.EN]: 'confirm'
    },
    'detail.title.title': {
        [LANGUAGES.ZH]: '提示',
        [LANGUAGES.EN]: 'Tip'
    },
    'detail.title.content': {
        [LANGUAGES.ZH]: '查看详细信息需要消耗一次次数，是否继续？',
        [LANGUAGES.EN]: 'Continue to view detailed information for one time cost?'
    },
    'export.info.exporting': {
        [LANGUAGES.ZH]: '正在准备导出数据，请稍候...',
        [LANGUAGES.EN]: 'Preparing data for export, please wait...'
    },
    'export.warning.largeData': {
        [LANGUAGES.ZH]: '正在导出大量数据，请耐心等待，过程可能需要几分钟...',
        [LANGUAGES.EN]: 'Exporting large amount of data, please be patient, it may take a few minutes...'
    },
    'email.send.success': {
        [LANGUAGES.ZH]: '邮件发送成功',
        [LANGUAGES.EN]: 'Email Send Success'
    },
    'email.table.actions': {
        [LANGUAGES.ZH]: '操作',
        [LANGUAGES.EN]: 'Operation'
    },
    // 品牌
    'brand.name': {
        [LANGUAGES.ZH]: '数海领航',
        [LANGUAGES.EN]: 'Light Foam'
    },

    // 通用
    'common.welcome': {
        [LANGUAGES.ZH]: '欢迎登录',
        [LANGUAGES.EN]: 'Welcome'
    },
    'common.login': {
        [LANGUAGES.ZH]: '登录',
        [LANGUAGES.EN]: 'Login'
    },
    'common.password': {
        [LANGUAGES.ZH]: '密码',
        [LANGUAGES.EN]: 'Password'
    },
    'common.reset': {
        [LANGUAGES.ZH]: '重置',
        [LANGUAGES.EN]: 'Reset'
    },
    'common.actions': {
        [LANGUAGES.ZH]: '操作',
        [LANGUAGES.EN]: 'Actions'
    },
    'common.tips': {
        [LANGUAGES.ZH]: '提示',
        [LANGUAGES.EN]: 'Tips'
    },
    'common.confirm': {
        [LANGUAGES.ZH]: '确定',
        [LANGUAGES.EN]: 'Confirm'
    },
    'common.cancel': {
        [LANGUAGES.ZH]: '取消',
        [LANGUAGES.EN]: 'Cancel'
    },
    'common.register': {
        [LANGUAGES.ZH]: '注册',
        [LANGUAGES.EN]: 'Register'
    },
    'common.back': {
        [LANGUAGES.ZH]: '返回',
        [LANGUAGES.EN]: 'Back'
    },

    // 登录页面
    'login.title': {
        [LANGUAGES.ZH]: '全球企业获客系统',
        [LANGUAGES.EN]: 'Global Enterprise Lead Generation System'
    },
    'login.subtitle': {
        [LANGUAGES.ZH]: '一站式海外营销解决方案',
        [LANGUAGES.EN]: 'One-stop Overseas Marketing Solution'
    },
    'login.feature1': {
        [LANGUAGES.ZH]: '全球B2B企业库，亿级企业覆盖',
        [LANGUAGES.EN]: 'Global B2B enterprise library, billions of enterprises covered'
    },
    'login.feature2': {
        [LANGUAGES.ZH]: '领英、Facebook、推特、广交会采购商多渠道获客',
        [LANGUAGES.EN]: 'Multi-channel leads: LinkedIn, Facebook, Twitter, Canton Fair buyers'
    },
    'login.feature3': {
        [LANGUAGES.ZH]: '企业搜索、智能搜邮、邮件反查、域名反查、社媒反查',
        [LANGUAGES.EN]: 'Enterprise search, Smart email search, Email lookup, Domain lookup, Social media lookup'
    },
    'login.feature4': {
        [LANGUAGES.ZH]: '免费企业邮箱群发，推广系统返佣',
        [LANGUAGES.EN]: 'Free enterprise email group sending, Promotion system rebates'
    },
    'login.footer': {
        [LANGUAGES.ZH]: '数海领航海外大数据',
        [LANGUAGES.EN]: 'Light Foam Overseas Big Data'
    },
    'login.passwordLogin': {
        [LANGUAGES.ZH]: '密码登录',
        [LANGUAGES.EN]: 'Password Login'
    },
    'login.smsLogin': {
        [LANGUAGES.ZH]: '短信登录',
        [LANGUAGES.EN]: 'SMS Login'
    },
    'login.usernamePlaceholder': {
        [LANGUAGES.ZH]: '用户名/手机号',
        [LANGUAGES.EN]: 'Username/Phone'
    },
    'login.mobilePlaceholder': {
        [LANGUAGES.ZH]: '请输入手机号',
        [LANGUAGES.EN]: 'Please input phone number'
    },
    'login.codePlaceholder': {
        [LANGUAGES.ZH]: '请输入验证码',
        [LANGUAGES.EN]: 'Please input verification code'
    },
    'login.getCode': {
        [LANGUAGES.ZH]: '获取验证码',
        [LANGUAGES.EN]: 'Get Code'
    },
    'login.mobileRequired': {
        [LANGUAGES.ZH]: '请输入手机号',
        [LANGUAGES.EN]: 'Please input phone number'
    },
    'login.mobileInvalid': {
        [LANGUAGES.ZH]: '手机号格式不正确',
        [LANGUAGES.EN]: 'Invalid phone number format'
    },
    'login.codeRequired': {
        [LANGUAGES.ZH]: '请输入验证码',
        [LANGUAGES.EN]: 'Please input verification code'
    },
    'login.codeInvalid': {
        [LANGUAGES.ZH]: '验证码格式不正确',
        [LANGUAGES.EN]: 'Invalid verification code format'
    },
    'login.codeSent': {
        [LANGUAGES.ZH]: '验证码已发送',
        [LANGUAGES.EN]: 'Verification code sent'
    },
    'login.codeSendFailed': {
        [LANGUAGES.ZH]: '验证码发送失败',
        [LANGUAGES.EN]: 'Failed to send verification code'
    },
    'login.loginFailed': {
        [LANGUAGES.ZH]: '登录失败',
        [LANGUAGES.EN]: 'Login failed'
    },
    'login.firstLoginSetPassword': {
        [LANGUAGES.ZH]: '首次登录，请设置密码',
        [LANGUAGES.EN]: 'First login, please set password'
    },
    'login.setPassword': {
        [LANGUAGES.ZH]: '设置密码',
        [LANGUAGES.EN]: 'Set Password'
    },
    'login.setPasswordTips': {
        [LANGUAGES.ZH]: '请设置您的登录密码',
        [LANGUAGES.EN]: 'Please set your login password'
    },
    'login.newPassword': {
        [LANGUAGES.ZH]: '新密码',
        [LANGUAGES.EN]: 'New Password'
    },
    'login.passwordSetSuccess': {
        [LANGUAGES.ZH]: '密码设置成功',
        [LANGUAGES.EN]: 'Password set successfully'
    },
    'login.passwordSetFailed': {
        [LANGUAGES.ZH]: '密码设置失败',
        [LANGUAGES.EN]: 'Failed to set password'
    },
    'login.passwordSetCanceled': {
        [LANGUAGES.ZH]: '已取消设置密码',
        [LANGUAGES.EN]: 'Password setup canceled'
    },
    'login.usernameRequired': {
        [LANGUAGES.ZH]: '请输入用户名',
        [LANGUAGES.EN]: 'Please input username'
    },
    'login.passwordRequired': {
        [LANGUAGES.ZH]: '请输入密码',
        [LANGUAGES.EN]: 'Please input password'
    },
    'login.agreement': {
        [LANGUAGES.ZH]: '我已阅读并同意',
        [LANGUAGES.EN]: 'I have read and agree to the'
    },
    'login.agreementRequired': {
        [LANGUAGES.ZH]: '请阅读并同意服务条款和隐私政策',
        [LANGUAGES.EN]: 'Please read and agree to the Terms of Service and Privacy Policy'
    },
    'login.terms': {
        [LANGUAGES.ZH]: '服务条款',
        [LANGUAGES.EN]: 'Terms of Service'
    },
    'login.privacy': {
        [LANGUAGES.ZH]: '隐私政策',
        [LANGUAGES.EN]: 'Privacy Policy'
    },
    'login.forgotPassword': {
        [LANGUAGES.ZH]: '忘记密码？',
        [LANGUAGES.EN]: 'Forgot Password?'
    },
    'login.noAccount': {
        [LANGUAGES.ZH]: '没有账号？',
        [LANGUAGES.EN]: 'No account?'
    },
    'login.register': {
        [LANGUAGES.ZH]: '立即注册',
        [LANGUAGES.EN]: 'Register Now'
    },
    'login.loginSuccess': {
        [LANGUAGES.ZH]: '登录成功',
        [LANGUAGES.EN]: 'Login successful'
    },

    // 注册页面
    'register.title': {
        [LANGUAGES.ZH]: '全球企业获客系统',
        [LANGUAGES.EN]: 'Global Enterprise Lead Generation System'
    },
    'register.subtitle': {
        [LANGUAGES.ZH]: '一站式海外营销解决方案',
        [LANGUAGES.EN]: 'One-stop Overseas Marketing Solution'
    },
    'register.welcome': {
        [LANGUAGES.ZH]: '欢迎注册',
        [LANGUAGES.EN]: 'Welcome to Register'
    },
    'register.username': {
        [LANGUAGES.ZH]: '用户名',
        [LANGUAGES.EN]: 'Username'
    },
    'register.usernamePlaceholder': {
        [LANGUAGES.ZH]: '请输入用户名',
        [LANGUAGES.EN]: 'Please input username'
    },
    'register.phone': {
        [LANGUAGES.ZH]: '手机号',
        [LANGUAGES.EN]: 'Phone Number'
    },
    'register.phonePlaceholder': {
        [LANGUAGES.ZH]: '请输入手机号',
        [LANGUAGES.EN]: 'Please input phone number'
    },
    'register.email': {
        [LANGUAGES.ZH]: '邮箱',
        [LANGUAGES.EN]: 'Email'
    },
    'register.emailPlaceholder': {
        [LANGUAGES.ZH]: '请输入邮箱',
        [LANGUAGES.EN]: 'Please input email'
    },
    'register.password': {
        [LANGUAGES.ZH]: '密码',
        [LANGUAGES.EN]: 'Password'
    },
    'register.passwordPlaceholder': {
        [LANGUAGES.ZH]: '请输入密码',
        [LANGUAGES.EN]: 'Please input password'
    },
    'register.confirmPassword': {
        [LANGUAGES.ZH]: '确认密码',
        [LANGUAGES.EN]: 'Confirm Password'
    },
    'register.confirmPasswordPlaceholder': {
        [LANGUAGES.ZH]: '请再次输入密码',
        [LANGUAGES.EN]: 'Please input password again'
    },
    'register.agreement': {
        [LANGUAGES.ZH]: '我已阅读并同意',
        [LANGUAGES.EN]: 'I have read and agree to the'
    },
    'register.terms': {
        [LANGUAGES.ZH]: '服务条款',
        [LANGUAGES.EN]: 'Terms of Service'
    },
    'register.privacy': {
        [LANGUAGES.ZH]: '隐私政策',
        [LANGUAGES.EN]: 'Privacy Policy'
    },
    'register.submit': {
        [LANGUAGES.ZH]: '注册',
        [LANGUAGES.EN]: 'Register'
    },
    'register.alreadyHaveAccount': {
        [LANGUAGES.ZH]: '已有账号？',
        [LANGUAGES.EN]: 'Already have an account?'
    },
    'register.login': {
        [LANGUAGES.ZH]: '去登录',
        [LANGUAGES.EN]: 'Login'
    },
    'register.usernameRequired': {
        [LANGUAGES.ZH]: '请输入用户名',
        [LANGUAGES.EN]: 'Please input username'
    },
    'register.usernameLength': {
        [LANGUAGES.ZH]: '用户名长度在3-20个字符之间',
        [LANGUAGES.EN]: 'Username length should be 3-20 characters'
    },
    'register.phoneRequired': {
        [LANGUAGES.ZH]: '请输入手机号',
        [LANGUAGES.EN]: 'Please input phone number'
    },
    'register.phoneInvalid': {
        [LANGUAGES.ZH]: '手机号格式不正确',
        [LANGUAGES.EN]: 'Invalid phone number format'
    },
    'register.emailRequired': {
        [LANGUAGES.ZH]: '请输入邮箱',
        [LANGUAGES.EN]: 'Please input email'
    },
    'register.emailInvalid': {
        [LANGUAGES.ZH]: '邮箱格式不正确',
        [LANGUAGES.EN]: 'Invalid email format'
    },
    'register.passwordRequired': {
        [LANGUAGES.ZH]: '请输入密码',
        [LANGUAGES.EN]: 'Please input password'
    },
    'register.passwordLength': {
        [LANGUAGES.ZH]: '密码长度在6-20个字符之间',
        [LANGUAGES.EN]: 'Password length should be 6-20 characters'
    },
    'register.confirmPasswordRequired': {
        [LANGUAGES.ZH]: '请再次输入密码',
        [LANGUAGES.EN]: 'Please input password again'
    },
    'register.passwordNotMatch': {
        [LANGUAGES.ZH]: '两次输入的密码不一致',
        [LANGUAGES.EN]: 'The two passwords do not match'
    },
    'register.agreeTermsFirst': {
        [LANGUAGES.ZH]: '请先同意服务条款和隐私政策',
        [LANGUAGES.EN]: 'Please agree to the Terms of Service and Privacy Policy first'
    },
    'register.code': {
        [LANGUAGES.ZH]: '验证码',
        [LANGUAGES.EN]: 'Verification Code'
    },
    'register.codePlaceholder': {
        [LANGUAGES.ZH]: '请输入验证码',
        [LANGUAGES.EN]: 'Please input verification code'
    },
    'register.getCode': {
        [LANGUAGES.ZH]: '获取验证码',
        [LANGUAGES.EN]: 'Get Code'
    },
    'register.codeRequired': {
        [LANGUAGES.ZH]: '请输入验证码',
        [LANGUAGES.EN]: 'Please input verification code'
    },
    'register.codeInvalid': {
        [LANGUAGES.ZH]: '验证码格式不正确',
        [LANGUAGES.EN]: 'Invalid verification code format'
    },
    'register.codeSent': {
        [LANGUAGES.ZH]: '验证码已发送',
        [LANGUAGES.EN]: 'Verification code sent'
    },
    'register.codeSendFailed': {
        [LANGUAGES.ZH]: '验证码发送失败，请稍后重试',
        [LANGUAGES.EN]: 'Failed to send verification code, please try again later'
    },
    'register.success': {
        [LANGUAGES.ZH]: '注册成功，即将跳转到登录页',
        [LANGUAGES.EN]: 'Registration successful, redirecting to login page'
    },
    'register.failed': {
        [LANGUAGES.ZH]: '注册失败，请稍后重试',
        [LANGUAGES.EN]: 'Registration failed, please try again later'
    },
    'register.failed.phoneRegistered': {
        [LANGUAGES.ZH]: '手机号已被注册',
        [LANGUAGES.EN]: 'Phone number has been registered'
    },
    'register.failed.usernameExists': {
        [LANGUAGES.ZH]: '用户名已存在',
        [LANGUAGES.EN]: 'Username already exists'
    },
    'register.failed.codeInvalid': {
        [LANGUAGES.ZH]: '验证码不正确或已过期',
        [LANGUAGES.EN]: 'Verification code is incorrect or has expired'
    },

    // 服务条款相关
    'terms.title': {
        [LANGUAGES.ZH]: '服务条款',
        [LANGUAGES.EN]: 'Terms of Service'
    },
    'terms.acceptance': {
        [LANGUAGES.ZH]: '接受条款',
        [LANGUAGES.EN]: 'Acceptance of Terms'
    },
    'terms.acceptanceContent': {
        [LANGUAGES.ZH]: '欢迎使用我们的服务。通过访问或使用我们的服务，您同意遵守这些条款和所有适用的法律和法规。如果您不同意这些条款，请不要使用我们的服务。',
        [LANGUAGES.EN]: 'Welcome to our service. By accessing or using our service, you agree to be bound by these terms and to comply with all applicable laws and regulations. If you do not agree with these terms, please do not use our service.'
    },
    'terms.services': {
        [LANGUAGES.ZH]: '服务描述',
        [LANGUAGES.EN]: 'Description of Services'
    },
    'terms.servicesContent': {
        [LANGUAGES.ZH]: '我们提供全球企业获客系统，包括但不限于海关数据、广交会采购商信息、全球企业库、智能搜邮等功能，帮助企业拓展海外市场。',
        [LANGUAGES.EN]: 'We provide a global enterprise lead generation system, including but not limited to customs data, Canton Fair buyer information, global company database, intelligent email search and other features to help businesses expand overseas markets.'
    },
    'terms.userAccounts': {
        [LANGUAGES.ZH]: '用户账户',
        [LANGUAGES.EN]: 'User Accounts'
    },
    'terms.userAccountsContent': {
        [LANGUAGES.ZH]: '您需要创建一个账户才能使用我们的某些服务。您负责维护您的账户的安全性，包括保护您的密码和限制对您的计算机的访问。',
        [LANGUAGES.EN]: 'You need to create an account to use certain of our services. You are responsible for maintaining the security of your account, including protecting your password and restricting access to your computer.'
    },
    'terms.userContent': {
        [LANGUAGES.ZH]: '用户内容',
        [LANGUAGES.EN]: 'User Content'
    },
    'terms.userContentRules': {
        [LANGUAGES.ZH]: '您可以提交、上传、存储或发送与我们服务相关的内容。您保留此类内容的所有权利，并且您对其负全部责任。',
        [LANGUAGES.EN]: 'You may submit, upload, store, or send content related to our services. You retain all rights to such content, and you are fully responsible for it.'
    },
    'terms.intellectualProperty': {
        [LANGUAGES.ZH]: '知识产权',
        [LANGUAGES.EN]: 'Intellectual Property'
    },
    'terms.intellectualPropertyContent': {
        [LANGUAGES.ZH]: '我们的服务和内容（不包括您的内容）受版权、商标和其他知识产权法律保护。在遵守这些条款的前提下，我们授予您有限的、非独占的、不可转让的许可，以使用我们的服务。',
        [LANGUAGES.EN]: 'Our services and content (excluding your content) are protected by copyright, trademark, and other intellectual property laws. Subject to these terms, we grant you a limited, non-exclusive, non-transferable license to use our services.'
    },
    'terms.dataPrivacy': {
        [LANGUAGES.ZH]: '数据和隐私',
        [LANGUAGES.EN]: 'Data and Privacy'
    },
    'terms.dataPrivacyContent': {
        [LANGUAGES.ZH]: '我们将按照隐私政策收集和使用与您的注册、访问或使用我们服务相关的数据。您同意我们的数据实践，包括收集、使用、处理和共享您的信息，如我们的隐私政策所述。',
        [LANGUAGES.EN]: 'We will collect and use data related to your registration, access, or use of our services as described in our Privacy Policy. You consent to our data practices, including the collection, use, processing, and sharing of your information as described in our Privacy Policy.'
    },
    'terms.termination': {
        [LANGUAGES.ZH]: '终止',
        [LANGUAGES.EN]: 'Termination'
    },
    'terms.terminationContent': {
        [LANGUAGES.ZH]: '我们可以在任何时候终止或暂停您的账户或访问我们的服务，如果您违反了这些条款，或者我们认为这样做是必要的，包括任何非法活动。',
        [LANGUAGES.EN]: 'We may terminate or suspend your account or access to our services at any time if you violate these terms or if we believe it is necessary to do so, including for any illegal activity.'
    },
    'terms.limitation': {
        [LANGUAGES.ZH]: '责任限制',
        [LANGUAGES.EN]: 'Limitation of Liability'
    },
    'terms.limitationContent': {
        [LANGUAGES.ZH]: '在法律允许的最大范围内，我们及我们的董事、员工、合作伙伴和供应商不对任何直接、间接、附带、特殊、后果性或惩罚性的损害负责，包括但不限于数据丢失或利润损失。',
        [LANGUAGES.EN]: 'To the maximum extent permitted by law, we and our directors, employees, partners, and suppliers will not be liable for any direct, indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of data or loss of profits.'
    },
    'terms.modification': {
        [LANGUAGES.ZH]: '条款修改',
        [LANGUAGES.EN]: 'Modification of Terms'
    },
    'terms.modificationContent': {
        [LANGUAGES.ZH]: '我们保留在任何时候修改这些条款的权利。如果条款发生重大变化，我们将通过在我们的网站上发布更新的条款或通过其他方式通知您。您在条款修改后继续使用我们的服务将构成您对修改后的条款的接受。',
        [LANGUAGES.EN]: 'We reserve the right to modify these terms at any time. If we make material changes to the terms, we will notify you by posting the updated terms on our website or by other means. Your continued use of our services after such modifications will constitute your acceptance of the modified terms.'
    },
    'terms.contactUs': {
        [LANGUAGES.ZH]: '联系我们',
        [LANGUAGES.EN]: 'Contact Us'
    },
    'terms.contactUsContent': {
        [LANGUAGES.ZH]: '如果您对这些服务条款有任何问题，请联系我们的客户支持团队。',
        [LANGUAGES.EN]: 'If you have any questions about these Terms of Service, please contact our customer support team.'
    },
    'terms.lastUpdated': {
        [LANGUAGES.ZH]: '最后更新日期',
        [LANGUAGES.EN]: 'Last Updated'
    },

    // 隐私政策相关
    'privacy.title': {
        [LANGUAGES.ZH]: '隐私政策',
        [LANGUAGES.EN]: 'Privacy Policy'
    },
    'privacy.intro': {
        [LANGUAGES.ZH]: '本隐私政策描述了我们如何收集、使用、处理和分享您的信息，包括个人数据，以便为您提供我们的服务。我们致力于保护您的隐私和个人信息。',
        [LANGUAGES.EN]: 'This Privacy Policy describes how we collect, use, process and share your information, including personal data, in order to provide you with our services. We are committed to protecting your privacy and personal information.'
    },
    'privacy.informationCollect': {
        [LANGUAGES.ZH]: '信息收集',
        [LANGUAGES.EN]: 'Information We Collect'
    },
    'privacy.informationCollectContent': {
        [LANGUAGES.ZH]: '我们可能收集以下类型的信息：',
        [LANGUAGES.EN]: 'We may collect the following types of information:'
    },
    'privacy.personalInfo': {
        [LANGUAGES.ZH]: '个人信息：如您的姓名、电子邮件地址、电话号码、地址等。',
        [LANGUAGES.EN]: 'Personal Information: such as your name, email address, phone number, address, etc.'
    },
    'privacy.usageData': {
        [LANGUAGES.ZH]: '使用数据：关于您如何使用我们服务的信息。',
        [LANGUAGES.EN]: 'Usage Data: information about how you use our services.'
    },
    'privacy.deviceInfo': {
        [LANGUAGES.ZH]: '设备信息：如您的设备类型、操作系统、浏览器类型等。',
        [LANGUAGES.EN]: 'Device Information: such as your device type, operating system, browser type, etc.'
    },
    'privacy.cookies': {
        [LANGUAGES.ZH]: 'Cookie和类似技术：用于收集信息，改善用户体验。',
        [LANGUAGES.EN]: 'Cookies and Similar Technologies: used to collect information and improve user experience.'
    },
    'privacy.useOfInformation': {
        [LANGUAGES.ZH]: '信息使用',
        [LANGUAGES.EN]: 'Use of Information'
    },
    'privacy.useOfInformationContent': {
        [LANGUAGES.ZH]: '我们可能使用收集的信息用于以下目的：',
        [LANGUAGES.EN]: 'We may use the collected information for the following purposes:'
    },
    'privacy.provideService': {
        [LANGUAGES.ZH]: '提供、维护和改进我们的服务。',
        [LANGUAGES.EN]: 'To provide, maintain, and improve our services.'
    },
    'privacy.improveService': {
        [LANGUAGES.ZH]: '研究和开发新的服务或功能。',
        [LANGUAGES.EN]: 'To research and develop new services or features.'
    },
    'privacy.communication': {
        [LANGUAGES.ZH]: '与您沟通，包括发送服务更新、安全警报等。',
        [LANGUAGES.EN]: 'To communicate with you, including sending service updates, security alerts, etc.'
    },
    'privacy.marketing': {
        [LANGUAGES.ZH]: '发送营销信息，如新功能介绍或特别优惠。',
        [LANGUAGES.EN]: 'To send marketing information, such as new feature introductions or special offers.'
    },
    'privacy.informationSharing': {
        [LANGUAGES.ZH]: '信息共享',
        [LANGUAGES.EN]: 'Information Sharing'
    },
    'privacy.informationSharingContent': {
        [LANGUAGES.ZH]: '我们不会出售您的个人信息。但在某些情况下，我们可能会与第三方共享您的信息，包括服务提供商、业务合作伙伴、在法律要求或业务转让的情况下。',
        [LANGUAGES.EN]: 'We do not sell your personal information. However, in certain situations, we may share your information with third parties, including service providers, business partners, as required by law or in the case of a business transfer.'
    },
    'privacy.dataRetention': {
        [LANGUAGES.ZH]: '数据保留',
        [LANGUAGES.EN]: 'Data Retention'
    },
    'privacy.dataRetentionContent': {
        [LANGUAGES.ZH]: '我们会在满足本隐私政策中规定的目的所必需的时间内保留您的信息，除非法律要求或允许更长的保留期限。',
        [LANGUAGES.EN]: 'We will retain your information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.'
    },
    'privacy.dataSecurity': {
        [LANGUAGES.ZH]: '数据安全',
        [LANGUAGES.EN]: 'Data Security'
    },
    'privacy.dataSecurityContent': {
        [LANGUAGES.ZH]: '我们采取合理的措施来保护您的个人信息免遭丢失、盗窃、滥用和未经授权的访问、披露、更改和销毁。',
        [LANGUAGES.EN]: 'We take reasonable measures to protect your personal information from loss, theft, misuse, and unauthorized access, disclosure, alteration, and destruction.'
    },
    'privacy.yourRights': {
        [LANGUAGES.ZH]: '您的权利',
        [LANGUAGES.EN]: 'Your Rights'
    },
    'privacy.yourRightsContent': {
        [LANGUAGES.ZH]: '根据适用法律，您可能有权访问、更正、删除您的个人信息，反对或限制某些处理，以及在某些情况下请求数据可携带性。',
        [LANGUAGES.EN]: 'Depending on applicable law, you may have the right to access, correct, delete your personal information, object to or restrict certain processing, and in some cases request data portability.'
    },
    'privacy.thirdPartyLinks': {
        [LANGUAGES.ZH]: '第三方链接',
        [LANGUAGES.EN]: 'Third-Party Links'
    },
    'privacy.thirdPartyLinksContent': {
        [LANGUAGES.ZH]: '我们的服务可能包含指向第三方网站或服务的链接，这些网站或服务不受我们控制。我们对这些第三方的隐私做法不负责任。',
        [LANGUAGES.EN]: 'Our services may contain links to third-party websites or services that are not controlled by us. We are not responsible for the privacy practices of these third parties.'
    },
    'privacy.childrenPrivacy': {
        [LANGUAGES.ZH]: '儿童隐私',
        [LANGUAGES.EN]: 'Children\'s Privacy'
    },
    'privacy.childrenPrivacyContent': {
        [LANGUAGES.ZH]: '我们的服务不面向13岁以下的儿童。我们不会故意收集13岁以下儿童的个人信息。',
        [LANGUAGES.EN]: 'Our services are not directed to children under 13. We do not knowingly collect personal information from children under 13.'
    },
    'privacy.changes': {
        [LANGUAGES.ZH]: '政策变更',
        [LANGUAGES.EN]: 'Changes to This Policy'
    },
    'privacy.changesContent': {
        [LANGUAGES.ZH]: '我们可能会不时更新此隐私政策。当我们这样做时，我们将在我们的网站上发布更新后的政策，并更新政策顶部的"最后更新"日期。',
        [LANGUAGES.EN]: 'We may update this Privacy Policy from time to time. When we do, we will post the updated policy on our website and update the "Last Updated" date at the top of the policy.'
    },
    'privacy.contactUs': {
        [LANGUAGES.ZH]: '联系我们',
        [LANGUAGES.EN]: 'Contact Us'
    },
    'privacy.contactUsContent': {
        [LANGUAGES.ZH]: '如果您对此隐私政策有任何问题或疑虑，请联系我们的数据保护团队。',
        [LANGUAGES.EN]: 'If you have any questions or concerns about this Privacy Policy, please contact our data protection team.'
    },
    'privacy.lastUpdated': {
        [LANGUAGES.ZH]: '最后更新日期',
        [LANGUAGES.EN]: 'Last Updated'
    },

    // 页面标题
    'title.app': {
        [LANGUAGES.ZH]: '数海领航',
        [LANGUAGES.EN]: 'GuangZhiMo'
    },

    // 导出功能国际化
    'export.dialog.title.exhibitors': {
        [LANGUAGES.ZH]: '导出广交会参展商数据',
        [LANGUAGES.EN]: 'Export Canton Fair Exhibitors Data'
    },
    'export.dialog.title.purchasers': {
        [LANGUAGES.ZH]: '导出广交会采购商数据',
        [LANGUAGES.EN]: 'Export Canton Fair Purchasers Data'
    },
    'export.selectFields': {
        [LANGUAGES.ZH]: '选择要导出的字段',
        [LANGUAGES.EN]: 'Select Fields to Export'
    },
    'export.max.limit.notice': {
        [LANGUAGES.ZH]: '最多可以导出50,000条数据',
        [LANGUAGES.EN]: 'Maximum can be exported 50,000'
    },
    'export.selectAll': {
        [LANGUAGES.ZH]: '全选',
        [LANGUAGES.EN]: 'Select All'
    },
    'export.selectNone': {
        [LANGUAGES.ZH]: '全不选',
        [LANGUAGES.EN]: 'Select None'
    },
    'export.selectedCount': {
        [LANGUAGES.ZH]: '已选择 {count} / {total} 个字段',
        [LANGUAGES.EN]: 'Selected {count} / {total} fields'
    },
    'export.noFieldsSelected': {
        [LANGUAGES.ZH]: '请至少选择一个字段进行导出',
        [LANGUAGES.EN]: 'Please select at least one field to export'
    },
    'export.countLabel': {
        [LANGUAGES.ZH]: '请输入要导出的记录数:',
        [LANGUAGES.EN]: 'Please enter the number of records to export:'
    },
    'export.countPlaceholder': {
        [LANGUAGES.ZH]: '请输入导出数量',
        [LANGUAGES.EN]: 'Enter export count'
    },
    'export.maxRecords': {
        [LANGUAGES.ZH]: '最多可导出 {count} 条记录',
        [LANGUAGES.EN]: 'Maximum {count} records can be exported'
    },
    'export.balanceInfo': {
        [LANGUAGES.ZH]: '您的导出余额: {balance} 次',
        [LANGUAGES.EN]: 'Your export balance: {balance} times'
    },
    'export.confirmButton': {
        [LANGUAGES.ZH]: '确认导出',
        [LANGUAGES.EN]: 'Confirm Export'
    },
    'export.cancelButton': {
        [LANGUAGES.ZH]: '取消',
        [LANGUAGES.EN]: 'Cancel'
    },
    'export.filename.exhibitors': {
        [LANGUAGES.ZH]: '广交会参展商数据',
        [LANGUAGES.EN]: 'Canton Fair Exhibitors Data'
    },
    'export.filename.purchasers': {
        [LANGUAGES.ZH]: '广交会采购商数据',
        [LANGUAGES.EN]: 'Canton Fair Purchasers Data'
    },

    // Facebook 页面所需
    'facebook.matched.count': {
        [LANGUAGES.ZH]: '符合条件的联系人：',
        [LANGUAGES.EN]: 'Matched Contacts: '
    },
    'facebook.matched.count.unit': {
        [LANGUAGES.ZH]: ' 条',
        [LANGUAGES.EN]: ' items'
    },

    // 通用导出弹窗标题
    'export.dialog.title': {
        [LANGUAGES.ZH]: '导出数据',
        [LANGUAGES.EN]: 'Export Data'
    },

    // Facebook 匹配数据
    'facebook.match.data': {
        [LANGUAGES.ZH]: '匹配数据',
        [LANGUAGES.EN]: 'Match Data'
    },

    // 广交会参展商字段国际化
    'exhibitor.field.companyName': {
        [LANGUAGES.ZH]: '公司名称',
        [LANGUAGES.EN]: 'Company Name'
    },
    'exhibitor.field.boothNumber': {
        [LANGUAGES.ZH]: '展位号',
        [LANGUAGES.EN]: 'Booth Number'
    },
    'exhibitor.field.contactPerson': {
        [LANGUAGES.ZH]: '联系人',
        [LANGUAGES.EN]: 'Contact Person'
    },
    'exhibitor.field.website': {
        [LANGUAGES.ZH]: '网站',
        [LANGUAGES.EN]: 'Website'
    },
    'exhibitor.field.address': {
        [LANGUAGES.ZH]: '地址',
        [LANGUAGES.EN]: 'Address'
    },
    'exhibitor.field.postalCode': {
        [LANGUAGES.ZH]: '邮编',
        [LANGUAGES.EN]: 'Postal Code'
    },
    'exhibitor.field.sessionInfo': {
        [LANGUAGES.ZH]: '届次',
        [LANGUAGES.EN]: 'Session'
    },
    'exhibitor.field.mainProducts': {
        [LANGUAGES.ZH]: '主要产品',
        [LANGUAGES.EN]: 'Main Products'
    },
    'exhibitor.field.mobilePhone': {
        [LANGUAGES.ZH]: '手机号码',
        [LANGUAGES.EN]: 'Mobile Phone'
    },
    'exhibitor.field.phone': {
        [LANGUAGES.ZH]: '固定电话',
        [LANGUAGES.EN]: 'Phone'
    },
    'exhibitor.field.fax': {
        [LANGUAGES.ZH]: '传真',
        [LANGUAGES.EN]: 'Fax'
    },
    'exhibitor.field.email': {
        [LANGUAGES.ZH]: '邮箱',
        [LANGUAGES.EN]: 'Email'
    },
    // 新增：行业、城市、省份、成立年份、公司类型、公司规模、历史参展、注册资金、公司简介
    'exhibitor.field.industry': {
        [LANGUAGES.ZH]: '行业',
        [LANGUAGES.EN]: 'Industry'
    },
    'exhibitor.field.city': {
        [LANGUAGES.ZH]: '城市',
        [LANGUAGES.EN]: 'City'
    },
    'exhibitor.field.province': {
        [LANGUAGES.ZH]: '省份',
        [LANGUAGES.EN]: 'Province'
    },
    'exhibitor.field.establishYear': {
        [LANGUAGES.ZH]: '成立年份',
        [LANGUAGES.EN]: 'Establishment Year'
    },
    'exhibitor.field.companyType': {
        [LANGUAGES.ZH]: '公司类型',
        [LANGUAGES.EN]: 'Company Type'
    },
    'exhibitor.field.companyScale': {
        [LANGUAGES.ZH]: '公司规模',
        [LANGUAGES.EN]: 'Company Size'
    },
    'exhibitor.field.recentExhibitionHistory': {
        [LANGUAGES.ZH]: '历史参展',
        [LANGUAGES.EN]: 'Recent Exhibition History'
    },
    'exhibitor.field.registeredCapital': {
        [LANGUAGES.ZH]: '注册资金',
        [LANGUAGES.EN]: 'Registered Capital'
    },
    'exhibitor.field.companyProfile': {
        [LANGUAGES.ZH]: '公司简介',
        [LANGUAGES.EN]: 'Company Profile'
    },

    // 广交会采购商字段国际化
    'purchaser.field.companyName': {
        [LANGUAGES.ZH]: '公司名称',
        [LANGUAGES.EN]: 'Company Name'
    },
    'purchaser.field.country': {
        [LANGUAGES.ZH]: '国家/地区',
        [LANGUAGES.EN]: 'Country/Region'
    },
    'purchaser.field.procurementProductType': {
        [LANGUAGES.ZH]: '采购产品类型',
        [LANGUAGES.EN]: 'Procurement Product Type'
    },
    'purchaser.field.contactPerson': {
        [LANGUAGES.ZH]: '联系人',
        [LANGUAGES.EN]: 'Contact Person'
    },
    'purchaser.field.contactWay': {
        [LANGUAGES.ZH]: '联系方式',
        [LANGUAGES.EN]: 'Contact Method'
    },
    'purchaser.field.phone': {
        [LANGUAGES.ZH]: '电话',
        [LANGUAGES.EN]: 'Phone'
    },
    'purchaser.field.fax': {
        [LANGUAGES.ZH]: '传真',
        [LANGUAGES.EN]: 'Fax'
    },
    'purchaser.field.address': {
        [LANGUAGES.ZH]: '地址',
        [LANGUAGES.EN]: 'Address'
    },
    'purchaser.field.zipCode': {
        [LANGUAGES.ZH]: '邮编',
        [LANGUAGES.EN]: 'Zip Code'
    },
    'purchaser.field.email': {
        [LANGUAGES.ZH]: '电子邮箱',
        [LANGUAGES.EN]: 'Email'
    },
    'purchaser.field.website': {
        [LANGUAGES.ZH]: '公司网站',
        [LANGUAGES.EN]: 'Company Website'
    },
    'purchaser.field.sessionInfo': {
        [LANGUAGES.ZH]: '广交会届数',
        [LANGUAGES.EN]: 'Canton Fair Session'
    },

    // 导出错误提示和成功消息国际化
    'export.error.noData': {
        [LANGUAGES.ZH]: '没有找到符合条件的数据',
        [LANGUAGES.EN]: 'No data found matching the criteria'
    },
    'export.error.countExceeded': {
        [LANGUAGES.ZH]: '导出数量不能超过符合条件的记录数 {maxCount}',
        [LANGUAGES.EN]: 'Export count cannot exceed the number of matching records {maxCount}'
    },
    'export.error.limitExceeded': {
        [LANGUAGES.ZH]: '导出数量不能超过100万',
        [LANGUAGES.EN]: 'Export count cannot exceed 1 million'
    },
    'export.error.balanceInsufficient': {
        [LANGUAGES.ZH]: '您的导出余额不足，请充值后再试',
        [LANGUAGES.EN]: 'Insufficient export balance, please recharge and try again'
    },
    'export.error.invalidCount': {
        [LANGUAGES.ZH]: '请输入有效的导出数量',
        [LANGUAGES.EN]: 'Please enter a valid export count'
    },
    'export.error.noFieldsSelected': {
        [LANGUAGES.ZH]: '请至少选择一个字段进行导出',
        [LANGUAGES.EN]: 'Please select at least one field to export'
    },
    'export.error.failed': {
        [LANGUAGES.ZH]: '导出失败，请稍后重试',
        [LANGUAGES.EN]: 'Export failed, please try again later'
    },
    'export.success.started': {
        [LANGUAGES.ZH]: '导出任务已开始，请稍候...',
        [LANGUAGES.EN]: 'Export task started, please wait...'
    },
    'export.success.completed': {
        [LANGUAGES.ZH]: '导出完成！',
        [LANGUAGES.EN]: 'Export completed!'
    },
    'export.loading.preparing': {
        [LANGUAGES.ZH]: '正在准备导出数据...',
        [LANGUAGES.EN]: 'Preparing export data...'
    },
    'export.loading.generating': {
        [LANGUAGES.ZH]: '正在生成Excel文件...',
        [LANGUAGES.EN]: 'Generating Excel file...'
    },

    // 全球企业库导出功能国际化
    'export.dialog.title.globalEnterprise': {
        [LANGUAGES.ZH]: '导出全球企业数据',
        [LANGUAGES.EN]: 'Export Global Enterprise Data'
    },
    'export.filename.globalEnterprise': {
        [LANGUAGES.ZH]: '全球企业数据',
        [LANGUAGES.EN]: 'Global Enterprise Data'
    },

    // LinkedIn获客导出功能国际化
    'export.dialog.title.linkedin': {
        [LANGUAGES.ZH]: '导出LinkedIn数据',
        [LANGUAGES.EN]: 'Export LinkedIn Data'
    },
    'export.filename.linkedin': {
        [LANGUAGES.ZH]: 'LinkedIn数据',
        [LANGUAGES.EN]: 'LinkedIn Data'
    },

    // 全球企业库字段国际化
    'globalEnterprise.field.companyName': {
        [LANGUAGES.ZH]: '公司名称',
        [LANGUAGES.EN]: 'Company Name'
    },
    'globalEnterprise.field.country': {
        [LANGUAGES.ZH]: '国家',
        [LANGUAGES.EN]: 'Country'
    },
    'globalEnterprise.field.province': {
        [LANGUAGES.ZH]: '省份',
        [LANGUAGES.EN]: 'Province'
    },
    'globalEnterprise.field.city': {
        [LANGUAGES.ZH]: '城市',
        [LANGUAGES.EN]: 'City'
    },
    'globalEnterprise.field.address': {
        [LANGUAGES.ZH]: '地址',
        [LANGUAGES.EN]: 'Address'
    },
    'globalEnterprise.field.industry': {
        [LANGUAGES.ZH]: '行业',
        [LANGUAGES.EN]: 'Industry'
    },
    'globalEnterprise.field.subIndustry': {
        [LANGUAGES.ZH]: '子行业',
        [LANGUAGES.EN]: 'subIndustry'
    },

    'globalEnterprise.field.companyType': {
        [LANGUAGES.ZH]: '公司类型',
        [LANGUAGES.EN]: 'Company Type'
    },
    'globalEnterprise.field.companyScale': {
        [LANGUAGES.ZH]: '公司规模',
        [LANGUAGES.EN]: 'Company Scale'
    },
    'globalEnterprise.field.registeredCapital': {
        [LANGUAGES.ZH]: '注册资本',
        [LANGUAGES.EN]: 'Registered Capital'
    },
    'globalEnterprise.field.establishedYear': {
        [LANGUAGES.ZH]: '成立年份',
        [LANGUAGES.EN]: 'Established Year'
    },
    'globalEnterprise.field.businessScope': {
        [LANGUAGES.ZH]: '经营范围',
        [LANGUAGES.EN]: 'Business Scope'
    },
    'globalEnterprise.field.website': {
        [LANGUAGES.ZH]: '网站',
        [LANGUAGES.EN]: 'Website'
    },
    'globalEnterprise.field.phone': {
        [LANGUAGES.ZH]: '电话',
        [LANGUAGES.EN]: 'Phone'
    },
    'globalEnterprise.field.mobile': {
        [LANGUAGES.ZH]: '手机号',
        [LANGUAGES.EN]: 'Mobile'
    },
    'globalEnterprise.field.email': {
        [LANGUAGES.ZH]: '邮箱',
        [LANGUAGES.EN]: 'Email'
    },
    'globalEnterprise.field.revenue': {
        [LANGUAGES.ZH]: '收入规模',
        [LANGUAGES.EN]: 'Revenue Size'
    },
    'globalEnterprise.field.employee': {
        [LANGUAGES.ZH]: '员工规模',
        [LANGUAGES.EN]: 'Employee Size'
    },

    // LinkedIn字段国际化
    'linkedin.field.firstName': {
        [LANGUAGES.ZH]: '名',
        [LANGUAGES.EN]: 'First Name'
    },
    'linkedin.field.lastName': {
        [LANGUAGES.ZH]: '姓',
        [LANGUAGES.EN]: 'Last Name'
    },
    'linkedin.field.fullName': {
        [LANGUAGES.ZH]: '全名',
        [LANGUAGES.EN]: 'Full Name'
    },
    'linkedin.field.headline': {
        [LANGUAGES.ZH]: '职位标题',
        [LANGUAGES.EN]: 'Headline'
    },
    'linkedin.field.currentCompany': {
        [LANGUAGES.ZH]: '当前公司',
        [LANGUAGES.EN]: 'Current Company'
    },
    'linkedin.field.currentPosition': {
        [LANGUAGES.ZH]: '当前职位',
        [LANGUAGES.EN]: 'Current Position'
    },
    'linkedin.field.zipcode': {
        [LANGUAGES.ZH]: '邮编',
        [LANGUAGES.EN]: 'Zipcode'
    },
    'linkedin.field.country': {
        [LANGUAGES.ZH]: '国家',
        [LANGUAGES.EN]: 'Country'
    },
    'linkedin.field.industry': {
        [LANGUAGES.ZH]: '行业',
        [LANGUAGES.EN]: 'Industry'
    },
    'linkedin.field.connectionDegree': {
        [LANGUAGES.ZH]: '联系度',
        [LANGUAGES.EN]: 'Connection Degree'
    },
    'linkedin.field.profileUrl': {
        [LANGUAGES.ZH]: 'LinkedIn链接',
        [LANGUAGES.EN]: 'LinkedIn URL'
    },
    'linkedin.field.twitterUrl': {
        [LANGUAGES.ZH]: 'Twitter链接',
        [LANGUAGES.EN]: 'Twitter URL'
    },
    'linkedin.field.githubUrl': {
        [LANGUAGES.ZH]: 'GitHub链接',
        [LANGUAGES.EN]: 'GitHub URL'
    },
    'linkedin.field.facebookUrl': {
        [LANGUAGES.ZH]: 'Facebook链接',
        [LANGUAGES.EN]: 'Facebook URL'
    },
    'linkedin.field.emails': {
        [LANGUAGES.ZH]: '邮箱',
        [LANGUAGES.EN]: 'Email'
    },
    'linkedin.field.phoneNumbers': {
        [LANGUAGES.ZH]: '电话',
        [LANGUAGES.EN]: 'Phone'
    },
    'linkedin.field.companyWebsite': {
        [LANGUAGES.ZH]: '公司网站',
        [LANGUAGES.EN]: 'Company Website'
    },
    'linkedin.field.twitterUserName': {
        [LANGUAGES.ZH]: 'Twitter用户名',
        [LANGUAGES.EN]: 'Twitter Username'
    },
    // 'linkedin.field.githubUrl': {
    //   [LANGUAGES.ZH]: 'GitHub链接',
    //   [LANGUAGES.EN]: 'GitHub URL'
    // },
    // 'linkedin.field.facebookUrl': {
    //   [LANGUAGES.ZH]: 'Facebook链接',
    //   [LANGUAGES.EN]: 'Facebook URL'
    // },

    // 菜单和导航
    'nav.workstation': {
        [LANGUAGES.ZH]: '工作台',
        [LANGUAGES.EN]: 'Workstation'
    },
    'nav.controlCenter': {
        [LANGUAGES.ZH]: '控制中心',
        [LANGUAGES.EN]: 'Control Center'
    },
    'nav.resourceCenter': {
        [LANGUAGES.ZH]: '资源中心',
        [LANGUAGES.EN]: 'Resource Center'
    },
    'nav.dashboard': {
        [LANGUAGES.ZH]: '仪表盘',
        [LANGUAGES.EN]: 'Dashboard'
    },
    'nav.documents': {
        [LANGUAGES.ZH]: '文档',
        [LANGUAGES.EN]: 'Documents'
    },
    'nav.allDocuments': {
        [LANGUAGES.ZH]: '所有文档',
        [LANGUAGES.EN]: 'All Documents'
    },
    'nav.sharedDocuments': {
        [LANGUAGES.ZH]: '共享文档',
        [LANGUAGES.EN]: 'Shared Documents'
    },
    'nav.statistics': {
        [LANGUAGES.ZH]: '统计',
        [LANGUAGES.EN]: 'Statistics'
    },
    'nav.assistant': {
        [LANGUAGES.ZH]: '助手',
        [LANGUAGES.EN]: 'Assistant'
    },
    'nav.settings': {
        [LANGUAGES.ZH]: '设置',
        [LANGUAGES.EN]: 'Settings'
    },
    'nav.leadsCenter': {
        [LANGUAGES.ZH]: '线索中心',
        [LANGUAGES.EN]: 'Leads Center'
    },
    'nav.customs': {
        [LANGUAGES.ZH]: '海关数据',
        [LANGUAGES.EN]: 'Customs Data'
    },
    'nav.cantonFair': {
        [LANGUAGES.ZH]: '广交会采购商',
        [LANGUAGES.EN]: 'Canton Fair Buyers'
    },
    'nav.cantonFairExhibitors': {
        [LANGUAGES.ZH]: '广交会参展商',
        [LANGUAGES.EN]: 'Canton Fair Exhibitors'
    },
    'nav.globalCompanies': {
        [LANGUAGES.ZH]: '全球企业库',
        [LANGUAGES.EN]: 'Global Companies'
    },
    'nav.intelligentMap': {
        [LANGUAGES.ZH]: '智能搜邮',
        [LANGUAGES.EN]: 'Intelligent Map'
    },
    'nav.emailLookup': {
        [LANGUAGES.ZH]: '联系方式反查',
        [LANGUAGES.EN]: 'Contact Method Lookup'
    },
    'nav.domainLookup': {
        [LANGUAGES.ZH]: '域名反查',
        [LANGUAGES.EN]: 'Domain Lookup'
    },
    'nav.socialLookup': {
        [LANGUAGES.ZH]: '社媒反查',
        [LANGUAGES.EN]: 'Social Media Lookup'
    },
    'nav.statisticalTools': {
        [LANGUAGES.ZH]: '营销辅助工具',
        [LANGUAGES.EN]: 'Marketing Aid Tools'
    },
    'nav.smsCenter': {
        [LANGUAGES.ZH]: '短信中心',
        [LANGUAGES.EN]: 'SMS Center'
    },
    'nav.emailCenter': {
        [LANGUAGES.ZH]: '邮件中心',
        [LANGUAGES.EN]: 'Email Center'
    },
    'nav.managementCenter': {
        [LANGUAGES.ZH]: '管理中心',
        [LANGUAGES.EN]: 'Management Center'
    },
    'nav.myFavorites': {
        [LANGUAGES.ZH]: '我的收藏',
        [LANGUAGES.EN]: 'My Favorites'
    },
    'nav.personalCenter': {
        [LANGUAGES.ZH]: '个人中心',
        [LANGUAGES.EN]: 'Personal Center'
    },
    'nav.transactionDynamics': {
        [LANGUAGES.ZH]: '交易动态',
        [LANGUAGES.EN]: 'Transaction Dynamics'
    },
    'nav.linkedin_leads': {
        [LANGUAGES.ZH]: 'LinkedIn获客',
        [LANGUAGES.EN]: 'LinkedIn Leads'
    },
    'nav.facebook_leads': {
        [LANGUAGES.ZH]: 'Facebook获客',
        [LANGUAGES.EN]: 'Facebook Leads'
    },
    'nav.twitter_leads': {
        [LANGUAGES.ZH]: 'Twitter获客',
        [LANGUAGES.EN]: 'Twitter Leads'
    },
    'nav.email_leads': {
        [LANGUAGES.ZH]: '邮件获客',
        [LANGUAGES.EN]: 'Email Leads'
    },

    // 用户相关
    'user.account': {
        [LANGUAGES.ZH]: '个人账户',
        [LANGUAGES.EN]: 'Personal Account'
    },
    'user.freeVersion': {
        [LANGUAGES.ZH]: '免费版',
        [LANGUAGES.EN]: 'Free Version'
    },
    'user.basicVip': {
        [LANGUAGES.ZH]: '初级VIP',
        [LANGUAGES.EN]: 'Basic VIP'
    },
    'user.advancedVip': {
        [LANGUAGES.ZH]: '高级VIP',
        [LANGUAGES.EN]: 'Advanced VIP'
    },
    'user.premiumVip': {
        [LANGUAGES.ZH]: '尊享VIP',
        [LANGUAGES.EN]: 'Premium VIP'
    },
    'user.logout': {
        [LANGUAGES.ZH]: '退出登录',
        [LANGUAGES.EN]: 'Logout'
    },
    'user.service': {
        [LANGUAGES.ZH]: '专属客服',
        [LANGUAGES.EN]: 'Customer Service'
    },
    'user.upgrade': {
        [LANGUAGES.ZH]: '升级会员',
        [LANGUAGES.EN]: 'Upgrade Membership'
    },
    'user.memberManagement': {
        [LANGUAGES.ZH]: '成员管理',
        [LANGUAGES.EN]: 'Member Management'
    },
    'user.myExports': {
        [LANGUAGES.ZH]: '我的导出',
        [LANGUAGES.EN]: 'My Exports'
    },
    'user.vipCenter': {
        [LANGUAGES.ZH]: '会员中心',
        [LANGUAGES.EN]: 'VIP Center'
    },
    'user.myAccount': {
        [LANGUAGES.ZH]: '我的账户',
        [LANGUAGES.EN]: 'My Account'
    },
    'user.passwordSettings': {
        [LANGUAGES.ZH]: '密码设置',
        [LANGUAGES.EN]: 'Password Settings'
    },
    'user.profileSettings': {
        [LANGUAGES.ZH]: '资料设置',
        [LANGUAGES.EN]: 'Profile Settings'
    },
    'user.promotion': {
        [LANGUAGES.ZH]: '推广返佣及联系客服',
        [LANGUAGES.EN]: 'Promotion Rebate & Contact Support'
    },
    'user.logoutFailed': {
        [LANGUAGES.ZH]: '退出登录失败',
        [LANGUAGES.EN]: 'Logout failed'
    },
    'user.accountBalance': {
        [LANGUAGES.ZH]: '账户余额',
        [LANGUAGES.EN]: 'Account Balance'
    },
    'user.recharge': {
        [LANGUAGES.ZH]: '充值',
        [LANGUAGES.EN]: 'Recharge'
    },
    'user.remainingSearches': {
        [LANGUAGES.ZH]: '剩余搜索次数',
        [LANGUAGES.EN]: 'Remaining Searches'
    },

    // 通用
    'common.learnMore': {
        [LANGUAGES.ZH]: '了解更多',
        [LANGUAGES.EN]: 'Learn More'
    },
    'common.later': {
        [LANGUAGES.ZH]: '稍后再说',
        [LANGUAGES.EN]: 'Maybe Later'
    },

    // 仪表盘
    'dashboard.myMarketing': {
        [LANGUAGES.ZH]: '我的营销',
        [LANGUAGES.EN]: 'My Marketing'
    },
    'dashboard.marquee.text': {
        [LANGUAGES.ZH]: '测系统上线！ | 白媒体全国仓分发展示量！ | 开票的说等你来体验！ | 全球企业数据服务让您的业务腾飞！ | 多维度数据分析，助力精准营销！',
        [LANGUAGES.EN]: 'Beta system is online! | Nationwide warehouse distribution display! | Invoicing features are ready to experience! | Global enterprise data services to boost your business! | Multi-dimensional data analysis for precise marketing!'
    },
    'dashboard.ad.moreServices': {
        [LANGUAGES.ZH]: '更多服务',
        [LANGUAGES.EN]: 'More Services'
    },
    'dashboard.ad.moreServicesLabel': {
        [LANGUAGES.ZH]: '更多\n服务',
        [LANGUAGES.EN]: 'More\nServices'
    },
    'dashboard.ad.businessPromotion': {
        [LANGUAGES.ZH]: '业务推广',
        [LANGUAGES.EN]: 'Business Promotion'
    },
    'dashboard.ad.moreProfessionalServicesTitle': {
        [LANGUAGES.ZH]: '数海领航 - 更多专业服务',
        [LANGUAGES.EN]: 'Light Foam - More Professional Services'
    },
    'dashboard.ad.moreServicesDesc': {
        [LANGUAGES.ZH]: '我们还提供以下专业服务，助力您的企业发展：',
        [LANGUAGES.EN]: 'We also provide the following professional services to support your business growth:'
    },
    'dashboard.ad.service.crossBorderEcommerce': {
        [LANGUAGES.ZH]: '跨境电商解决方案',
        [LANGUAGES.EN]: 'Cross-border E-commerce Solutions'
    },
    'dashboard.ad.service.overseasMarketingAnalytics': {
        [LANGUAGES.ZH]: '海外营销数据分析',
        [LANGUAGES.EN]: 'Overseas Marketing Analytics'
    },
    'dashboard.ad.service.globalSupplyChain': {
        [LANGUAGES.ZH]: '全球供应链管理',
        [LANGUAGES.EN]: 'Global Supply Chain Management'
    },
    'dashboard.ad.service.internationalBusinessConsulting': {
        [LANGUAGES.ZH]: '国际商务咨询',
        [LANGUAGES.EN]: 'International Business Consulting'
    },
    'dashboard.membership.tip': {
        [LANGUAGES.ZH]: '多种会员版本<br>升级会员即可使用更多功能',
        [LANGUAGES.EN]: 'Multiple membership plans<br>Upgrade to unlock more features'
    },
    'dashboard.membership.upgradeNow': {
        [LANGUAGES.ZH]: '立即升级',
        [LANGUAGES.EN]: 'Upgrade Now'
    },
    'dashboard.balance.exportRemaining': {
        [LANGUAGES.ZH]: '数据导出剩余次数',
        [LANGUAGES.EN]: 'Remaining Data Export Quota'
    },
    'dashboard.balance.searchRemaining': {
        [LANGUAGES.ZH]: '数据搜索剩余次数',
        [LANGUAGES.EN]: 'Remaining Data Search Quota'
    },
    'dashboard.vip.openVIP': {
        [LANGUAGES.ZH]: '开通VIP',
        [LANGUAGES.EN]: 'Activate VIP'
    },
    'dashboard.advisor.title': {
        [LANGUAGES.ZH]: '专属技术顾问',
        [LANGUAGES.EN]: 'Dedicated Technical Advisor'
    },
    'dashboard.advisor.desc': {
        [LANGUAGES.ZH]: '遇到疑难杂症可以及时咨询我哦～',
        [LANGUAGES.EN]: 'Feel free to reach out for any technical issues~'
    },
    'dashboard.advisor.qrAlt': {
        [LANGUAGES.ZH]: '客服二维码',
        [LANGUAGES.EN]: 'Customer Service QR Code'
    },
    'dashboard.advisor.links.features': {
        [LANGUAGES.ZH]: '功能详解',
        [LANGUAGES.EN]: 'Feature Guide'
    },
    'dashboard.advisor.links.faq': {
        [LANGUAGES.ZH]: '问题解答',
        [LANGUAGES.EN]: 'Q&A'
    },
    'dashboard.advisor.links.feedback': {
        [LANGUAGES.ZH]: '需求反馈',
        [LANGUAGES.EN]: 'Feedback'
    },
    'dashboard.load.search.stats.failed': {
        [LANGUAGES.ZH]: '加载搜索记录条数统计失败',
        [LANGUAGES.EN]: 'Failed to load search count statistics'
    },
    'dashboard.updateTime': {
        [LANGUAGES.ZH]: '更新时间',
        [LANGUAGES.EN]: 'Update Time'
    },
    'dashboard.emailStats': {
        [LANGUAGES.ZH]: '邮件统计',
        [LANGUAGES.EN]: 'Email Statistics'
    },
    'dashboard.totalSent': {
        [LANGUAGES.ZH]: '发送总数',
        [LANGUAGES.EN]: 'Total Sent'
    },
    'dashboard.readCount': {
        [LANGUAGES.ZH]: '已读数',
        [LANGUAGES.EN]: 'Read Count'
    },
    'dashboard.successCount': {
        [LANGUAGES.ZH]: '成功数',
        [LANGUAGES.EN]: 'Success Count'
    },
    'dashboard.smsStats': {
        [LANGUAGES.ZH]: '短信统计',
        [LANGUAGES.EN]: 'SMS Statistics'
    },
    'dashboard.myData': {
        [LANGUAGES.ZH]: '我的数据',
        [LANGUAGES.EN]: 'My Data'
    },
    'dashboard.sentEmails': {
        [LANGUAGES.ZH]: '发送邮件',
        [LANGUAGES.EN]: 'Sent Emails'
    },
    'dashboard.sentSMS': {
        [LANGUAGES.ZH]: '发送短信',
        [LANGUAGES.EN]: 'Sent SMS'
    },
    'dashboard.queryTimes': {
        [LANGUAGES.ZH]: '查询次数',
        [LANGUAGES.EN]: 'Query Times'
    },
    'dashboard.tutorials': {
        [LANGUAGES.ZH]: '使用教程',
        [LANGUAGES.EN]: 'Usage Tutorials'
    },
    'dashboard.viewAll': {
        [LANGUAGES.ZH]: '查看全部',
        [LANGUAGES.EN]: 'View All'
    },
    'dashboard.clickToLearn': {
        [LANGUAGES.ZH]: '点击了解',
        [LANGUAGES.EN]: 'Click to Learn'
    },
    'dashboard.enterpriseTutorial': {
        [LANGUAGES.ZH]: '企业获客教程',
        [LANGUAGES.EN]: 'Enterprise Lead Tutorial'
    },
    'dashboard.cantonFairTutorial': {
        [LANGUAGES.ZH]: '广交会使用教程',
        [LANGUAGES.EN]: 'Canton Fair Usage Tutorial'
    },
    'dashboard.customsDataTutorial': {
        [LANGUAGES.ZH]: '海关数据使用教程',
        [LANGUAGES.EN]: 'Customs Data Usage Tutorial'
    },
    'dashboard.last7Days': {
        [LANGUAGES.ZH]: '近7天',
        [LANGUAGES.EN]: 'Last 7 Days'
    },
    'dashboard.all': {
        [LANGUAGES.ZH]: '全部',
        [LANGUAGES.EN]: 'All'
    },
    'dashboard.sentSms': {
        [LANGUAGES.ZH]: '发送短信',
        [LANGUAGES.EN]: 'Sent SMS'
    },
    'dashboard.searchCount': {
        [LANGUAGES.ZH]: '搜索次数',
        [LANGUAGES.EN]: 'Search Count'
    },
    'dashboard.monitoringTask': {
        [LANGUAGES.ZH]: '监控任务',
        [LANGUAGES.EN]: 'Monitoring Task'
    },
    'dashboard.alreadyMessaged': {
        [LANGUAGES.ZH]: '已私信',
        [LANGUAGES.EN]: 'Already Messaged'
    },
    'dashboard.videoCount': {
        [LANGUAGES.ZH]: '视频数',
        [LANGUAGES.EN]: 'Video Count'
    },
    'dashboard.leads': {
        [LANGUAGES.ZH]: '线索',
        [LANGUAGES.EN]: 'Leads'
    },
    'dashboard.searchHistory': {
        [LANGUAGES.ZH]: '搜索记录',
        [LANGUAGES.EN]: 'Search History'
    },
    'dashboard.viewMore': {
        [LANGUAGES.ZH]: '查看更多',
        [LANGUAGES.EN]: 'View More'
    },
    'dashboard.customsData': {
        [LANGUAGES.ZH]: '海关数据',
        [LANGUAGES.EN]: 'Customs Data'
    },
    'dashboard.keyword': {
        [LANGUAGES.ZH]: '关键词',
        [LANGUAGES.EN]: 'Keyword'
    },
    'dashboard.data': {
        [LANGUAGES.ZH]: '数据',
        [LANGUAGES.EN]: 'Data'
    },
    'dashboard.items': {
        [LANGUAGES.ZH]: '条',
        [LANGUAGES.EN]: 'items'
    },
    'dashboard.searchTime': {
        [LANGUAGES.ZH]: '搜索时间',
        [LANGUAGES.EN]: 'Search Time'
    },
    'dashboard.cantonFair': {
        [LANGUAGES.ZH]: '广交会',
        [LANGUAGES.EN]: 'Canton Fair'
    },
    'dashboard.enterprise': {
        [LANGUAGES.ZH]: '企业',
        [LANGUAGES.EN]: 'Enterprise'
    },
    'dashboard.quickAccess': {
        [LANGUAGES.ZH]: '快捷入口',
        [LANGUAGES.EN]: 'Quick Access'
    },
    'dashboard.customize': {
        [LANGUAGES.ZH]: '自定义',
        [LANGUAGES.EN]: 'Customize'
    },
    'dashboard.companySearch': {
        [LANGUAGES.ZH]: '企业搜索',
        [LANGUAGES.EN]: 'Company Search'
    },
    'dashboard.cantonFairBuyers': {
        [LANGUAGES.ZH]: '广交会采购商',
        [LANGUAGES.EN]: 'Canton Fair Buyers'
    },
    'dashboard.mapSearch': {
        [LANGUAGES.ZH]: '地图搜客',
        [LANGUAGES.EN]: 'Map Search'
    },
    'dashboard.socialInsights': {
        [LANGUAGES.ZH]: '社媒洞察',
        [LANGUAGES.EN]: 'Social Media Insights'
    },

    // 资源
    'resource.customsData': {
        [LANGUAGES.ZH]: '海关数据',
        [LANGUAGES.EN]: 'Customs Data'
    },
    'resource.cantonFair': {
        [LANGUAGES.ZH]: '广交会',
        [LANGUAGES.EN]: 'Canton Fair'
    },
    'resource.cantonFairBuyers': {
        [LANGUAGES.ZH]: '广交会采购商',
        [LANGUAGES.EN]: 'Canton Fair Buyers'
    },
    'resource.enterprise': {
        [LANGUAGES.ZH]: '企业',
        [LANGUAGES.EN]: 'Enterprise'
    },
    'resource.globalCompanies': {
        [LANGUAGES.ZH]: '全球企业库',
        [LANGUAGES.EN]: 'Global Companies'
    },

    // 查询和搜索
    'search.inquiry': {
        [LANGUAGES.ZH]: '询盘',
        [LANGUAGES.EN]: 'Inquiry'
    },
    'search.today': {
        [LANGUAGES.ZH]: '今天',
        [LANGUAGES.EN]: 'Today'
    },
    'search.last7Days': {
        [LANGUAGES.ZH]: '近7天',
        [LANGUAGES.EN]: 'Last 7 Days'
    },
    'search.all': {
        [LANGUAGES.ZH]: '全部',
        [LANGUAGES.EN]: 'All'
    },
    'search.history': {
        [LANGUAGES.ZH]: '搜索记录',
        [LANGUAGES.EN]: 'Search History'
    },
    'search.count': {
        [LANGUAGES.ZH]: '搜索次数',
        [LANGUAGES.EN]: 'Search Count'
    },
    'search.viewMore': {
        [LANGUAGES.ZH]: '查看更多',
        [LANGUAGES.EN]: 'View More'
    },
    'search.keyword': {
        [LANGUAGES.ZH]: '关键词',
        [LANGUAGES.EN]: 'Keyword'
    },
    'search.data': {
        [LANGUAGES.ZH]: '数据',
        [LANGUAGES.EN]: 'Data'
    },
    'search.searchTime': {
        [LANGUAGES.ZH]: '搜索时间',
        [LANGUAGES.EN]: 'Search Time'
    },
    'search.items': {
        [LANGUAGES.ZH]: '条',
        [LANGUAGES.EN]: 'items'
    },

    // 工具和功能
    'tools.smartEmailSearch': {
        [LANGUAGES.ZH]: '联系方式搜查',
        [LANGUAGES.EN]: 'Contact Search'
    },
    'tools.smartPhoneSearch': {
        [LANGUAGES.ZH]: '联系方式互查',
        [LANGUAGES.EN]: 'Mutual Contact Check'
    },
    'tools.emailLookup': {
        [LANGUAGES.ZH]: '邮件反查',
        [LANGUAGES.EN]: 'Email Lookup'
    },
    'tools.domainLookup': {
        [LANGUAGES.ZH]: '域名反查',
        [LANGUAGES.EN]: 'Domain Lookup'
    },
    'tools.socialLookup': {
        [LANGUAGES.ZH]: '社媒反查',
        [LANGUAGES.EN]: 'Social Media Lookup'
    },
    'tools.conversionTools': {
        [LANGUAGES.ZH]: '转化工具',
        [LANGUAGES.EN]: 'Conversion Tools'
    },
    'tools.smsCenter': {
        [LANGUAGES.ZH]: '短信中心',
        [LANGUAGES.EN]: 'SMS Center'
    },
    'tools.emailCenter': {
        [LANGUAGES.ZH]: '邮件中心',
        [LANGUAGES.EN]: 'Email Center'
    },

    // 管理
    'management.center': {
        [LANGUAGES.ZH]: '管理中心',
        [LANGUAGES.EN]: 'Management Center'
    },
    'management.myFavorites': {
        [LANGUAGES.ZH]: '我的收藏',
        [LANGUAGES.EN]: 'My Favorites'
    },
    'management.personalCenter': {
        [LANGUAGES.ZH]: '个人中心',
        [LANGUAGES.EN]: 'Personal Center'
    },

    // 日期选择器
    'datepicker.to': {
        [LANGUAGES.ZH]: '至',
        [LANGUAGES.EN]: 'to'
    },
    'datepicker.startDate': {
        [LANGUAGES.ZH]: '开始日期',
        [LANGUAGES.EN]: 'Start Date'
    },
    'datepicker.endDate': {
        [LANGUAGES.ZH]: '结束日期',
        [LANGUAGES.EN]: 'End Date'
    },

    // 监控
    'monitor.task': {
        [LANGUAGES.ZH]: '监控任务',
        [LANGUAGES.EN]: 'Monitoring Task'
    },
    'monitor.alreadyMessaged': {
        [LANGUAGES.ZH]: '已私信',
        [LANGUAGES.EN]: 'Already Messaged'
    },
    'monitor.videoCount': {
        [LANGUAGES.ZH]: '视频数',
        [LANGUAGES.EN]: 'Video Count'
    },
    'monitor.leads': {
        [LANGUAGES.ZH]: '线索',
        [LANGUAGES.EN]: 'Leads'
    },

    // 右侧边栏
    'sidebar.quickAccess': {
        [LANGUAGES.ZH]: '快捷入口',
        [LANGUAGES.EN]: 'Quick Access'
    },
    'sidebar.customize': {
        [LANGUAGES.ZH]: '自定义',
        [LANGUAGES.EN]: 'Customize'
    },
    'sidebar.enterpriseSearch': {
        [LANGUAGES.ZH]: '企业搜索',
        [LANGUAGES.EN]: 'Enterprise Search'
    },
    'sidebar.mapSearch': {
        [LANGUAGES.ZH]: '地图搜客',
        [LANGUAGES.EN]: 'Map Search'
    },
    'sidebar.socialInsights': {
        [LANGUAGES.ZH]: '社媒洞察',
        [LANGUAGES.EN]: 'Social Media Insights'
    },
    'sidebar.accountBalance': {
        [LANGUAGES.ZH]: '账户余额',
        [LANGUAGES.EN]: 'Account Balance'
    },
    'sidebar.recharge': {
        [LANGUAGES.ZH]: '充值',
        [LANGUAGES.EN]: 'Recharge'
    },
    'sidebar.remainingSearches': {
        [LANGUAGES.ZH]: '数据搜索剩余搜索次数',
        [LANGUAGES.EN]: 'Remaining data search queries'
    },
    'sidebar.activateVIP': {
        [LANGUAGES.ZH]: '开通VIP',
        [LANGUAGES.EN]: 'Activate VIP'
    },
    'sidebar.technicalAdvisor': {
        [LANGUAGES.ZH]: '专属技术顾问',
        [LANGUAGES.EN]: 'Dedicated Technical Advisor'
    },
    'sidebar.consultHelp': {
        [LANGUAGES.ZH]: '遇到疑难杂症可以及时咨询我哦',
        [LANGUAGES.EN]: 'Feel free to consult with me if you encounter any issues'
    },
    'sidebar.featureExplanation': {
        [LANGUAGES.ZH]: '功能讲解',
        [LANGUAGES.EN]: 'Feature Explanation'
    },
    'sidebar.qa': {
        [LANGUAGES.ZH]: '问题解答',
        [LANGUAGES.EN]: 'Q&A'
    },
    'sidebar.feedback': {
        [LANGUAGES.ZH]: '需求反馈',
        [LANGUAGES.EN]: 'Feedback'
    },
    'sidebar.multipleVersions': {
        [LANGUAGES.ZH]: '多种会员版本',
        [LANGUAGES.EN]: 'Multiple membership options'
    },
    'sidebar.upgradeFeatures': {
        [LANGUAGES.ZH]: '升级会员即可享受对应功能免费',
        [LANGUAGES.EN]: 'Upgrade for free features'
    },
    'sidebar.upgradeNow': {
        [LANGUAGES.ZH]: '立即升级',
        [LANGUAGES.EN]: 'Upgrade Now'
    },
    'sidebar.customerService': {
        [LANGUAGES.ZH]: '咨询客服',
        [LANGUAGES.EN]: 'Customer Service'
    },

    // 语言切换提示
    'language.switchedToChinese': {
        [LANGUAGES.ZH]: '已切换到中文',
        [LANGUAGES.EN]: 'Switched to Chinese'
    },
    'language.switchedToEnglish': {
        [LANGUAGES.ZH]: '已切换到英文',
        [LANGUAGES.EN]: 'Switched to English'
    },
    'language.switchFailed': {
        [LANGUAGES.ZH]: '语言切换失败',
        [LANGUAGES.EN]: 'Failed to switch language'
    },

    // 应用内硬编码文本
    'dashboard.inquiries': {
        [LANGUAGES.ZH]: '询盘',
        [LANGUAGES.EN]: 'Inquiries'
    },
    'dashboard.tiktok': {
        [LANGUAGES.ZH]: 'Tik Tok',
        [LANGUAGES.EN]: 'Tik Tok'
    },
    'dashboard.youtube': {
        [LANGUAGES.ZH]: 'Youtube',
        [LANGUAGES.EN]: 'Youtube'
    },
    'dashboard.today': {
        [LANGUAGES.ZH]: '今天',
        [LANGUAGES.EN]: 'Today'
    },

    // VIP相关
    'vip.multipleVersions': {
        [LANGUAGES.ZH]: '多种会员版本',
        [LANGUAGES.EN]: 'Multiple Versions'
    },
    'vip.upgradeDescription': {
        [LANGUAGES.ZH]: '升级会员享受更多功能',
        [LANGUAGES.EN]: 'Upgrade for more features'
    },
    'vip.upgradeNow': {
        [LANGUAGES.ZH]: '立即升级',
        [LANGUAGES.EN]: 'Upgrade Now'
    },
    'vip.activateVIP': {
        [LANGUAGES.ZH]: '开通VIP',
        [LANGUAGES.EN]: 'Activate VIP'
    },

    // 支持相关
    'support.technicalAdvisor': {
        [LANGUAGES.ZH]: '技术顾问',
        [LANGUAGES.EN]: 'Technical Advisor'
    },
    'support.advisorDescription': {
        [LANGUAGES.ZH]: '专属服务顾问',
        [LANGUAGES.EN]: 'Dedicated service advisor'
    },
    'support.featureGuide': {
        [LANGUAGES.ZH]: '功能指南',
        [LANGUAGES.EN]: 'Feature Guide'
    },
    'support.troubleshooting': {
        [LANGUAGES.ZH]: '问题排查',
        [LANGUAGES.EN]: 'Troubleshooting'
    },
    'support.feedback': {
        [LANGUAGES.ZH]: '意见反馈',
        [LANGUAGES.EN]: 'Feedback'
    },
    'support.contactSupport': {
        [LANGUAGES.ZH]: '联系客服',
        [LANGUAGES.EN]: 'Contact Support'
    },
    'linkedin.title': {
        [LANGUAGES.ZH]: '领英获客',
        [LANGUAGES.EN]: 'LinkedIn Leads'
    },
    'linkedin.search': {
        [LANGUAGES.ZH]: '搜索',
        [LANGUAGES.EN]: 'Search'
    },
    'linkedin.search.name': {
        [LANGUAGES.ZH]: '搜索姓名',
        [LANGUAGES.EN]: 'Search name'
    },
    'linkedin.reset.filters': {
        [LANGUAGES.ZH]: '重置筛选条件',
        [LANGUAGES.EN]: 'Reset Filters'
    },
    'linkedin.company': {
        [LANGUAGES.ZH]: '所属公司',
        [LANGUAGES.EN]: 'Company'
    },
    'linkedin.search.company': {
        [LANGUAGES.ZH]: '搜索公司',
        [LANGUAGES.EN]: 'Search company'
    },
    'linkedin.job.title': {
        [LANGUAGES.ZH]: '职位',
        [LANGUAGES.EN]: 'Job Title'
    },
    'linkedin.search.job': {
        [LANGUAGES.ZH]: '搜索职位',
        [LANGUAGES.EN]: 'Search job title'
    },
    'linkedin.industry': {
        [LANGUAGES.ZH]: '行业',
        [LANGUAGES.EN]: 'Industry'
    },
    'linkedin.search.industry': {
        [LANGUAGES.ZH]: '搜索行业',
        [LANGUAGES.EN]: 'Search industry'
    },
    'linkedin.location': {
        [LANGUAGES.ZH]: '国家/地区',
        [LANGUAGES.EN]: 'Country/Region'
    },
    'linkedin.search.location': {
        [LANGUAGES.ZH]: '搜索国家/地区',
        [LANGUAGES.EN]: 'Search country/region'
    },
    'linkedin.contact.method': {
        [LANGUAGES.ZH]: '联系方式',
        [LANGUAGES.EN]: 'Contact Method'
    },
    'linkedin.url.filter': {
        [LANGUAGES.ZH]: 'URL筛选',
        [LANGUAGES.EN]: 'URL Filter'
    },
    'linkedin.url.company.website': {
        [LANGUAGES.ZH]: '公司网址',
        [LANGUAGES.EN]: 'Company Website'
    },
    'linkedin.url.facebook': {
        [LANGUAGES.ZH]: 'Facebook链接',
        [LANGUAGES.EN]: 'Facebook URL'
    },
    'linkedin.url.twitter': {
        [LANGUAGES.ZH]: 'Twitter链接',
        [LANGUAGES.EN]: 'Twitter URL'
    },
    'linkedin.url.github': {
        [LANGUAGES.ZH]: 'Github链接',
        [LANGUAGES.EN]: 'Github URL'
    },
    'linkedin.facebook.url': {
        [LANGUAGES.ZH]: 'Facebook链接:',
        [LANGUAGES.EN]: 'Facebook URL:'
    },
    'linkedin.twitter.url': {
        [LANGUAGES.ZH]: 'Twitter链接:',
        [LANGUAGES.EN]: 'Twitter URL:'
    },
    'linkedin.github.url': {
        [LANGUAGES.ZH]: 'Github链接:',
        [LANGUAGES.EN]: 'Github URL:'
    },
    'linkedin.facebook': {
        [LANGUAGES.ZH]: 'Facebook',
        [LANGUAGES.EN]: 'Facebook'
    },
    'linkedin.twitter': {
        [LANGUAGES.ZH]: 'Twitter',
        [LANGUAGES.EN]: 'Twitter'
    },
    'linkedin.github': {
        [LANGUAGES.ZH]: 'Github',
        [LANGUAGES.EN]: 'Github'
    },
    'linkedin.company.size': {
        [LANGUAGES.ZH]: '公司规模',
        [LANGUAGES.EN]: 'Company Size'
    },
    // 'linkedin.company.founded': {
    //   [LANGUAGES.ZH]: '公司成立时间',
    //   [LANGUAGES.EN]: 'Company Founded'
    // },
    'linkedin.company.founded.after_2020': {
        [LANGUAGES.ZH]: '2020年以后成立',
        [LANGUAGES.EN]: 'Founded after 2020'
    },
    'linkedin.company.founded.2015_2020': {
        [LANGUAGES.ZH]: '2015-2020年成立',
        [LANGUAGES.EN]: 'Founded 2015-2020'
    },
    'linkedin.company.founded.2010_2015': {
        [LANGUAGES.ZH]: '2010-2015年成立',
        [LANGUAGES.EN]: 'Founded 2010-2015'
    },
    'linkedin.company.founded.2005_2010': {
        [LANGUAGES.ZH]: '2005-2010年成立',
        [LANGUAGES.EN]: 'Founded 2005-2010'
    },
    'linkedin.company.founded.2000_2005': {
        [LANGUAGES.ZH]: '2000-2005年成立',
        [LANGUAGES.EN]: 'Founded 2000-2005'
    },
    'linkedin.company.founded.before_2000': {
        [LANGUAGES.ZH]: '2000年以前成立',
        [LANGUAGES.EN]: 'Founded before 2000'
    },
    // 新增：灵活筛选模式标签
    'linkedin.company.founded.mode.exact': {
        [LANGUAGES.ZH]: '准确时间查找',
        [LANGUAGES.EN]: 'Exact Year Search'
    },
    'linkedin.company.founded.mode.range': {
        [LANGUAGES.ZH]: '范围时间查找',
        [LANGUAGES.EN]: 'Range Search'
    },
    'linkedin.company.founded.mode.preset': {
        [LANGUAGES.ZH]: '预设范围',
        [LANGUAGES.EN]: 'Preset Ranges'
    },
    'linkedin.company.founded.exact.placeholder': {
        [LANGUAGES.ZH]: '输入年份，如：2010',
        [LANGUAGES.EN]: 'Enter year, e.g.: 2010'
    },
    'linkedin.company.founded.range.start': {
        [LANGUAGES.ZH]: '起始年份',
        [LANGUAGES.EN]: 'Start Year'
    },
    'linkedin.company.founded.range.end': {
        [LANGUAGES.ZH]: '结束年份',
        [LANGUAGES.EN]: 'End Year'
    },
    'linkedin.company.founded.range.start.placeholder': {
        [LANGUAGES.ZH]: '如：2008',
        [LANGUAGES.EN]: 'e.g.: 2008'
    },
    'linkedin.company.founded.range.end.placeholder': {
        [LANGUAGES.ZH]: '如：2018',
        [LANGUAGES.EN]: 'e.g.: 2018'
    },
    'linkedin.company.founded.add.year': {
        [LANGUAGES.ZH]: '添加年份',
        [LANGUAGES.EN]: 'Add Year'
    },
    'linkedin.company.founded.clear.all': {
        [LANGUAGES.ZH]: '清空所有',
        [LANGUAGES.EN]: 'Clear All'
    },
    'linkedin.company.founded.selected.years': {
        [LANGUAGES.ZH]: '已选择年份',
        [LANGUAGES.EN]: 'Selected Years'
    },
    'linkedin.company.founded.custom.range': {
        [LANGUAGES.ZH]: '自定义范围',
        [LANGUAGES.EN]: 'Custom Range'
    },
    'linkedin.company.founded.error.invalid.year': {
        [LANGUAGES.ZH]: '请输入有效的年份（1900-2030）',
        [LANGUAGES.EN]: 'Please enter a valid year (1900-2030)'
    },
    'linkedin.company.founded.error.range.invalid': {
        [LANGUAGES.ZH]: '结束年份必须大于起始年份',
        [LANGUAGES.EN]: 'End year must be greater than start year'
    },
    'linkedin.company.founded.search.hint': {
        [LANGUAGES.ZH]: '输入年份后按回车键搜索',
        [LANGUAGES.EN]: 'Press Enter to search after entering year'
    },
    'linkedin.company.founded': {
        [LANGUAGES.ZH]: '公司成立时间',
        [LANGUAGES.EN]: 'Company Founded'
    },
    'linkedin.company.founded.select.placeholder': {
        [LANGUAGES.ZH]: '选择成立年份',
        [LANGUAGES.EN]: 'Select founding years'
    },
    'linkedin.matched.count': {
        [LANGUAGES.ZH]: '符合条件的联系人：',
        [LANGUAGES.EN]: 'Matching contacts: '
    },
    'linkedin.matched.count.unit': {
        [LANGUAGES.ZH]: '条',
        [LANGUAGES.EN]: ''
    },
    // LinkedIn敏感信息处理相关翻译
    'linkedin.view.full.contact.tooltip': {
        [LANGUAGES.ZH]: '点击消耗一次次数查看完整信息',
        [LANGUAGES.EN]: 'Click to view full information (costs 1 credit)'
    },
    'linkedin.confirm.view.contact': {
        [LANGUAGES.ZH]: '查看完整联系信息将消耗一次查看次数，是否继续？',
        [LANGUAGES.EN]: 'Viewing full contact information will cost 1 credit. Continue?'
    },
    'linkedin.view.full.contact': {
        [LANGUAGES.ZH]: '查看完整联系信息',
        [LANGUAGES.EN]: 'View Full Contact Information'
    },
    'linkedin.confirm': {
        [LANGUAGES.ZH]: '确认',
        [LANGUAGES.EN]: 'Confirm'
    },
    'linkedin.cancel': {
        [LANGUAGES.ZH]: '取消',
        [LANGUAGES.EN]: 'Cancel'
    },
    'linkedin.contact.view.success': {
        [LANGUAGES.ZH]: '联系信息获取成功',
        [LANGUAGES.EN]: 'Contact information retrieved successfully'
    },
    'linkedin.contact.view.error': {
        [LANGUAGES.ZH]: '获取联系信息失败，请稍后重试',
        [LANGUAGES.EN]: 'Failed to retrieve contact information, please try again later'
    },
    'linkedin.export.data': {
        [LANGUAGES.ZH]: '导出数据',
        [LANGUAGES.EN]: 'Export Data'
    },
    'linkedin.no.data': {
        [LANGUAGES.ZH]: '没有符合条件的联系人',
        [LANGUAGES.EN]: 'No matching contacts'
    },
    'linkedin.details': {
        [LANGUAGES.ZH]: '详情',
        [LANGUAGES.EN]: 'Details'
    },
    'linkedin.collapse': {
        [LANGUAGES.ZH]: '收起',
        [LANGUAGES.EN]: 'Collapse'
    },
    'linkedin.position': {
        [LANGUAGES.ZH]: '职位:',
        [LANGUAGES.EN]: 'Position:'
    },
    'linkedin.company.label': {
        [LANGUAGES.ZH]: '所属公司:',
        [LANGUAGES.EN]: 'Company:'
    },
    'linkedin.industry.label': {
        [LANGUAGES.ZH]: '行业:',
        [LANGUAGES.EN]: 'Industry:'
    },
    'linkedin.contact.info': {
        [LANGUAGES.ZH]: '联系信息:',
        [LANGUAGES.EN]: 'Contact Info:'
    },
    'linkedin.details.info': {
        [LANGUAGES.ZH]: '详细信息',
        [LANGUAGES.EN]: 'Detailed Information'
    },
    'linkedin.location.label': {
        [LANGUAGES.ZH]: '地点:',
        [LANGUAGES.EN]: 'Location:'
    },
    'linkedin.field.location': {
        [LANGUAGES.ZH]: '地点:',
        [LANGUAGES.EN]: 'Location:'
    },
    'linkedin.company.size.label': {
        [LANGUAGES.ZH]: '公司规模:',
        [LANGUAGES.EN]: 'Company Size:'
    },
    'linkedin.company.industry': {
        [LANGUAGES.ZH]: '公司行业:',
        [LANGUAGES.EN]: 'Company Industry:'
    },
    'linkedin.salary.range': {
        [LANGUAGES.ZH]: '薪资范围:',
        [LANGUAGES.EN]: 'Salary Range:'
    },
    'linkedin.work.experience': {
        [LANGUAGES.ZH]: '工作经验:',
        [LANGUAGES.EN]: 'Work Experience:'
    },
    'linkedin.years': {
        [LANGUAGES.ZH]: '年',
        [LANGUAGES.EN]: 'years'
    },
    'linkedin.company.website': {
        [LANGUAGES.ZH]: '公司网站:',
        [LANGUAGES.EN]: 'Company Website:'
    },
    'linkedin.linkedin.url': {
        [LANGUAGES.ZH]: '领英链接:',
        [LANGUAGES.EN]: 'LinkedIn URL:'
    },
    'linkedin.skills': {
        [LANGUAGES.ZH]: '技能:',
        [LANGUAGES.EN]: 'Skills:'
    },
    'linkedin.job.summary': {
        [LANGUAGES.ZH]: '工作概述:',
        [LANGUAGES.EN]: 'Job Summary:'
    },
    'linkedin.contact.details': {
        [LANGUAGES.ZH]: '联系人详情',
        [LANGUAGES.EN]: 'Contact Details'
    },
    'linkedin.contact.methods': {
        [LANGUAGES.ZH]: '联系方式',
        [LANGUAGES.EN]: 'Contact Methods'
    },
    'linkedin.email': {
        [LANGUAGES.ZH]: '邮箱:',
        [LANGUAGES.EN]: 'Email:'
    },
    'linkedin.phone': {
        [LANGUAGES.ZH]: '电话:',
        [LANGUAGES.EN]: 'Phone:'
    },
    'linkedin.linkedin': {
        [LANGUAGES.ZH]: '领英:',
        [LANGUAGES.EN]: 'LinkedIn:'
    },
    'linkedin.work.info': {
        [LANGUAGES.ZH]: '工作信息',
        [LANGUAGES.EN]: 'Work Information'
    },
    'linkedin.unknown.position': {
        [LANGUAGES.ZH]: '职位未知',
        [LANGUAGES.EN]: 'Unknown Position'
    },
    'linkedin.unknown.company': {
        [LANGUAGES.ZH]: '公司未知',
        [LANGUAGES.EN]: 'Unknown Company'
    },
    'linkedin.unknown.location': {
        [LANGUAGES.ZH]: '地点未知',
        [LANGUAGES.EN]: 'Unknown Location'
    },

    // CustomsTradeInfo页面相关
    'customs.title': {
        [LANGUAGES.ZH]: '海关贸易数据',
        [LANGUAGES.EN]: 'Customs Trade Data'
    },
    'customs.search': {
        [LANGUAGES.ZH]: '搜索',
        [LANGUAGES.EN]: 'Search'
    },
    'customs.product.keyword.english': {
        [LANGUAGES.ZH]: '产品关键词(英文)',
        [LANGUAGES.EN]: 'Product Keyword (English)'
    },
    'customs.hs.code': {
        [LANGUAGES.ZH]: '海关编码',
        [LANGUAGES.EN]: 'HS Code'
    },
    'customs.reset.search': {
        [LANGUAGES.ZH]: '重置搜索条件',
        [LANGUAGES.EN]: 'Reset Search'
    },
    'customs.export.data': {
        [LANGUAGES.ZH]: '导出数据',
        [LANGUAGES.EN]: 'Export Data'
    },
    'customs.no.data': {
        [LANGUAGES.ZH]: '暂无数据',
        [LANGUAGES.EN]: 'No Data Available'
    },
    'customs.product.keyword': {
        [LANGUAGES.ZH]: '产品关键词:',
        [LANGUAGES.EN]: 'Product Keyword:'
    },
    'customs.bill.of.lading.number': {
        [LANGUAGES.ZH]: '提单号:',
        [LANGUAGES.EN]: 'Bill of Lading Number:'
    },
    'customs.product.description': {
        [LANGUAGES.ZH]: '产品描述:',
        [LANGUAGES.EN]: 'Product Description:'
    },
    'customs.origin.country': {
        [LANGUAGES.ZH]: '原产国:',
        [LANGUAGES.EN]: 'Origin Country:'
    },
    'customs.supplier': {
        [LANGUAGES.ZH]: '供应商:',
        [LANGUAGES.EN]: 'Supplier:'
    },
    'customs.port.of.loading': {
        [LANGUAGES.ZH]: '起运港:',
        [LANGUAGES.EN]: 'Port of Loading:'
    },
    'customs.destination.country': {
        [LANGUAGES.ZH]: '目的国:',
        [LANGUAGES.EN]: 'Destination Country:'
    },
    'customs.purchaser': {
        [LANGUAGES.ZH]: '采购商:',
        [LANGUAGES.EN]: 'Purchaser:'
    },
    'customs.port.of.destination': {
        [LANGUAGES.ZH]: '目的港:',
        [LANGUAGES.EN]: 'Port of Destination:'
    },
    'customs.start.date': {
        [LANGUAGES.ZH]: '开始日期:',
        [LANGUAGES.EN]: 'Start Date:'
    },
    'customs.end.date': {
        [LANGUAGES.ZH]: '结束日期:',
        [LANGUAGES.EN]: 'End Date:'
    },
    'customs.quantity': {
        [LANGUAGES.ZH]: '件',
        [LANGUAGES.EN]: 'pieces'
    },

    // Facebook相关
    'facebook.title': {
        [LANGUAGES.ZH]: 'Facebook联系人',
        [LANGUAGES.EN]: 'Facebook Contacts'
    },
    'facebook.search.placeholder': {
        [LANGUAGES.ZH]: '搜索联系人',
        [LANGUAGES.EN]: 'Search contacts'
    },
    'facebook.match': {
        [LANGUAGES.ZH]: '匹配数据',
        [LANGUAGES.EN]: 'Match Data'
    },
    'facebook.filter.gender': {
        [LANGUAGES.ZH]: '性别',
        [LANGUAGES.EN]: 'Gender'
    },
    'facebook.filter.location': {
        [LANGUAGES.ZH]: '所在地',
        [LANGUAGES.EN]: 'Location'
    },
    'facebook.filter.contactMethod': {
        [LANGUAGES.ZH]: '联系方式',
        [LANGUAGES.EN]: 'Contact Method'
    },
    'facebook.filter.all': {
        [LANGUAGES.ZH]: '全选',
        [LANGUAGES.EN]: 'All'
    },
    'facebook.filter.searchLocation': {
        [LANGUAGES.ZH]: '搜索所在地',
        [LANGUAGES.EN]: 'Search location'
    },
    'facebook.filter.locationHint': {
        [LANGUAGES.ZH]: '选择多个地区时，只显示符合任一选中地区的联系人',
        [LANGUAGES.EN]: 'When multiple locations are selected, contacts matching any of the selected locations will be displayed'
    },
    'facebook.filter.resetSuccess': {
        [LANGUAGES.ZH]: '已重置筛选条件',
        [LANGUAGES.EN]: 'Filters have been reset'
    },
    // 'facebook.gender.male': {
    //   [LANGUAGES.ZH]: '男',
    //   [LANGUAGES.EN]: 'Male'
    // },
    // 'facebook.gender.female': {
    //   [LANGUAGES.ZH]: '女',
    //   [LANGUAGES.EN]: 'Female'
    // },
    'facebook.contact.email': {
        [LANGUAGES.ZH]: '邮箱',
        [LANGUAGES.EN]: 'Email'
    },
    'facebook.contact.phone': {
        [LANGUAGES.ZH]: '电话',
        [LANGUAGES.EN]: 'Phone'
    },
    'facebook.error.fetchFailed': {
        [LANGUAGES.ZH]: '获取数据失败，请联系管理员',
        [LANGUAGES.EN]: 'Failed to fetch data, please contact administrator'
    },
    'facebook.no.data': {
        [LANGUAGES.ZH]: '未找到数据',
        [LANGUAGES.EN]: 'No data found'
    },
    'facebook.export': {
        [LANGUAGES.ZH]: '导出数据',
        [LANGUAGES.EN]: 'Export Data'
    },
    'facebook.addToLeads': {
        [LANGUAGES.ZH]: '添加到线索',
        [LANGUAGES.EN]: 'Add to Leads'
    },
    'facebook.viewDetails': {
        [LANGUAGES.ZH]: '查看详情',
        [LANGUAGES.EN]: 'View Details'
    },
    // 'facebook.gender.male': {
    //   [LANGUAGES.ZH]: '男',
    //   [LANGUAGES.EN]: 'Male'
    // },
    // 'facebook.gender.female': {
    //   [LANGUAGES.ZH]: '女',
    //   [LANGUAGES.EN]: 'Female'
    // },
    'facebook.gender.other': {
        [LANGUAGES.ZH]: '其他',
        [LANGUAGES.EN]: 'Other'
    },
    'facebook.email.hasEmail': {
        [LANGUAGES.ZH]: '有邮箱',
        [LANGUAGES.EN]: 'Has Email'
    },
    'facebook.email.noEmail': {
        [LANGUAGES.ZH]: '无邮箱',
        [LANGUAGES.EN]: 'No Email'
    },
    'facebook.remarks': {
        [LANGUAGES.ZH]: '备注',
        [LANGUAGES.EN]: 'Remarks'
    },
    'facebook.total': {
        [LANGUAGES.ZH]: '共 {total} 条记录',
        [LANGUAGES.EN]: 'Total {total} records'
    },
    'facebook.leads.success': {
        [LANGUAGES.ZH]: '已成功添加到线索',
        [LANGUAGES.EN]: 'Successfully added to leads'
    },
    'facebook.leads.failed': {
        [LANGUAGES.ZH]: '添加到线索失败',
        [LANGUAGES.EN]: 'Failed to add to leads'
    },
    // 添加缺失的翻译键
    'all': {
        [LANGUAGES.ZH]: '全部',
        [LANGUAGES.EN]: 'All'
    },
    'email': {
        [LANGUAGES.ZH]: '邮箱',
        [LANGUAGES.EN]: 'Email'
    },
    'phone': {
        [LANGUAGES.ZH]: '电话',
        [LANGUAGES.EN]: 'Phone'
    },
    'linkedin': {
        [LANGUAGES.ZH]: 'LinkedIn',
        [LANGUAGES.EN]: 'LinkedIn'
    },
    'enterRemarks': {
        [LANGUAGES.ZH]: '请输入备注信息',
        [LANGUAGES.EN]: 'Please enter remarks'
    },
    'cancel': {
        [LANGUAGES.ZH]: '取消',
        [LANGUAGES.EN]: 'Cancel'
    },
    'confirm': {
        [LANGUAGES.ZH]: '确认',
        [LANGUAGES.EN]: 'Confirm'
    },
    'noData': {
        [LANGUAGES.ZH]: '暂无数据',
        [LANGUAGES.EN]: 'No data available'
    },
    'search': {
        [LANGUAGES.ZH]: '搜索',
        [LANGUAGES.EN]: 'Search'
    },
    // 导出记录页面
    'exportRecords.title': {
        [LANGUAGES.ZH]: '我的导出记录',
        [LANGUAGES.EN]: 'My Export Records'
    },
    'exportRecords.fileName': {
        [LANGUAGES.ZH]: '文件名称',
        [LANGUAGES.EN]: 'File Name'
    },
    'exportRecords.dataSource': {
        [LANGUAGES.ZH]: '数据来源',
        [LANGUAGES.EN]: 'Data Source'
    },
    'exportRecords.fileSize': {
        [LANGUAGES.ZH]: '文件大小',
        [LANGUAGES.EN]: 'File Size'
    },
    'exportRecords.recordCount': {
        [LANGUAGES.ZH]: '记录数量',
        [LANGUAGES.EN]: 'Record Count'
    },
    'exportRecords.exportDate': {
        [LANGUAGES.ZH]: '导出时间',
        [LANGUAGES.EN]: 'Export Date'
    },
    'exportRecords.actions': {
        [LANGUAGES.ZH]: '操作',
        [LANGUAGES.EN]: 'Actions'
    },
    'exportRecords.download': {
        [LANGUAGES.ZH]: '下载',
        [LANGUAGES.EN]: 'Download'
    },
    'exportRecords.downloadSuccess': {
        [LANGUAGES.ZH]: '文件下载成功',
        [LANGUAGES.EN]: 'File downloaded successfully'
    },
    'exportRecords.downloadFailed': {
        [LANGUAGES.ZH]: '下载文件失败，请稍后重试',
        [LANGUAGES.EN]: 'Download failed, please try again later'
    },
    'exportRecords.noRecords': {
        [LANGUAGES.ZH]: '暂无导出记录',
        [LANGUAGES.EN]: 'No export records'
    },
    // Twitter相关
    'twitter.title': {
        [LANGUAGES.ZH]: '推特联系人',
        [LANGUAGES.EN]: 'Twitter Contacts'
    },
    'twitter.search.placeholder': {
        [LANGUAGES.ZH]: '搜索联系人',
        [LANGUAGES.EN]: 'Search contacts'
    },
    'twitter.no.data': {
        [LANGUAGES.ZH]: '没有找到符合条件的联系人',
        [LANGUAGES.EN]: 'No matching contacts found'
    },
    'twitter.reset.filters': {
        [LANGUAGES.ZH]: '重置筛选条件',
        [LANGUAGES.EN]: 'Reset Filters'
    },
    'twitter.reload': {
        [LANGUAGES.ZH]: '重新加载',
        [LANGUAGES.EN]: 'Reload'
    },
    'twitter.export.data': {
        [LANGUAGES.ZH]: '导出数据',
        [LANGUAGES.EN]: 'Export Data'
    },
    'twitter.export.loading': {
        [LANGUAGES.ZH]: '导出中...',
        [LANGUAGES.EN]: 'Exporting...'
    },
    'twitter.detail.collapse': {
        [LANGUAGES.ZH]: '收起',
        [LANGUAGES.EN]: 'Collapse'
    },
    'twitter.detail.expand': {
        [LANGUAGES.ZH]: '详情',
        [LANGUAGES.EN]: 'Details'
    },
    'twitter.detail.title': {
        [LANGUAGES.ZH]: '详细信息',
        [LANGUAGES.EN]: 'Detailed Information'
    },
    'twitter.email': {
        [LANGUAGES.ZH]: '邮箱:',
        [LANGUAGES.EN]: 'Email:'
    },
    'twitter.username': {
        [LANGUAGES.ZH]: '用户名:',
        [LANGUAGES.EN]: 'Username:'
    },
    'twitter.followers': {
        [LANGUAGES.ZH]: '关注者数量:',
        [LANGUAGES.EN]: 'Followers:'
    },
    'twitter.created.time': {
        [LANGUAGES.ZH]: '账户创建时间:',
        [LANGUAGES.EN]: 'Account Created:'
    },
    'twitter.screenname': {
        [LANGUAGES.ZH]: '屏幕名称:',
        [LANGUAGES.EN]: 'Screen Name:'
    },
    'twitter.website': {
        [LANGUAGES.ZH]: '个人网址:',
        [LANGUAGES.EN]: 'Website:'
    },
    'twitter.description': {
        [LANGUAGES.ZH]: '描述信息:',
        [LANGUAGES.EN]: 'Description:'
    },
    'twitter.friends': {
        [LANGUAGES.ZH]: '好友数量:',
        [LANGUAGES.EN]: 'Friends Count:'
    },
    'twitter.listed': {
        [LANGUAGES.ZH]: '列表数量:',
        [LANGUAGES.EN]: 'Listed Count:'
    },
    'twitter.statuses': {
        [LANGUAGES.ZH]: '推文数量:',
        [LANGUAGES.EN]: 'Tweets Count:'
    },
    'twitter.last.source': {
        [LANGUAGES.ZH]: '最后推文来源:',
        [LANGUAGES.EN]: 'Last Tweet Source:'
    },
    'twitter.export.dialog.title': {
        [LANGUAGES.ZH]: '导出数据',
        [LANGUAGES.EN]: 'Export Data'
    },
    'twitter.export.matching.records': {
        [LANGUAGES.ZH]: '符合条件的记录数:',
        [LANGUAGES.EN]: 'Matching records:'
    },
    'twitter.export.balance': {
        [LANGUAGES.ZH]: '当前导出余额:',
        [LANGUAGES.EN]: 'Current export balance:'
    },
    'twitter.export.note': {
        [LANGUAGES.ZH]: '注意: 每导出一条记录将消耗一点导出额度',
        [LANGUAGES.EN]: 'Note: Each exported record consumes one export quota'
    },
    'twitter.export.enter.count': {
        [LANGUAGES.ZH]: '请输入要导出的记录数:',
        [LANGUAGES.EN]: 'Please enter the number of records to export:'
    },
    'twitter.export.error.exceed': {
        [LANGUAGES.ZH]: '导出数量不能超过符合条件的记录数',
        [LANGUAGES.EN]: 'Export count cannot exceed the number of matching records'
    },
    'twitter.export.error.balance': {
        [LANGUAGES.ZH]: '导出余额不足',
        [LANGUAGES.EN]: 'Insufficient export balance'
    },
    'twitter.export.error.limit': {
        [LANGUAGES.ZH]: '导出数量不能超过100万',
        [LANGUAGES.EN]: 'Export count cannot exceed 1 million'
    },
    'twitter.cancel': {
        [LANGUAGES.ZH]: '取消',
        [LANGUAGES.EN]: 'Cancel'
    },
    'twitter.export.confirm': {
        [LANGUAGES.ZH]: '确认导出',
        [LANGUAGES.EN]: 'Confirm Export'
    },
    'twitter.email.filter': {
        [LANGUAGES.ZH]: '邮箱',
        [LANGUAGES.EN]: 'Email'
    },
    'twitter.email.placeholder': {
        [LANGUAGES.ZH]: '请输入邮箱',
        [LANGUAGES.EN]: 'Please enter email'
    },
    'twitter.registration.year': {
        [LANGUAGES.ZH]: '注册年份',
        [LANGUAGES.EN]: 'Registration Year'
    },
    'twitter.select.year': {
        [LANGUAGES.ZH]: '请选择注册年份',
        [LANGUAGES.EN]: 'Select registration year'
    },
    'twitter.followers.count': {
        [LANGUAGES.ZH]: '关注者数量',
        [LANGUAGES.EN]: 'Followers Count'
    },
    'twitter.min.value': {
        [LANGUAGES.ZH]: '最小值',
        [LANGUAGES.EN]: 'Min value'
    },
    'twitter.max.value': {
        [LANGUAGES.ZH]: '最大值',
        [LANGUAGES.EN]: 'Max value'
    },
    'twitter.to': {
        [LANGUAGES.ZH]: '至',
        [LANGUAGES.EN]: 'to'
    },

    // LinkedIn公司规模选项
    'linkedin.company.size.self-employed': {
        [LANGUAGES.ZH]: '个体经营',
        [LANGUAGES.EN]: 'Self-employed'
    },
    'linkedin.company.size.1-10': {
        [LANGUAGES.ZH]: '1-10人',
        [LANGUAGES.EN]: '1-10'
    },
    'linkedin.company.size.11-50': {
        [LANGUAGES.ZH]: '11-50人',
        [LANGUAGES.EN]: '11-50'
    },
    'linkedin.company.size.51-200': {
        [LANGUAGES.ZH]: '51-200人',
        [LANGUAGES.EN]: '51-200'
    },
    'linkedin.company.size.201-500': {
        [LANGUAGES.ZH]: '201-500人',
        [LANGUAGES.EN]: '201-500'
    },
    'linkedin.company.size.501-1000': {
        [LANGUAGES.ZH]: '501-1000人',
        [LANGUAGES.EN]: '501-1000'
    },
    'linkedin.company.size.1001-5000': {
        [LANGUAGES.ZH]: '1001-5000人',
        [LANGUAGES.EN]: '1001-5000'
    },
    'linkedin.company.size.5001-10000': {
        [LANGUAGES.ZH]: '5001-10000人',
        [LANGUAGES.EN]: '5001-10000'
    },
    'linkedin.company.size.10001+': {
        [LANGUAGES.ZH]: '10001人以上',
        [LANGUAGES.EN]: '10001+'
    },

    // LinkedIn职位选项 - 完整翻译
    'linkedin.job.ndt_engineer': {
        [LANGUAGES.ZH]: '无损检测工程师',
        [LANGUAGES.EN]: 'ndt engineer'
    },
    'linkedin.job.medical_laboratory_technician': {
        [LANGUAGES.ZH]: '医学实验室技师',
        [LANGUAGES.EN]: 'medical laboratory technician'
    },
    'linkedin.job.laboratory_assistant': {
        [LANGUAGES.ZH]: '实验室助理',
        [LANGUAGES.EN]: 'laboratory assistant'
    },
    'linkedin.job.bookkeeper_and_office_coordinator': {
        [LANGUAGES.ZH]: '簿记员兼办公室协调员',
        [LANGUAGES.EN]: 'bookkeeper and office coordinator'
    },
    'linkedin.job.software_engineer': {
        [LANGUAGES.ZH]: '软件工程师',
        [LANGUAGES.EN]: 'software engineer'
    },
    'linkedin.job.data_analyst': {
        [LANGUAGES.ZH]: '数据分析师',
        [LANGUAGES.EN]: 'data analyst'
    },
    'linkedin.job.project_manager': {
        [LANGUAGES.ZH]: '项目经理',
        [LANGUAGES.EN]: 'project manager'
    },
    'linkedin.job.marketing_specialist': {
        [LANGUAGES.ZH]: '市场专员',
        [LANGUAGES.EN]: 'marketing specialist'
    },
    'linkedin.job.sales_representative': {
        [LANGUAGES.ZH]: '销售代表',
        [LANGUAGES.EN]: 'sales representative'
    },
    'linkedin.job.human_resources_manager': {
        [LANGUAGES.ZH]: '人力资源经理',
        [LANGUAGES.EN]: 'human resources manager'
    },
    'linkedin.job.accountant': {
        [LANGUAGES.ZH]: '会计',
        [LANGUAGES.EN]: 'accountant'
    },
    'linkedin.job.financial_analyst': {
        [LANGUAGES.ZH]: '金融分析师',
        [LANGUAGES.EN]: 'financial analyst'
    },
    'linkedin.job.operations_manager': {
        [LANGUAGES.ZH]: '运营经理',
        [LANGUAGES.EN]: 'operations manager'
    },
    'linkedin.job.customer_service_representative': {
        [LANGUAGES.ZH]: '客服代表',
        [LANGUAGES.EN]: 'customer service representative'
    },
    'linkedin.job.product_manager': {
        [LANGUAGES.ZH]: '产品经理',
        [LANGUAGES.EN]: 'product manager'
    },
    'linkedin.job.graphic_designer': {
        [LANGUAGES.ZH]: '平面设计师',
        [LANGUAGES.EN]: 'graphic designer'
    },
    'linkedin.job.content_writer': {
        [LANGUAGES.ZH]: '内容撰写员',
        [LANGUAGES.EN]: 'content writer'
    },
    'linkedin.job.research_scientist': {
        [LANGUAGES.ZH]: '研究科学家',
        [LANGUAGES.EN]: 'research scientist'
    },
    'linkedin.job.network_administrator': {
        [LANGUAGES.ZH]: '网络管理员',
        [LANGUAGES.EN]: 'network administrator'
    },
    'linkedin.job.business_analyst': {
        [LANGUAGES.ZH]: '业务分析师',
        [LANGUAGES.EN]: 'business analyst'
    },
    'linkedin.job.senior_software_engineer': {
        [LANGUAGES.ZH]: '高级软件工程师',
        [LANGUAGES.EN]: 'senior software engineer'
    },
    'linkedin.job.junior_developer': {
        [LANGUAGES.ZH]: '初级开发工程师',
        [LANGUAGES.EN]: 'junior developer'
    },
    'linkedin.job.full_stack_developer': {
        [LANGUAGES.ZH]: '全栈开发工程师',
        [LANGUAGES.EN]: 'full stack developer'
    },
    'linkedin.job.frontend_developer': {
        [LANGUAGES.ZH]: '前端开发工程师',
        [LANGUAGES.EN]: 'frontend developer'
    },
    'linkedin.job.backend_developer': {
        [LANGUAGES.ZH]: '后端开发工程师',
        [LANGUAGES.EN]: 'backend developer'
    },
    'linkedin.job.devops_engineer': {
        [LANGUAGES.ZH]: 'DevOps工程师',
        [LANGUAGES.EN]: 'devops engineer'
    },
    'linkedin.job.quality_assurance_engineer': {
        [LANGUAGES.ZH]: '质量保证工程师',
        [LANGUAGES.EN]: 'quality assurance engineer'
    },
    'linkedin.job.data_scientist': {
        [LANGUAGES.ZH]: '数据科学家',
        [LANGUAGES.EN]: 'data scientist'
    },
    'linkedin.job.machine_learning_engineer': {
        [LANGUAGES.ZH]: '机器学习工程师',
        [LANGUAGES.EN]: 'machine learning engineer'
    },
    'linkedin.job.systems_administrator': {
        [LANGUAGES.ZH]: '系统管理员',
        [LANGUAGES.EN]: 'systems administrator'
    },
    'linkedin.job.cloud_architect': {
        [LANGUAGES.ZH]: '云架构师',
        [LANGUAGES.EN]: 'cloud architect'
    },
    'linkedin.job.database_administrator': {
        [LANGUAGES.ZH]: '数据库管理员',
        [LANGUAGES.EN]: 'database administrator'
    },
    'linkedin.job.it_support_specialist': {
        [LANGUAGES.ZH]: 'IT支持专员',
        [LANGUAGES.EN]: 'IT support specialist'
    },
    'linkedin.job.security_analyst': {
        [LANGUAGES.ZH]: '安全分析师',
        [LANGUAGES.EN]: 'security analyst'
    },
    'linkedin.job.ux_designer': {
        [LANGUAGES.ZH]: '用户体验设计师',
        [LANGUAGES.EN]: 'UX designer'
    },
    'linkedin.job.ui_designer': {
        [LANGUAGES.ZH]: '用户界面设计师',
        [LANGUAGES.EN]: 'UI designer'
    },
    'linkedin.job.product_owner': {
        [LANGUAGES.ZH]: '产品负责人',
        [LANGUAGES.EN]: 'product owner'
    },
    'linkedin.job.scrum_master': {
        [LANGUAGES.ZH]: '敏捷教练',
        [LANGUAGES.EN]: 'scrum master'
    },
    'linkedin.job.agile_coach': {
        [LANGUAGES.ZH]: '敏捷顾问',
        [LANGUAGES.EN]: 'agile coach'
    },
    'linkedin.job.sales_manager': {
        [LANGUAGES.ZH]: '销售经理',
        [LANGUAGES.EN]: 'sales manager'
    },
    'linkedin.job.account_executive': {
        [LANGUAGES.ZH]: '客户经理',
        [LANGUAGES.EN]: 'account executive'
    },
    'linkedin.job.marketing_manager': {
        [LANGUAGES.ZH]: '市场经理',
        [LANGUAGES.EN]: 'marketing manager'
    },
    'linkedin.job.digital_marketing_specialist': {
        [LANGUAGES.ZH]: '数字营销专员',
        [LANGUAGES.EN]: 'digital marketing specialist'
    },
    'linkedin.job.content_marketing_manager': {
        [LANGUAGES.ZH]: '内容营销经理',
        [LANGUAGES.EN]: 'content marketing manager'
    },
    'linkedin.job.seo_specialist': {
        [LANGUAGES.ZH]: 'SEO专员',
        [LANGUAGES.EN]: 'SEO specialist'
    },
    'linkedin.job.social_media_manager': {
        [LANGUAGES.ZH]: '社交媒体经理',
        [LANGUAGES.EN]: 'social media manager'
    },
    'linkedin.job.pr_specialist': {
        [LANGUAGES.ZH]: '公关专员',
        [LANGUAGES.EN]: 'PR specialist'
    },
    'linkedin.job.brand_manager': {
        [LANGUAGES.ZH]: '品牌经理',
        [LANGUAGES.EN]: 'brand manager'
    },
    'linkedin.job.customer_success_manager': {
        [LANGUAGES.ZH]: '客户成功经理',
        [LANGUAGES.EN]: 'customer success manager'
    },
    'linkedin.job.technical_support_engineer': {
        [LANGUAGES.ZH]: '技术支持工程师',
        [LANGUAGES.EN]: 'technical support engineer'
    },
    'linkedin.job.technical_writer': {
        [LANGUAGES.ZH]: '技术文档工程师',
        [LANGUAGES.EN]: 'technical writer'
    },
    'linkedin.job.chief_executive_officer': {
        [LANGUAGES.ZH]: '首席执行官',
        [LANGUAGES.EN]: 'chief executive officer'
    },
    'linkedin.job.chief_financial_officer': {
        [LANGUAGES.ZH]: '首席财务官',
        [LANGUAGES.EN]: 'chief financial officer'
    },
    'linkedin.job.chief_technology_officer': {
        [LANGUAGES.ZH]: '首席技术官',
        [LANGUAGES.EN]: 'chief technology officer'
    },
    'linkedin.job.chief_operating_officer': {
        [LANGUAGES.ZH]: '首席运营官',
        [LANGUAGES.EN]: 'chief operating officer'
    },
    'linkedin.job.vice_president_of_engineering': {
        [LANGUAGES.ZH]: '工程副总裁',
        [LANGUAGES.EN]: 'vice president of engineering'
    },
    'linkedin.job.vice_president_of_sales': {
        [LANGUAGES.ZH]: '销售副总裁',
        [LANGUAGES.EN]: 'vice president of sales'
    },
    'linkedin.job.vice_president_of_marketing': {
        [LANGUAGES.ZH]: '市场副总裁',
        [LANGUAGES.EN]: 'vice president of marketing'
    },
    'linkedin.job.director_of_product': {
        [LANGUAGES.ZH]: '产品总监',
        [LANGUAGES.EN]: 'director of product'
    },
    'linkedin.job.director_of_engineering': {
        [LANGUAGES.ZH]: '工程总监',
        [LANGUAGES.EN]: 'director of engineering'
    },
    'linkedin.job.director_of_marketing': {
        [LANGUAGES.ZH]: '市场总监',
        [LANGUAGES.EN]: 'director of marketing'
    },

    // LinkedIn行业选项 - 完整翻译
    'linkedin.industry.aviation_and_aerospace': {
        [LANGUAGES.ZH]: '航空航天',
        [LANGUAGES.EN]: 'aviation & aerospace'
    },
    'linkedin.industry.pharmaceuticals': {
        [LANGUAGES.ZH]: '制药',
        [LANGUAGES.EN]: 'pharmaceuticals'
    },
    'linkedin.industry.education_management': {
        [LANGUAGES.ZH]: '教育管理',
        [LANGUAGES.EN]: 'education management'
    },
    'linkedin.industry.hospital_and_health_care': {
        [LANGUAGES.ZH]: '医院与医疗保健',
        [LANGUAGES.EN]: 'hospital & health care'
    },
    'linkedin.industry.information_technology': {
        [LANGUAGES.ZH]: '信息技术',
        [LANGUAGES.EN]: 'information technology'
    },
    'linkedin.industry.financial_services': {
        [LANGUAGES.ZH]: '金融服务',
        [LANGUAGES.EN]: 'financial services'
    },
    'linkedin.industry.manufacturing': {
        [LANGUAGES.ZH]: '制造业',
        [LANGUAGES.EN]: 'manufacturing'
    },
    'linkedin.industry.retail': {
        [LANGUAGES.ZH]: '零售业',
        [LANGUAGES.EN]: 'retail'
    },
    'linkedin.industry.telecommunications': {
        [LANGUAGES.ZH]: '电信',
        [LANGUAGES.EN]: 'telecommunications'
    },
    'linkedin.industry.healthcare': {
        [LANGUAGES.ZH]: '医疗保健',
        [LANGUAGES.EN]: 'healthcare'
    },
    'linkedin.industry.real_estate': {
        [LANGUAGES.ZH]: '房地产',
        [LANGUAGES.EN]: 'real estate'
    },
    'linkedin.industry.insurance': {
        [LANGUAGES.ZH]: '保险',
        [LANGUAGES.EN]: 'insurance'
    },
    'linkedin.industry.automotive': {
        [LANGUAGES.ZH]: '汽车',
        [LANGUAGES.EN]: 'automotive'
    },
    'linkedin.industry.media_and_entertainment': {
        [LANGUAGES.ZH]: '媒体与娱乐',
        [LANGUAGES.EN]: 'media & entertainment'
    },
    'linkedin.industry.energy': {
        [LANGUAGES.ZH]: '能源',
        [LANGUAGES.EN]: 'energy'
    },
    'linkedin.industry.professional_services': {
        [LANGUAGES.ZH]: '专业服务',
        [LANGUAGES.EN]: 'professional services'
    },
    'linkedin.industry.construction': {
        [LANGUAGES.ZH]: '建筑',
        [LANGUAGES.EN]: 'construction'
    },
    'linkedin.industry.transportation': {
        [LANGUAGES.ZH]: '交通运输',
        [LANGUAGES.EN]: 'transportation'
    },
    'linkedin.industry.agriculture': {
        [LANGUAGES.ZH]: '农业',
        [LANGUAGES.EN]: 'agriculture'
    },
    'linkedin.industry.government': {
        [LANGUAGES.ZH]: '政府',
        [LANGUAGES.EN]: 'government'
    },
    'linkedin.industry.biotechnology': {
        [LANGUAGES.ZH]: '生物技术',
        [LANGUAGES.EN]: 'biotechnology'
    },
    'linkedin.industry.chemicals': {
        [LANGUAGES.ZH]: '化工',
        [LANGUAGES.EN]: 'chemicals'
    },
    'linkedin.industry.computer_software': {
        [LANGUAGES.ZH]: '计算机软件',
        [LANGUAGES.EN]: 'computer software'
    },
    'linkedin.industry.computer_hardware': {
        [LANGUAGES.ZH]: '计算机硬件',
        [LANGUAGES.EN]: 'computer hardware'
    },
    'linkedin.industry.computer_networking': {
        [LANGUAGES.ZH]: '计算机网络',
        [LANGUAGES.EN]: 'computer networking'
    },
    'linkedin.industry.consumer_electronics': {
        [LANGUAGES.ZH]: '消费电子',
        [LANGUAGES.EN]: 'consumer electronics'
    },
    'linkedin.industry.consumer_goods': {
        [LANGUAGES.ZH]: '消费品',
        [LANGUAGES.EN]: 'consumer goods'
    },
    'linkedin.industry.defense_and_space': {
        [LANGUAGES.ZH]: '国防与航天',
        [LANGUAGES.EN]: 'defense & space'
    },
    'linkedin.industry.e-learning': {
        [LANGUAGES.ZH]: '在线教育',
        [LANGUAGES.EN]: 'e-learning'
    },
    'linkedin.industry.e-commerce': {
        [LANGUAGES.ZH]: '电子商务',
        [LANGUAGES.EN]: 'e-commerce'
    },
    'linkedin.industry.electrical_and_electronic_manufacturing': {
        [LANGUAGES.ZH]: '电气电子制造',
        [LANGUAGES.EN]: 'electrical & electronic manufacturing'
    },
    'linkedin.industry.environmental_services': {
        [LANGUAGES.ZH]: '环境服务',
        [LANGUAGES.EN]: 'environmental services'
    },
    'linkedin.industry.facilities_services': {
        [LANGUAGES.ZH]: '设施服务',
        [LANGUAGES.EN]: 'facilities services'
    },
    'linkedin.industry.food_and_beverages': {
        [LANGUAGES.ZH]: '食品饮料',
        [LANGUAGES.EN]: 'food & beverages'
    },
    'linkedin.industry.food_production': {
        [LANGUAGES.ZH]: '食品生产',
        [LANGUAGES.EN]: 'food production'
    },
    'linkedin.industry.furniture': {
        [LANGUAGES.ZH]: '家具',
        [LANGUAGES.EN]: 'furniture'
    },
    'linkedin.industry.gambling_and_casinos': {
        [LANGUAGES.ZH]: '博彩娱乐',
        [LANGUAGES.EN]: 'gambling & casinos'
    },
    'linkedin.industry.glass,_ceramics_and_concrete': {
        [LANGUAGES.ZH]: '玻璃陶瓷混凝土',
        [LANGUAGES.EN]: 'glass, ceramics & concrete'
    },
    'linkedin.industry.government_administration': {
        [LANGUAGES.ZH]: '政府管理',
        [LANGUAGES.EN]: 'government administration'
    },
    'linkedin.industry.government_relations': {
        [LANGUAGES.ZH]: '政府关系',
        [LANGUAGES.EN]: 'government relations'
    },
    'linkedin.industry.graphic_design': {
        [LANGUAGES.ZH]: '平面设计',
        [LANGUAGES.EN]: 'graphic design'
    },
    'linkedin.industry.higher_education': {
        [LANGUAGES.ZH]: '高等教育',
        [LANGUAGES.EN]: 'higher education'
    },
    'linkedin.industry.hospitality': {
        [LANGUAGES.ZH]: '酒店业',
        [LANGUAGES.EN]: 'hospitality'
    },
    'linkedin.industry.human_resources': {
        [LANGUAGES.ZH]: '人力资源',
        [LANGUAGES.EN]: 'human resources'
    },
    'linkedin.industry.import_and_export': {
        [LANGUAGES.ZH]: '进出口贸易',
        [LANGUAGES.EN]: 'import & export'
    },
    'linkedin.industry.industrial_automation': {
        [LANGUAGES.ZH]: '工业自动化',
        [LANGUAGES.EN]: 'industrial automation'
    },
    'linkedin.industry.internet': {
        [LANGUAGES.ZH]: '互联网',
        [LANGUAGES.EN]: 'internet'
    },
    'linkedin.industry.investment_banking': {
        [LANGUAGES.ZH]: '投资银行',
        [LANGUAGES.EN]: 'investment banking'
    },
    'linkedin.industry.investment_management': {
        [LANGUAGES.ZH]: '投资管理',
        [LANGUAGES.EN]: 'investment management'
    },
    'linkedin.industry.legal_services': {
        [LANGUAGES.ZH]: '法律服务',
        [LANGUAGES.EN]: 'legal services'
    },
    'linkedin.industry.leisure,_travel_and_tourism': {
        [LANGUAGES.ZH]: '休闲旅游',
        [LANGUAGES.EN]: 'leisure, travel & tourism'
    },
    'linkedin.industry.logistics_and_supply_chain': {
        [LANGUAGES.ZH]: '物流供应链',
        [LANGUAGES.EN]: 'logistics & supply chain'
    },
    'linkedin.industry.luxury_goods_and_jewelry': {
        [LANGUAGES.ZH]: '奢侈品珠宝',
        [LANGUAGES.EN]: 'luxury goods & jewelry'
    },
    'linkedin.industry.machinery': {
        [LANGUAGES.ZH]: '机械',
        [LANGUAGES.EN]: 'machinery'
    },
    'linkedin.industry.management_consulting': {
        [LANGUAGES.ZH]: '管理咨询',
        [LANGUAGES.EN]: 'management consulting'
    },
    'linkedin.industry.maritime': {
        [LANGUAGES.ZH]: '海事',
        [LANGUAGES.EN]: 'maritime'
    },
    'linkedin.industry.marketing_and_advertising': {
        [LANGUAGES.ZH]: '市场营销广告',
        [LANGUAGES.EN]: 'marketing & advertising'
    },
    'linkedin.industry.market_research': {
        [LANGUAGES.ZH]: '市场研究',
        [LANGUAGES.EN]: 'market research'
    },
    'linkedin.industry.mechanical_or_industrial_engineering': {
        [LANGUAGES.ZH]: '机械工业工程',
        [LANGUAGES.EN]: 'mechanical or industrial engineering'
    },
    'linkedin.industry.medical_devices': {
        [LANGUAGES.ZH]: '医疗器械',
        [LANGUAGES.EN]: 'medical devices'
    },
    'linkedin.industry.medical_practice': {
        [LANGUAGES.ZH]: '医疗实践',
        [LANGUAGES.EN]: 'medical practice'
    },
    'linkedin.industry.mental_health_care': {
        [LANGUAGES.ZH]: '心理健康护理',
        [LANGUAGES.EN]: 'mental health care'
    },
    'linkedin.industry.military': {
        [LANGUAGES.ZH]: '军事',
        [LANGUAGES.EN]: 'military'
    },
    'linkedin.industry.mining_and_metals': {
        [LANGUAGES.ZH]: '采矿冶金',
        [LANGUAGES.EN]: 'mining & metals'
    },
    'linkedin.industry.motion_pictures_and_film': {
        [LANGUAGES.ZH]: '电影制作',
        [LANGUAGES.EN]: 'motion pictures & film'
    },
    'linkedin.industry.museums_and_institutions': {
        [LANGUAGES.ZH]: '博物馆机构',
        [LANGUAGES.EN]: 'museums & institutions'
    },
    'linkedin.industry.music': {
        [LANGUAGES.ZH]: '音乐',
        [LANGUAGES.EN]: 'music'
    },
    'linkedin.industry.nanotechnology': {
        [LANGUAGES.ZH]: '纳米技术',
        [LANGUAGES.EN]: 'nanotechnology'
    },
    'linkedin.industry.non-profit_organization_management': {
        [LANGUAGES.ZH]: '非营利组织管理',
        [LANGUAGES.EN]: 'non-profit organization management'
    },
    'linkedin.industry.oil_and_energy': {
        [LANGUAGES.ZH]: '石油能源',
        [LANGUAGES.EN]: 'oil & energy'
    },
    'linkedin.industry.online_media': {
        [LANGUAGES.ZH]: '在线媒体',
        [LANGUAGES.EN]: 'online media'
    },
    'linkedin.industry.outsourcing/offshoring': {
        [LANGUAGES.ZH]: '外包离岸',
        [LANGUAGES.EN]: 'outsourcing/offshoring'
    },
    'linkedin.industry.package/freight_delivery': {
        [LANGUAGES.ZH]: '包裹货运',
        [LANGUAGES.EN]: 'package/freight delivery'
    },
    'linkedin.industry.packaging_and_containers': {
        [LANGUAGES.ZH]: '包装容器',
        [LANGUAGES.EN]: 'packaging & containers'
    },
    'linkedin.industry.paper_and_forest_products': {
        [LANGUAGES.ZH]: '造纸林业',
        [LANGUAGES.EN]: 'paper & forest products'
    },
    'linkedin.industry.performing_arts': {
        [LANGUAGES.ZH]: '表演艺术',
        [LANGUAGES.EN]: 'performing arts'
    },
    'linkedin.industry.plastics': {
        [LANGUAGES.ZH]: '塑料',
        [LANGUAGES.EN]: 'plastics'
    },
    'linkedin.industry.primary/secondary_education': {
        [LANGUAGES.ZH]: '中小学教育',
        [LANGUAGES.EN]: 'primary/secondary education'
    },
    'linkedin.industry.printing': {
        [LANGUAGES.ZH]: '印刷',
        [LANGUAGES.EN]: 'printing'
    },
    'linkedin.industry.public_policy': {
        [LANGUAGES.ZH]: '公共政策',
        [LANGUAGES.EN]: 'public policy'
    },
    'linkedin.industry.public_relations_and_communications': {
        [LANGUAGES.ZH]: '公关传播',
        [LANGUAGES.EN]: 'public relations & communications'
    },
    'linkedin.industry.public_safety': {
        [LANGUAGES.ZH]: '公共安全',
        [LANGUAGES.EN]: 'public safety'
    },
    'linkedin.industry.publishing': {
        [LANGUAGES.ZH]: '出版',
        [LANGUAGES.EN]: 'publishing'
    },
    'linkedin.industry.railroad_manufacture': {
        [LANGUAGES.ZH]: '铁路制造',
        [LANGUAGES.EN]: 'railroad manufacture'
    },
    'linkedin.industry.ranching': {
        [LANGUAGES.ZH]: '牧场',
        [LANGUAGES.EN]: 'ranching'
    },
    'linkedin.industry.research': {
        [LANGUAGES.ZH]: '研究',
        [LANGUAGES.EN]: 'research'
    },
    'linkedin.industry.restaurants': {
        [LANGUAGES.ZH]: '餐饮',
        [LANGUAGES.EN]: 'restaurants'
    },
    'linkedin.industry.security_and_investigations': {
        [LANGUAGES.ZH]: '安全调查',
        [LANGUAGES.EN]: 'security & investigations'
    },
    'linkedin.industry.semiconductors': {
        [LANGUAGES.ZH]: '半导体',
        [LANGUAGES.EN]: 'semiconductors'
    },
    'linkedin.industry.shipbuilding': {
        [LANGUAGES.ZH]: '造船',
        [LANGUAGES.EN]: 'shipbuilding'
    },
    'linkedin.industry.sporting_goods': {
        [LANGUAGES.ZH]: '体育用品',
        [LANGUAGES.EN]: 'sporting goods'
    },
    'linkedin.industry.sports': {
        [LANGUAGES.ZH]: '体育',
        [LANGUAGES.EN]: 'sports'
    },
    'linkedin.industry.staffing_and_recruiting': {
        [LANGUAGES.ZH]: '人员招聘',
        [LANGUAGES.EN]: 'staffing & recruiting'
    },
    'linkedin.industry.supermarkets': {
        [LANGUAGES.ZH]: '超市',
        [LANGUAGES.EN]: 'supermarkets'
    },
    'linkedin.industry.textiles': {
        [LANGUAGES.ZH]: '纺织',
        [LANGUAGES.EN]: 'textiles'
    },
    'linkedin.industry.think_tanks': {
        [LANGUAGES.ZH]: '智库',
        [LANGUAGES.EN]: 'think tanks'
    },
    'linkedin.industry.tobacco': {
        [LANGUAGES.ZH]: '烟草',
        [LANGUAGES.EN]: 'tobacco'
    },
    'linkedin.industry.translation_and_localization': {
        [LANGUAGES.ZH]: '翻译本地化',
        [LANGUAGES.EN]: 'translation & localization'
    },
    'linkedin.industry.utilities': {
        [LANGUAGES.ZH]: '公用事业',
        [LANGUAGES.EN]: 'utilities'
    },
    'linkedin.industry.venture_capital_and_private_equity': {
        [LANGUAGES.ZH]: '风险投资私募',
        [LANGUAGES.EN]: 'venture capital & private equity'
    },
    'linkedin.industry.veterinary': {
        [LANGUAGES.ZH]: '兽医',
        [LANGUAGES.EN]: 'veterinary'
    },
    'linkedin.industry.warehousing': {
        [LANGUAGES.ZH]: '仓储',
        [LANGUAGES.EN]: 'warehousing'
    },
    'linkedin.industry.wholesale': {
        [LANGUAGES.ZH]: '批发',
        [LANGUAGES.EN]: 'wholesale'
    },
    'linkedin.industry.wine_and_spirits': {
        [LANGUAGES.ZH]: '酒类',
        [LANGUAGES.EN]: 'wine & spirits'
    },
    'linkedin.industry.wireless': {
        [LANGUAGES.ZH]: '无线通信',
        [LANGUAGES.EN]: 'wireless'
    },
    'linkedin.industry.writing_and_editing': {
        [LANGUAGES.ZH]: '写作编辑',
        [LANGUAGES.EN]: 'writing & editing'
    },

    // LinkedIn地点选项 - 完整翻译
    'linkedin.location.united_states': {
        [LANGUAGES.ZH]: '美国',
        [LANGUAGES.EN]: 'united states'
    },
    'linkedin.location.china': {
        [LANGUAGES.ZH]: '中国',
        [LANGUAGES.EN]: 'china'
    },
    'linkedin.location.india': {
        [LANGUAGES.ZH]: '印度',
        [LANGUAGES.EN]: 'india'
    },
    'linkedin.location.united_kingdom': {
        [LANGUAGES.ZH]: '英国',
        [LANGUAGES.EN]: 'united kingdom'
    },
    'linkedin.location.canada': {
        [LANGUAGES.ZH]: '加拿大',
        [LANGUAGES.EN]: 'canada'
    },
    'linkedin.location.germany': {
        [LANGUAGES.ZH]: '德国',
        [LANGUAGES.EN]: 'germany'
    },
    'linkedin.location.france': {
        [LANGUAGES.ZH]: '法国',
        [LANGUAGES.EN]: 'france'
    },
    'linkedin.location.japan': {
        [LANGUAGES.ZH]: '日本',
        [LANGUAGES.EN]: 'japan'
    },
    'linkedin.location.australia': {
        [LANGUAGES.ZH]: '澳大利亚',
        [LANGUAGES.EN]: 'australia'
    },
    'linkedin.location.brazil': {
        [LANGUAGES.ZH]: '巴西',
        [LANGUAGES.EN]: 'brazil'
    },
    'linkedin.location.singapore': {
        [LANGUAGES.ZH]: '新加坡',
        [LANGUAGES.EN]: 'singapore'
    },
    'linkedin.location.spain': {
        [LANGUAGES.ZH]: '西班牙',
        [LANGUAGES.EN]: 'spain'
    },
    'linkedin.location.italy': {
        [LANGUAGES.ZH]: '意大利',
        [LANGUAGES.EN]: 'italy'
    },
    'linkedin.location.netherlands': {
        [LANGUAGES.ZH]: '荷兰',
        [LANGUAGES.EN]: 'netherlands'
    },
    'linkedin.location.sweden': {
        [LANGUAGES.ZH]: '瑞典',
        [LANGUAGES.EN]: 'sweden'
    },
    'linkedin.location.russia': {
        [LANGUAGES.ZH]: '俄罗斯',
        [LANGUAGES.EN]: 'russia'
    },
    'linkedin.location.mexico': {
        [LANGUAGES.ZH]: '墨西哥',
        [LANGUAGES.EN]: 'mexico'
    },
    'linkedin.location.south_korea': {
        [LANGUAGES.ZH]: '韩国',
        [LANGUAGES.EN]: 'south korea'
    },
    'linkedin.location.ireland': {
        [LANGUAGES.ZH]: '爱尔兰',
        [LANGUAGES.EN]: 'ireland'
    },
    'linkedin.location.switzerland': {
        [LANGUAGES.ZH]: '瑞士',
        [LANGUAGES.EN]: 'switzerland'
    },
    'linkedin.location.argentina': {
        [LANGUAGES.ZH]: '阿根廷',
        [LANGUAGES.EN]: 'argentina'
    },
    'linkedin.location.austria': {
        [LANGUAGES.ZH]: '奥地利',
        [LANGUAGES.EN]: 'austria'
    },
    'linkedin.location.belgium': {
        [LANGUAGES.ZH]: '比利时',
        [LANGUAGES.EN]: 'belgium'
    },
    'linkedin.location.chile': {
        [LANGUAGES.ZH]: '智利',
        [LANGUAGES.EN]: 'chile'
    },
    'linkedin.location.colombia': {
        [LANGUAGES.ZH]: '哥伦比亚',
        [LANGUAGES.EN]: 'colombia'
    },
    'linkedin.location.czech_republic': {
        [LANGUAGES.ZH]: '捷克',
        [LANGUAGES.EN]: 'czech republic'
    },
    'linkedin.location.denmark': {
        [LANGUAGES.ZH]: '丹麦',
        [LANGUAGES.EN]: 'denmark'
    },
    'linkedin.location.egypt': {
        [LANGUAGES.ZH]: '埃及',
        [LANGUAGES.EN]: 'egypt'
    },
    'linkedin.location.finland': {
        [LANGUAGES.ZH]: '芬兰',
        [LANGUAGES.EN]: 'finland'
    },
    'linkedin.location.greece': {
        [LANGUAGES.ZH]: '希腊',
        [LANGUAGES.EN]: 'greece'
    },
    'linkedin.location.hong_kong': {
        [LANGUAGES.ZH]: '香港',
        [LANGUAGES.EN]: 'hong kong'
    },
    'linkedin.location.hungary': {
        [LANGUAGES.ZH]: '匈牙利',
        [LANGUAGES.EN]: 'hungary'
    },
    'linkedin.location.indonesia': {
        [LANGUAGES.ZH]: '印度尼西亚',
        [LANGUAGES.EN]: 'indonesia'
    },
    'linkedin.location.israel': {
        [LANGUAGES.ZH]: '以色列',
        [LANGUAGES.EN]: 'israel'
    },
    'linkedin.location.malaysia': {
        [LANGUAGES.ZH]: '马来西亚',
        [LANGUAGES.EN]: 'malaysia'
    },
    'linkedin.location.new_zealand': {
        [LANGUAGES.ZH]: '新西兰',
        [LANGUAGES.EN]: 'new zealand'
    },
    'linkedin.location.norway': {
        [LANGUAGES.ZH]: '挪威',
        [LANGUAGES.EN]: 'norway'
    },
    'linkedin.company.tech_innovations': {
        [LANGUAGES.ZH]: '科技创新公司',
        [LANGUAGES.EN]: 'Tech Innovations'
    },
    'linkedin.company.global_healthcare': {
        [LANGUAGES.ZH]: '全球医疗保健',
        [LANGUAGES.EN]: 'Global Healthcare'
    },
    'linkedin.company.shanghai_tech': {
        [LANGUAGES.ZH]: '上海科技',
        [LANGUAGES.EN]: 'Shanghai Tech'
    },
    'linkedin.company.huddleston_technical_services': {
        [LANGUAGES.ZH]: '哈德尔斯顿技术服务',
        [LANGUAGES.EN]: 'huddleston technical services'
    },
    'linkedin.company.lifesouth_community_blood_centers': {
        [LANGUAGES.ZH]: '生命南方社区血液中心',
        [LANGUAGES.EN]: 'lifesouth community blood centers'
    },
    'linkedin.company.jefferson_county_board_of_education': {
        [LANGUAGES.ZH]: '杰斐逊县教育委员会',
        [LANGUAGES.EN]: 'jefferson county board of education'
    },
    'linkedin.company.microsoft': {
        [LANGUAGES.ZH]: '微软',
        [LANGUAGES.EN]: 'Microsoft'
    },
    'linkedin.company.apple': {
        [LANGUAGES.ZH]: '苹果',
        [LANGUAGES.EN]: 'Apple'
    },
    'linkedin.company.google': {
        [LANGUAGES.ZH]: '谷歌',
        [LANGUAGES.EN]: 'Google'
    },
    'linkedin.company.amazon': {
        [LANGUAGES.ZH]: '亚马逊',
        [LANGUAGES.EN]: 'Amazon'
    },
    'linkedin.company.facebook': {
        [LANGUAGES.ZH]: '脸书',
        [LANGUAGES.EN]: 'Facebook'
    },
    'linkedin.company.alibaba': {
        [LANGUAGES.ZH]: '阿里巴巴',
        [LANGUAGES.EN]: 'Alibaba'
    },
    'linkedin.company.tencent': {
        [LANGUAGES.ZH]: '腾讯',
        [LANGUAGES.EN]: 'Tencent'
    },
    'linkedin.company.baidu': {
        [LANGUAGES.ZH]: '百度',
        [LANGUAGES.EN]: 'Baidu'
    },
    'linkedin.company.ibm': {
        [LANGUAGES.ZH]: 'IBM',
        [LANGUAGES.EN]: 'IBM'
    },
    'linkedin.company.intel': {
        [LANGUAGES.ZH]: '英特尔',
        [LANGUAGES.EN]: 'Intel'
    },
    'linkedin.company.oracle': {
        [LANGUAGES.ZH]: '甲骨文',
        [LANGUAGES.EN]: 'Oracle'
    },
    'linkedin.company.sap': {
        [LANGUAGES.ZH]: 'SAP',
        [LANGUAGES.EN]: 'SAP'
    },
    'linkedin.company.xiaomi': {
        [LANGUAGES.ZH]: '小米',
        [LANGUAGES.EN]: 'Xiaomi'
    },
    'linkedin.company.tsmc': {
        [LANGUAGES.ZH]: '台积电',
        [LANGUAGES.EN]: 'TSMC'
    },
    'linkedin.company.sony': {
        [LANGUAGES.ZH]: '索尼',
        [LANGUAGES.EN]: 'Sony'
    },
    'linkedin.company.samsung': {
        [LANGUAGES.ZH]: '三星',
        [LANGUAGES.EN]: 'Samsung'
    },
    'linkedin.company.huawei': {
        [LANGUAGES.ZH]: '华为',
        [LANGUAGES.EN]: 'Huawei'
    },
    'linkedin.company.siemens': {
        [LANGUAGES.ZH]: '西门子',
        [LANGUAGES.EN]: 'Siemens'
    },
    'linkedin.company.general_electric': {
        [LANGUAGES.ZH]: '通用电气',
        [LANGUAGES.EN]: 'General Electric'
    },
    'linkedin.company.johnson_and_johnson': {
        [LANGUAGES.ZH]: '强生',
        [LANGUAGES.EN]: 'Johnson and Johnson'
    },
    'linkedin.company.pfizer': {
        [LANGUAGES.ZH]: '辉瑞',
        [LANGUAGES.EN]: 'Pfizer'
    },
    'linkedin.company.novartis': {
        [LANGUAGES.ZH]: '诺华',
        [LANGUAGES.EN]: 'Novartis'
    },
    'linkedin.company.roche': {
        [LANGUAGES.ZH]: '罗氏',
        [LANGUAGES.EN]: 'Roche'
    },
    'linkedin.company.boeing': {
        [LANGUAGES.ZH]: '波音',
        [LANGUAGES.EN]: 'Boeing'
    },
    'linkedin.company.airbus': {
        [LANGUAGES.ZH]: '空客',
        [LANGUAGES.EN]: 'Airbus'
    },

    // LinkedIn薪资范围选项
    'linkedin.salary.under_15,000': {
        [LANGUAGES.ZH]: '15,000以下',
        [LANGUAGES.EN]: 'under 15,000'
    },
    'linkedin.salary.15,000-25,000': {
        [LANGUAGES.ZH]: '15,000-25,000',
        [LANGUAGES.EN]: '15,000-25,000'
    },
    'linkedin.salary.25,000-35,000': {
        [LANGUAGES.ZH]: '25,000-35,000',
        [LANGUAGES.EN]: '25,000-35,000'
    },
    'linkedin.salary.35,000-45,000': {
        [LANGUAGES.ZH]: '35,000-45,000',
        [LANGUAGES.EN]: '35,000-45,000'
    },
    'linkedin.salary.45,000-55,000': {
        [LANGUAGES.ZH]: '45,000-55,000',
        [LANGUAGES.EN]: '45,000-55,000'
    },
    'linkedin.salary.55,000-65,000': {
        [LANGUAGES.ZH]: '55,000-65,000',
        [LANGUAGES.EN]: '55,000-65,000'
    },
    'linkedin.salary.65,000-75,000': {
        [LANGUAGES.ZH]: '65,000-75,000',
        [LANGUAGES.EN]: '65,000-75,000'
    },
    'linkedin.salary.75,000-85,000': {
        [LANGUAGES.ZH]: '75,000-85,000',
        [LANGUAGES.EN]: '75,000-85,000'
    },
    'linkedin.salary.85,000-95,000': {
        [LANGUAGES.ZH]: '85,000-95,000',
        [LANGUAGES.EN]: '85,000-95,000'
    },
    'linkedin.salary.95,000-105,000': {
        [LANGUAGES.ZH]: '95,000-105,000',
        [LANGUAGES.EN]: '95,000-105,000'
    },
    'linkedin.salary.105,000-125,000': {
        [LANGUAGES.ZH]: '105,000-125,000',
        [LANGUAGES.EN]: '105,000-125,000'
    },
    'linkedin.salary.125,000-150,000': {
        [LANGUAGES.ZH]: '125,000-150,000',
        [LANGUAGES.EN]: '125,000-150,000'
    },
    'linkedin.salary.150,000-175,000': {
        [LANGUAGES.ZH]: '150,000-175,000',
        [LANGUAGES.EN]: '150,000-175,000'
    },
    'linkedin.salary.175,000-200,000': {
        [LANGUAGES.ZH]: '175,000-200,000',
        [LANGUAGES.EN]: '175,000-200,000'
    },
    'linkedin.salary.200,000-250,000': {
        [LANGUAGES.ZH]: '200,000-250,000',
        [LANGUAGES.EN]: '200,000-250,000'
    },
    'linkedin.salary.250,000-300,000': {
        [LANGUAGES.ZH]: '250,000-300,000',
        [LANGUAGES.EN]: '250,000-300,000'
    },
    'linkedin.salary.300,000-400,000': {
        [LANGUAGES.ZH]: '300,000-400,000',
        [LANGUAGES.EN]: '300,000-400,000'
    },
    'linkedin.salary.400,000-500,000': {
        [LANGUAGES.ZH]: '400,000-500,000',
        [LANGUAGES.EN]: '400,000-500,000'
    },
    'linkedin.salary.500,000+': {
        [LANGUAGES.ZH]: '500,000以上',
        [LANGUAGES.EN]: '500,000+'
    },
    'twitter.unknown.user': {
        [LANGUAGES.ZH]: '未知用户',
        [LANGUAGES.EN]: 'Unknown User'
    },
    // 分页相关翻译
    'pagination.total': {
        [LANGUAGES.ZH]: '共',
        [LANGUAGES.EN]: 'Total'
    },
    'pagination.item': {
        [LANGUAGES.ZH]: '条',
        [LANGUAGES.EN]: 'items'
    },
    'pagination.prev': {
        [LANGUAGES.ZH]: '上一页',
        [LANGUAGES.EN]: 'Previous'
    },
    'pagination.next': {
        [LANGUAGES.ZH]: '下一页',
        [LANGUAGES.EN]: 'Next'
    },
    'pagination.jumper': {
        [LANGUAGES.ZH]: '前往',
        [LANGUAGES.EN]: 'Go to'
    },
    'pagination.page': {
        [LANGUAGES.ZH]: '页',
        [LANGUAGES.EN]: 'Page'
    },
    'pagination.items.per.page': {
        [LANGUAGES.ZH]: '条/页',
        [LANGUAGES.EN]: 'items/page'
    },
    // Twitter错误信息
    'twitter.error.fetchFailed': {
        [LANGUAGES.ZH]: '获取数据失败，请稍后重试',
        [LANGUAGES.EN]: 'Failed to fetch data, please try again later'
    },
    'twitter.reset.filters.success': {
        [LANGUAGES.ZH]: '已重置筛选条件',
        [LANGUAGES.EN]: 'Filters have been reset'
    },
    'twitter.export.error.invalid.count': {
        [LANGUAGES.ZH]: '请输入有效的导出数量',
        [LANGUAGES.EN]: 'Please enter a valid export count'
    },
    'twitter.export.preparing': {
        [LANGUAGES.ZH]: '正在准备导出数据，请稍候...',
        [LANGUAGES.EN]: 'Preparing to export data, please wait...'
    },
    'twitter.export.success': {
        [LANGUAGES.ZH]: '导出成功',
        [LANGUAGES.EN]: 'Export successful'
    },
    'twitter.export.error.format': {
        [LANGUAGES.ZH]: '导出失败：响应格式不正确',
        [LANGUAGES.EN]: 'Export failed: Incorrect response format'
    },
    'twitter.export.error.failed': {
        [LANGUAGES.ZH]: '导出失败，请稍后重试',
        [LANGUAGES.EN]: 'Export failed, please try again later'
    },

    // 全球企业库页面翻译
    'enterprise.search.title': {
        [LANGUAGES.ZH]: '搜索',
        [LANGUAGES.EN]: 'Search'
    },
    'enterprise.search.placeholder': {
        [LANGUAGES.ZH]: '搜索企业名称',
        [LANGUAGES.EN]: 'Search company name'
    },
    'enterprise.search.lastName': {
        [LANGUAGES.ZH]: '搜索名',
        [LANGUAGES.EN]: 'Search for first name'
    },
    'enterprise.search.firstName': {
        [LANGUAGES.ZH]: '搜索姓',
        [LANGUAGES.EN]: 'Search for last name'
    },
    'enterprise.search.zipcode': {
        [LANGUAGES.ZH]: '搜索邮编',
        [LANGUAGES.EN]: 'Search for Zipcode'
    },
    'enterprise.search.state': {
        [LANGUAGES.ZH]: '搜索所在州/省',
        [LANGUAGES.EN]: 'Search for State'
    },
    'enterprise.export.data': {
        [LANGUAGES.ZH]: '导出数据',
        [LANGUAGES.EN]: 'Export Data'
    },
    'enterprise.reset.filters': {
        [LANGUAGES.ZH]: '重置筛选条件',
        [LANGUAGES.EN]: 'Reset Filters'
    },
    'enterprise.filter.country': {
        [LANGUAGES.ZH]: '国家',
        [LANGUAGES.EN]: 'Country'
    },
    'enterprise.filter.country.placeholder': {
        [LANGUAGES.ZH]: '搜索国家',
        [LANGUAGES.EN]: 'Search country'
    },
    'enterprise.filter.industry': {
        [LANGUAGES.ZH]: '行业',
        [LANGUAGES.EN]: 'Industry'
    },
    'enterprise.filter.city': {
        [LANGUAGES.ZH]: '城市',
        [LANGUAGES.EN]: 'City'
    },
    'enterprise.filter.city.placeholder': {
        [LANGUAGES.ZH]: '搜索城市',
        [LANGUAGES.EN]: 'Search City'
    },
    'enterprise.filter.industry.placeholder': {
        [LANGUAGES.ZH]: '搜索行业',
        [LANGUAGES.EN]: 'Search industry'
    },
    'enterprise.filter.employee': {
        [LANGUAGES.ZH]: '员工规模',
        [LANGUAGES.EN]: 'Employee Size'
    },
    'enterprise.filter.revenue': {
        [LANGUAGES.ZH]: '收入规模',
        [LANGUAGES.EN]: 'Revenue Size'
    },
    'enterprise.filter.contact': {
        [LANGUAGES.ZH]: '联系方式',
        [LANGUAGES.EN]: 'Contact'
    },
    'enterprise.filter.contact.phone': {
        [LANGUAGES.ZH]: '电话',
        [LANGUAGES.EN]: 'Phone'
    },
    'enterprise.filter.contact.mobile': {
        [LANGUAGES.ZH]: '手机号',
        [LANGUAGES.EN]: 'Mobile'
    },
    'enterprise.filter.contact.email': {
        [LANGUAGES.ZH]: '邮箱',
        [LANGUAGES.EN]: 'Email'
    },
    'enterprise.filter.contact.website': {
        [LANGUAGES.ZH]: '网站',
        [LANGUAGES.EN]: 'Website'
    },

    'enterprise.info.mobile': {
        [LANGUAGES.ZH]: '手机号',
        [LANGUAGES.EN]: 'Mobile'
    },

    'enterprise.filter.all': {
        [LANGUAGES.ZH]: '全选',
        [LANGUAGES.EN]: 'All'
    },
    'enterprise.no.data': {
        [LANGUAGES.ZH]: '没有找到符合条件的企业',
        [LANGUAGES.EN]: 'No matching companies found'
    },
    'enterprise.login.to.access': {
        [LANGUAGES.ZH]: '登录以访问数据',
        [LANGUAGES.EN]: 'Login to access data'
    },
    'enterprise.unknown': {
        [LANGUAGES.ZH]: '未知企业',
        [LANGUAGES.EN]: 'Unknown Company'
    },
    'enterprise.detail.expand': {
        [LANGUAGES.ZH]: '详情',
        [LANGUAGES.EN]: 'Details'
    },
    'enterprise.detail.collapse': {
        [LANGUAGES.ZH]: '收起',
        [LANGUAGES.EN]: 'Collapse'
    },
    'enterprise.info.country': {
        [LANGUAGES.ZH]: '国家:',
        [LANGUAGES.EN]: 'Country:'
    },
    'enterprise.info.city': {
        [LANGUAGES.ZH]: '城市:',
        [LANGUAGES.EN]: 'City:'
    },
    'enterprise.info.industry': {
        [LANGUAGES.ZH]: '行业:',
        [LANGUAGES.EN]: 'Industry:'
    },
    'enterprise.info.subindustry': {
        [LANGUAGES.ZH]: '子行业:',
        [LANGUAGES.EN]: 'Sub-Industry:'
    },
    'enterprise.info.employee': {
        [LANGUAGES.ZH]: '员工规模:',
        [LANGUAGES.EN]: 'Employee Size:'
    },
    'enterprise.info.revenue': {
        [LANGUAGES.ZH]: '收入规模:',
        [LANGUAGES.EN]: 'Revenue Size:'
    },
    'enterprise.detail.title': {
        [LANGUAGES.ZH]: '详细信息',
        [LANGUAGES.EN]: 'Detailed Information'
    },
    'enterprise.info.contact': {
        [LANGUAGES.ZH]: '联系人:',
        [LANGUAGES.EN]: 'Contact:'
    },
    'enterprise.info.phone': {
        [LANGUAGES.ZH]: '电话:',
        [LANGUAGES.EN]: 'Phone:'
    },
    'enterprise.info.position': {
        [LANGUAGES.ZH]: '职位',
        [LANGUAGES.EN]: 'Position:'
    },
    'enterprise.info.department': {
        [LANGUAGES.ZH]: '部门:',
        [LANGUAGES.EN]: 'Department:'
    },
    'enterprise.info.email': {
        [LANGUAGES.ZH]: '邮箱:',
        [LANGUAGES.EN]: 'Email:'
    },
    'enterprise.info.address': {
        [LANGUAGES.ZH]: '地址:',
        [LANGUAGES.EN]: 'Address:'
    },
    'enterprise.info.website': {
        [LANGUAGES.ZH]: '网站:',
        [LANGUAGES.EN]: 'Website:'
    },


    // 域名反查页面字段映射
    'domain.field.firstname': {
        [LANGUAGES.ZH]: '名',
        [LANGUAGES.EN]: 'First Name'
    },
    'domain.field.middlename': {
        [LANGUAGES.ZH]: '中间名',
        [LANGUAGES.EN]: 'Middle Name'
    },
    'domain.field.lastname': {
        [LANGUAGES.ZH]: '姓',
        [LANGUAGES.EN]: 'Last Name'
    },
    'domain.field.businessemail': {
        [LANGUAGES.ZH]: '公司邮箱',
        [LANGUAGES.EN]: 'Business Email'
    },
    'domain.field.personalemails': {
        [LANGUAGES.ZH]: '个人邮箱',
        [LANGUAGES.EN]: 'Personal Emails'
    },

    // 添加导入收件人对话框相关的翻译
    'email.import.recipients': {
        [LANGUAGES.ZH]: '导入收件人',
        [LANGUAGES.EN]: 'Import Recipients'
    },
    'email.import.description': {
        [LANGUAGES.ZH]: '请上传包含收件人邮箱地址的Excel文件。系统将自动提取文件中的有效邮箱地址。',
        [LANGUAGES.EN]: 'Please upload an Excel file containing recipient email addresses. The system will automatically extract valid email addresses from the file.'
    },
    'email.upload.drag': {
        [LANGUAGES.ZH]: '拖拽文件到此处或',
        [LANGUAGES.EN]: 'Drag files here or'
    },
    'email.upload.click': {
        [LANGUAGES.ZH]: '点击上传',
        [LANGUAGES.EN]: 'click to upload'
    },
    'email.upload.tip': {
        [LANGUAGES.ZH]: '仅支持 .xlsx, .xls 格式文件',
        [LANGUAGES.EN]: 'Only .xlsx, .xls format files are supported'
    },
    'email.preview': {
        [LANGUAGES.ZH]: '预览 (前5个):',
        [LANGUAGES.EN]: 'Preview (First 5):'
    },
    'email.preview.more': {
        [LANGUAGES.ZH]: '还有 {count} 个邮箱...',
        [LANGUAGES.EN]: '{count} more emails...'
    },
    'email.import.confirm': {
        [LANGUAGES.ZH]: '确认导入',
        [LANGUAGES.EN]: 'Confirm Import'
    },
    'domain.field.jobtitle': {
        [LANGUAGES.ZH]: '职位',
        [LANGUAGES.EN]: 'Job Title'
    },
    'domain.field.senioritylevel': {
        [LANGUAGES.ZH]: '资历',
        [LANGUAGES.EN]: 'Seniority Level'
    },
    'domain.field.department': {
        [LANGUAGES.ZH]: '部门',
        [LANGUAGES.EN]: 'Department'
    },
    'domain.field.mobilephone': {
        [LANGUAGES.ZH]: '手机',
        [LANGUAGES.EN]: 'Mobile Phone'
    },
    'domain.field.contactmetrocity': {
        [LANGUAGES.ZH]: '联系城市',
        [LANGUAGES.EN]: 'Contact Metro City'
    },
    'domain.field.contactstate': {
        [LANGUAGES.ZH]: '联系州',
        [LANGUAGES.EN]: 'Contact State'
    },
    'domain.field.companyname': {
        [LANGUAGES.ZH]: '公司名称',
        [LANGUAGES.EN]: 'Company Name'
    },
    'domain.field.companydomain': {
        [LANGUAGES.ZH]: '公司域名',
        [LANGUAGES.EN]: 'Company Domain'
    },
    'domain.field.companyphone': {
        [LANGUAGES.ZH]: '公司电话',
        [LANGUAGES.EN]: 'Company Phone'
    },
    'domain.field.primaryindustry': {
        [LANGUAGES.ZH]: '主要行业',
        [LANGUAGES.EN]: 'Primary Industry'
    },
    'domain.field.primarysic': {
        [LANGUAGES.ZH]: '主要SIC',
        [LANGUAGES.EN]: 'Primary SIC'
    },
    'domain.field.companyindustry': {
        [LANGUAGES.ZH]: '公司行业',
        [LANGUAGES.EN]: 'Company Industry'
    },
    'domain.field.companycity': {
        [LANGUAGES.ZH]: '公司城市',
        [LANGUAGES.EN]: 'Company City'
    },
    'domain.field.companystate': {
        [LANGUAGES.ZH]: '公司州',
        [LANGUAGES.EN]: 'Company State'
    },
    'domain.field.companyzip': {
        [LANGUAGES.ZH]: '公司邮编',
        [LANGUAGES.EN]: 'Company ZIP'
    },
    'domain.field.companyemployeecount': {
        [LANGUAGES.ZH]: '公司规模',
        [LANGUAGES.EN]: 'Company Employee Count'
    },
    'domain.field.companyrevenue': {
        [LANGUAGES.ZH]: '公司收入',
        [LANGUAGES.EN]: 'Company Revenue'
    },
    'domain.field.businessemailvalidationstatus': {
        [LANGUAGES.ZH]: '公司邮箱验证状态',
        [LANGUAGES.EN]: 'Business Email Validation Status'
    },
    'domain.field.importstatus': {
        [LANGUAGES.ZH]: '导入状态',
        [LANGUAGES.EN]: 'Import Status'
    },
    'domain.field.importtime': {
        [LANGUAGES.ZH]: '导入时间',
        [LANGUAGES.EN]: 'Import Time'
    },
    'domain.field.linkedin_url': {
        [LANGUAGES.ZH]: '领英网址',
        [LANGUAGES.EN]: 'LinkedIn URL'
    },
    'domain.field.company_linkedin_url': {
        [LANGUAGES.ZH]: '公司领英网址',
        [LANGUAGES.EN]: 'Company LinkedIn URL'
    },
    'domain.field.company_address': {
        [LANGUAGES.ZH]: '公司地址',
        [LANGUAGES.EN]: 'Company Address'
    },
    'domain.field.company_sic': {
        [LANGUAGES.ZH]: '公司SIC编码',
        [LANGUAGES.EN]: 'Company SIC Code'
    },

    'enterprise.info.state': {
        [LANGUAGES.ZH]: '州/省:',
        [LANGUAGES.EN]: 'State/Province:'
    },
    'enterprise.info.zipcode': {
        [LANGUAGES.ZH]: '邮编:',
        [LANGUAGES.EN]: 'Zip Code:'
    },
    'enterprise.export.dialog.title': {
        [LANGUAGES.ZH]: '导出数据',
        [LANGUAGES.EN]: 'Export Data'
    },
    'enterprise.export.matching.records': {
        [LANGUAGES.ZH]: '符合条件的记录数:',
        [LANGUAGES.EN]: 'Matching records:'
    },
    'enterprise.export.balance': {
        [LANGUAGES.ZH]: '当前导出余额:',
        [LANGUAGES.EN]: 'Current export balance:'
    },
    'enterprise.export.note': {
        [LANGUAGES.ZH]: '注意: 每导出一条记录将消耗一点导出额度',
        [LANGUAGES.EN]: 'Note: Each exported record consumes one export quota'
    },
    'enterprise.export.enter.count': {
        [LANGUAGES.ZH]: '请输入要导出的记录数:',
        [LANGUAGES.EN]: 'Please enter the number of records to export:'
    },
    'enterprise.export.error.exceed': {
        [LANGUAGES.ZH]: '导出数量不能超过符合条件的记录数',
        [LANGUAGES.EN]: 'Export count cannot exceed the number of matching records'
    },
    'enterprise.export.error.balance': {
        [LANGUAGES.ZH]: '导出余额不足',
        [LANGUAGES.EN]: 'Insufficient export balance'
    },
    'enterprise.export.error.limit': {
        [LANGUAGES.ZH]: '导出数量不能超过100万',
        [LANGUAGES.EN]: 'Export count cannot exceed 1 million'
    },
    'enterprise.cancel': {
        [LANGUAGES.ZH]: '取消',
        [LANGUAGES.EN]: 'Cancel'
    },
    'enterprise.export.confirm': {
        [LANGUAGES.ZH]: '确认导出',
        [LANGUAGES.EN]: 'Confirm Export'
    },
    'enterprise.detail.view.tip': {
        [LANGUAGES.ZH]: '点击查看详细信息（消耗1次查询次数）',
        [LANGUAGES.EN]: 'Click to view detailed information (consumes 1 query quota)'
    },
    'enterprise.detail.confirm.message': {
        [LANGUAGES.ZH]: '查看详细信息将消耗1次查询次数，是否继续？',
        [LANGUAGES.EN]: 'Viewing detailed information will consume 1 query quota. Continue?'
    },
    'enterprise.detail.confirm.title': {
        [LANGUAGES.ZH]: '确认查看',
        [LANGUAGES.EN]: 'Confirm View'
    },
    'enterprise.detail.confirm.yes': {
        [LANGUAGES.ZH]: '确认',
        [LANGUAGES.EN]: 'Confirm'
    },
    'enterprise.detail.confirm.no': {
        [LANGUAGES.ZH]: '取消',
        [LANGUAGES.EN]: 'Cancel'
    },
    'enterprise.detail.view.success': {
        [LANGUAGES.ZH]: '查看成功',
        [LANGUAGES.EN]: 'View successful'
    },
    'enterprise.detail.view.error': {
        [LANGUAGES.ZH]: '查看失败',
        [LANGUAGES.EN]: 'View failed'
    },

    // 广交会采购商页面翻译
    'canton.purchaser.search.title': {
        [LANGUAGES.ZH]: '搜索',
        [LANGUAGES.EN]: 'Search'
    },
    'canton.purchaser.search.placeholder': {
        [LANGUAGES.ZH]: '搜索采购商名称、广交会届数或采购产品类型',
        [LANGUAGES.EN]: 'Search purchaser name, canton fair session or product type'
    },
    'canton.purchaser.reset.filters': {
        [LANGUAGES.ZH]: '重置筛选条件',
        [LANGUAGES.EN]: 'Reset Filters'
    },
    'canton.purchaser.session': {
        [LANGUAGES.ZH]: '广交会届数',
        [LANGUAGES.EN]: 'Canton Fair Session'
    },
    'canton.purchaser.all': {
        [LANGUAGES.ZH]: '全部',
        [LANGUAGES.EN]: 'All'
    },
    'canton.purchaser.session.placeholder': {
        [LANGUAGES.ZH]: '搜索届数',
        [LANGUAGES.EN]: 'Search session'
    },
    'canton.purchaser.country': {
        [LANGUAGES.ZH]: '国家',
        [LANGUAGES.EN]: 'Country'
    },
    'canton.purchaser.country.placeholder': {
        [LANGUAGES.ZH]: '搜索国家',
        [LANGUAGES.EN]: 'Search country'
    },
    'canton.purchaser.export.data': {
        [LANGUAGES.ZH]: '导出数据',
        [LANGUAGES.EN]: 'Export Data'
    },
    'canton.purchaser.no.data': {
        [LANGUAGES.ZH]: '没有找到符合条件的采购商',
        [LANGUAGES.EN]: 'No matching purchasers found'
    },
    'canton.purchaser.collapse': {
        [LANGUAGES.ZH]: '收起',
        [LANGUAGES.EN]: 'Collapse'
    },
    'canton.purchaser.details': {
        [LANGUAGES.ZH]: '详情',
        [LANGUAGES.EN]: 'Details'
    },
    'canton.purchaser.detail.title': {
        [LANGUAGES.ZH]: '详细信息',
        [LANGUAGES.EN]: 'Detailed Information'
    },
    'canton.purchaser.session.info': {
        [LANGUAGES.ZH]: '广交会届数:',
        [LANGUAGES.EN]: 'Canton Fair Session:'
    },
    'canton.purchaser.country.info': {
        [LANGUAGES.ZH]: '国家:',
        [LANGUAGES.EN]: 'Country:'
    },
    'canton.purchaser.product.type': {
        [LANGUAGES.ZH]: '采购产品类型:',
        [LANGUAGES.EN]: 'Product Type:'
    },
    'canton.purchaser.contact.person': {
        [LANGUAGES.ZH]: '联系人:',
        [LANGUAGES.EN]: 'Contact Person:'
    },
    'canton.purchaser.contact.way': {
        [LANGUAGES.ZH]: '联系方式:',
        [LANGUAGES.EN]: 'Contact Way:'
    },
    'canton.purchaser.phone': {
        [LANGUAGES.ZH]: '电话:',
        [LANGUAGES.EN]: 'Phone:'
    },
    'canton.purchaser.fax': {
        [LANGUAGES.ZH]: '传真:',
        [LANGUAGES.EN]: 'Fax:'
    },
    'canton.purchaser.address': {
        [LANGUAGES.ZH]: '地址:',
        [LANGUAGES.EN]: 'Address:'
    },
    'canton.purchaser.zipcode': {
        [LANGUAGES.ZH]: '邮编:',
        [LANGUAGES.EN]: 'Zip Code:'
    },
    'canton.purchaser.email': {
        [LANGUAGES.ZH]: '邮箱:',
        [LANGUAGES.EN]: 'Email:'
    },
    'canton.purchaser.website': {
        [LANGUAGES.ZH]: '网站:',
        [LANGUAGES.EN]: 'Website:'
    },

    // 广交会参展商页面翻译
    'canton.exhibitor.search.title': {
        [LANGUAGES.ZH]: '搜索',
        [LANGUAGES.EN]: 'Search'
    },
    'canton.exhibitor.search.placeholder': {
        [LANGUAGES.ZH]: '搜索参展商名称',
        [LANGUAGES.EN]: 'Search exhibitor name'
    },
    'canton.purchaser.search.name.placeholder': {
        [LANGUAGES.ZH]: '搜索采购商名称',
        [LANGUAGES.EN]: 'Search purchaser name'
    },

    'canton.exhibitor.search.product': {
        [LANGUAGES.ZH]: '搜索参展商主营产品',
        [LANGUAGES.EN]: 'Search exhibitor main products'
    },
    'canton.exhibitor.reset.filters': {
        [LANGUAGES.ZH]: '重置筛选条件',
        [LANGUAGES.EN]: 'Reset Filters'
    },
    'canton.exhibitor.session': {
        [LANGUAGES.ZH]: '广交会届数',
        [LANGUAGES.EN]: 'Canton Fair Session'
    },
    'canton.exhibitor.all.sessions': {
        [LANGUAGES.ZH]: '全部届数',
        [LANGUAGES.EN]: 'All Sessions'
    },
    'canton.exhibitor.session.number': {
        [LANGUAGES.ZH]: '第{number}届',
        [LANGUAGES.EN]: 'Session {number}'
    },
    'canton.exhibitor.company.type': {
        [LANGUAGES.ZH]: '企业类型',
        [LANGUAGES.EN]: 'Company Type'
    },
    'canton.exhibitor.company.type.placeholder': {
        [LANGUAGES.ZH]: '搜索企业类型',
        [LANGUAGES.EN]: 'Search company type'
    },
    'canton.exhibitor.company.scale': {
        [LANGUAGES.ZH]: '企业规模',
        [LANGUAGES.EN]: 'Company Scale'
    },
    'canton.exhibitor.company.scale.placeholder': {
        [LANGUAGES.ZH]: '搜索企业规模',
        [LANGUAGES.EN]: 'Search company scale'
    },
    'canton.exhibitor.all': {
        [LANGUAGES.ZH]: '全选',
        [LANGUAGES.EN]: 'All'
    },
    'canton.exhibitor.export.data': {
        [LANGUAGES.ZH]: '导出数据',
        [LANGUAGES.EN]: 'Export Data'
    },
    'canton.exhibitor.no.data': {
        [LANGUAGES.ZH]: '没有找到符合条件的参展商',
        [LANGUAGES.EN]: 'No matching exhibitors found'
    },
    'canton.exhibitor.reload': {
        [LANGUAGES.ZH]: '重新加载',
        [LANGUAGES.EN]: 'Reload'
    },
    'canton.exhibitor.unknown': {
        [LANGUAGES.ZH]: '未知公司',
        [LANGUAGES.EN]: 'Unknown Company'
    },
    'canton.exhibitor.unknown.value': {
        [LANGUAGES.ZH]: '未知',
        [LANGUAGES.EN]: 'Unknown'
    },
    'canton.exhibitor.session.info': {
        [LANGUAGES.ZH]: '广交会届数:',
        [LANGUAGES.EN]: 'Canton Fair Session:'
    },
    'canton.exhibitor.company.scale.info': {
        [LANGUAGES.ZH]: '企业规模:',
        [LANGUAGES.EN]: 'Company Scale:'
    },
    'canton.exhibitor.company.type.info': {
        [LANGUAGES.ZH]: '企业类型:',
        [LANGUAGES.EN]: 'Company Type:'
    },
    'canton.exhibitor.main.products': {
        [LANGUAGES.ZH]: '主营产品:',
        [LANGUAGES.EN]: 'Main Products:'
    },
    'canton.exhibitor.collapse': {
        [LANGUAGES.ZH]: '收起',
        [LANGUAGES.EN]: 'Collapse'
    },
    'canton.exhibitor.details': {
        [LANGUAGES.ZH]: '详情',
        [LANGUAGES.EN]: 'Details'
    },
    'canton.exhibitor.detail.title': {
        [LANGUAGES.ZH]: '详细信息',
        [LANGUAGES.EN]: 'Detailed Information'
    },
    'canton.exhibitor.booth.number': {
        [LANGUAGES.ZH]: '展位号:',
        [LANGUAGES.EN]: 'Booth Number:'
    },
    'canton.exhibitor.contact.person': {
        [LANGUAGES.ZH]: '负责人:',
        [LANGUAGES.EN]: 'Contact Person:'
    },
    'canton.exhibitor.mobile': {
        [LANGUAGES.ZH]: '手机:',
        [LANGUAGES.EN]: 'Mobile:'
    },
    'canton.exhibitor.phone': {
        [LANGUAGES.ZH]: '电话:',
        [LANGUAGES.EN]: 'Phone:'
    },
    // Facebook页面翻译键补充
    'facebook.collapse': {
        [LANGUAGES.ZH]: '收起',
        [LANGUAGES.EN]: 'Collapse'
    },
    'facebook.details': {
        [LANGUAGES.ZH]: '详情',
        [LANGUAGES.EN]: 'Details'
    },
    'facebook.details.info': {
        [LANGUAGES.ZH]: '详细信息',
        [LANGUAGES.EN]: 'Detailed Information'
    },
    'facebook.gender': {
        [LANGUAGES.ZH]: '性别',
        [LANGUAGES.EN]: 'Gender'
    },
    'facebook.current.location': {
        [LANGUAGES.ZH]: '当前所在地',
        [LANGUAGES.EN]: 'Current Location'
    },
    'facebook.hometown': {
        [LANGUAGES.ZH]: '家乡',
        [LANGUAGES.EN]: 'Hometown'
    },
    'facebook.relationship.status': {
        [LANGUAGES.ZH]: '关系状态',
        [LANGUAGES.EN]: 'Relationship Status'
    },
    'facebook.workplace': {
        [LANGUAGES.ZH]: '工作地点',
        [LANGUAGES.EN]: 'Workplace'
    },
    'facebook.email.hasEmail': {
        [LANGUAGES.ZH]: '有邮箱',
        [LANGUAGES.EN]: 'Has Email'
    },
    'facebook.email.noEmail': {
        [LANGUAGES.ZH]: '无邮箱',
        [LANGUAGES.EN]: 'No Email'
    },
    'facebook.remarks': {
        [LANGUAGES.ZH]: '备注',
        [LANGUAGES.EN]: 'Remarks'
    },
    'facebook.total': {
        [LANGUAGES.ZH]: '共 {total} 条记录',
        [LANGUAGES.EN]: 'Total {total} records'
    },
    'facebook.leads.success': {
        [LANGUAGES.ZH]: '已成功添加到线索',
        [LANGUAGES.EN]: 'Successfully added to leads'
    },
    'facebook.leads.failed': {
        [LANGUAGES.ZH]: '添加到线索失败',
        [LANGUAGES.EN]: 'Failed to add to leads'
    },
    // 添加缺失的翻译键
    'all': {
        [LANGUAGES.ZH]: '全部',
        [LANGUAGES.EN]: 'All'
    },
    'enterRemarks': {
        [LANGUAGES.ZH]: '请输入备注信息',
        [LANGUAGES.EN]: 'Please enter remarks'
    },
    'cancel': {
        [LANGUAGES.ZH]: '取消',
        [LANGUAGES.EN]: 'Cancel'
    },
    'confirm': {
        [LANGUAGES.ZH]: '确认',
        [LANGUAGES.EN]: 'Confirm'
    },
    'noData': {
        [LANGUAGES.ZH]: '暂无数据',
        [LANGUAGES.EN]: 'No data available'
    },
    'search': {
        [LANGUAGES.ZH]: '搜索',
        [LANGUAGES.EN]: 'Search'
    },
    // 导出记录页面
    'exportRecords.title': {
        [LANGUAGES.ZH]: '我的导出记录',
        [LANGUAGES.EN]: 'My Export Records'
    },
    'exportRecords.fileName': {
        [LANGUAGES.ZH]: '文件名称',
        [LANGUAGES.EN]: 'File Name'
    },
    'exportRecords.dataSource': {
        [LANGUAGES.ZH]: '数据来源',
        [LANGUAGES.EN]: 'Data Source'
    },
    'exportRecords.fileSize': {
        [LANGUAGES.ZH]: '文件大小',
        [LANGUAGES.EN]: 'File Size'
    },
    'exportRecords.recordCount': {
        [LANGUAGES.ZH]: '记录数量',
        [LANGUAGES.EN]: 'Record Count'
    },
    'exportRecords.exportDate': {
        [LANGUAGES.ZH]: '导出时间',
        [LANGUAGES.EN]: 'Export Date'
    },
    'facebook.loading': {
        [LANGUAGES.ZH]: '正在获取详细信息...',
        [LANGUAGES.EN]: 'loading details...'
    },
    'facebook.look.file': {
        [LANGUAGES.ZH]: '查看资料',
        [LANGUAGES.EN]: 'View Data'
    },
    'facebook.contact.info': {
        [LANGUAGES.ZH]: '联系方式',
        [LANGUAGES.EN]: 'Contact Information'
    },
    'facebook.copy.link': {
        [LANGUAGES.ZH]: '复制链接',
        [LANGUAGES.EN]: 'Copy Link'
    },
    'facebook.update.date': {
        [LANGUAGES.ZH]: '更新日期',
        [LANGUAGES.EN]: 'Update Date'
    },
    'facebook.join.date': {
        [LANGUAGES.ZH]: '加入日期',
        [LANGUAGES.EN]: 'Join Date'
    },
    'facebook.id': {
        [LANGUAGES.ZH]: '用户账号',
        [LANGUAGES.EN]: 'Facebook ID'
    },
    'facebook.start.match': {
        [LANGUAGES.ZH]: '开始匹配',
        [LANGUAGES.EN]: 'Start Matching'
    },
    'facebook.download.result': {
        [LANGUAGES.ZH]: '下载结果',
        [LANGUAGES.EN]: 'Download Result'
    },
    // 智能搜邮页面翻译键
    'intelligent.mail.title': {
        [LANGUAGES.ZH]: '联系方式搜查',
        [LANGUAGES.EN]: 'Contact Search'
    },
    'intelligent.mail.description': {
        [LANGUAGES.ZH]: '通过关键词在搜索引擎中查询相关企业联系方式',
        [LANGUAGES.EN]: 'Search for corporate contact information using keywords in search engines'
    },
    'intelligent.mail.search.email': {
        [LANGUAGES.ZH]: '搜邮箱',
        [LANGUAGES.EN]: 'Search Email'
    },
    'intelligent.mail.search.phone': {
        [LANGUAGES.ZH]: '搜电话',
        [LANGUAGES.EN]: 'Search Phone'
    },
    'intelligent.mail.search.placeholder': {
        [LANGUAGES.ZH]: '请输入公司名称、人名、网址进行搜索',
        [LANGUAGES.EN]: 'Enter company name, person name, or website URL to search'
    },
    'intelligent.mail.search.button': {
        [LANGUAGES.ZH]: '搜索',
        [LANGUAGES.EN]: 'Search'
    },
    'intelligent.mail.results': {
        [LANGUAGES.ZH]: '搜索结果',
        [LANGUAGES.EN]: 'Search Results'
    },
    'intelligent.mail.return': {
        [LANGUAGES.ZH]: '返回',
        [LANGUAGES.EN]: 'Return'
    },
    'intelligent.mail.load.more': {
        [LANGUAGES.ZH]: '加载更多',
        [LANGUAGES.EN]: 'Load More'
    },
    'intelligent.mail.no.results': {
        [LANGUAGES.ZH]: '未找到相关结果，请尝试其他关键词',
        [LANGUAGES.EN]: 'No relevant results found. Please try other keywords.'
    },
    'intelligent.mail.feature.keyword': {
        [LANGUAGES.ZH]: '关键词搜索',
        [LANGUAGES.EN]: 'Keyword Search'
    },
    'intelligent.mail.feature.keyword.desc': {
        [LANGUAGES.ZH]: '输入公司名、人名或网址，获取相关联系方式',
        [LANGUAGES.EN]: 'Enter company name, person name or URL to get relevant contact information'
    },
    'intelligent.mail.feature.collection': {
        [LANGUAGES.ZH]: '邮箱电话搜集',
        [LANGUAGES.EN]: 'Email & Phone Collection'
    },
    'intelligent.mail.feature.collection.desc': {
        [LANGUAGES.ZH]: '从搜索结果中提取有效的邮箱和电话联系方式',
        [LANGUAGES.EN]: 'Extract valid email and phone contacts from search results'
    },
    'intelligent.mail.feature.summary': {
        [LANGUAGES.ZH]: '信息汇总',
        [LANGUAGES.EN]: 'Information Summary'
    },
    'intelligent.mail.feature.summary.desc': {
        [LANGUAGES.ZH]: '汇总显示搜索到的联系人信息，便于快速浏览',
        [LANGUAGES.EN]: 'Summarize contact information found for quick browsing'
    },
    'intelligent.mail.feature.copy': {
        [LANGUAGES.ZH]: '一键复制',
        [LANGUAGES.EN]: 'One-Click Copy'
    },
    'intelligent.mail.feature.copy.desc': {
        [LANGUAGES.ZH]: '便捷复制功能，快速保存查询到的联系信息',
        [LANGUAGES.EN]: 'Convenient copy function to quickly save contact information'
    },
    'intelligent.mail.company': {
        [LANGUAGES.ZH]: '公司名称',
        [LANGUAGES.EN]: 'Company Name'
    },
    'intelligent.mail.contact': {
        [LANGUAGES.ZH]: '联系人',
        [LANGUAGES.EN]: 'Contact Person'
    },
    'intelligent.mail.position': {
        [LANGUAGES.ZH]: '职位',
        [LANGUAGES.EN]: 'Position'
    },
    'intelligent.mail.email': {
        [LANGUAGES.ZH]: '邮箱',
        [LANGUAGES.EN]: 'Email'
    },
    'intelligent.mail.phone': {
        [LANGUAGES.ZH]: '电话',
        [LANGUAGES.EN]: 'Phone'
    },
    'intelligent.mail.region': {
        [LANGUAGES.ZH]: '地区',
        [LANGUAGES.EN]: 'Region'
    },
    'intelligent.mail.website': {
        [LANGUAGES.ZH]: '网站',
        [LANGUAGES.EN]: 'Website'
    },
    'intelligent.mail.industry': {
        [LANGUAGES.ZH]: '行业',
        [LANGUAGES.EN]: 'Industry'
    },
    'intelligent.mail.session': {
        [LANGUAGES.ZH]: '届次',
        [LANGUAGES.EN]: 'Session'
    },
    'intelligent.mail.booth': {
        [LANGUAGES.ZH]: '展位号',
        [LANGUAGES.EN]: 'Booth Number'
    },
    'intelligent.mail.account': {
        [LANGUAGES.ZH]: '账号',
        [LANGUAGES.EN]: 'Account'
    },
    'intelligent.mail.copy': {
        [LANGUAGES.ZH]: '复制',
        [LANGUAGES.EN]: 'Copy'
    },
    'intelligent.mail.unknown': {
        [LANGUAGES.ZH]: '未知',
        [LANGUAGES.EN]: 'Unknown'
    },
    'intelligent.mail.source.canton.exhibitor': {
        [LANGUAGES.ZH]: '广交会参展商',
        [LANGUAGES.EN]: 'Canton Fair Exhibitor'
    },
    'intelligent.mail.source.canton.purchaser': {
        [LANGUAGES.ZH]: '广交会采购商',
        [LANGUAGES.EN]: 'Canton Fair Purchaser'
    },
    'intelligent.mail.source.facebook': {
        [LANGUAGES.ZH]: 'Facebook',
        [LANGUAGES.EN]: 'Facebook'
    },
    'intelligent.mail.source.twitter': {
        [LANGUAGES.ZH]: 'Twitter',
        [LANGUAGES.EN]: 'Twitter'
    },
    'intelligent.mail.source.linkedin': {
        [LANGUAGES.ZH]: 'LinkedIn',
        [LANGUAGES.EN]: 'LinkedIn'
    },
    'intelligent.mail.source.enterprise': {
        [LANGUAGES.ZH]: '全球企业库',
        [LANGUAGES.EN]: 'Global Enterprise Library'
    },
    'intelligent.mail.source.unknown': {
        [LANGUAGES.ZH]: '未知来源',
        [LANGUAGES.EN]: 'Unknown Source'
    },
    // 邮件反查页面翻译键
    'email.reverse.title': {
        [LANGUAGES.ZH]: '邮件反查',
        [LANGUAGES.EN]: 'Email Reverse Check'
    },
    'email.reverse.description': {
        [LANGUAGES.ZH]: '通过已知邮箱或电话查询关联的企业和个人详细信息',
        [LANGUAGES.EN]: 'Query associated companies and personal details through known email or phone'
    },
    'email.reverse.email.check': {
        [LANGUAGES.ZH]: '邮件反查',
        [LANGUAGES.EN]: 'Email Check'
    },
    'email.reverse.phone.check': {
        [LANGUAGES.ZH]: '电话反查',
        [LANGUAGES.EN]: 'Phone Check'
    },
    'email.reverse.email.placeholder': {
        [LANGUAGES.ZH]: '请输入完整邮箱地址进行精确查询',
        [LANGUAGES.EN]: 'Please enter the complete email address for precise query'
    },
    'email.reverse.phone.placeholder': {
        [LANGUAGES.ZH]: '请输入完整电话号码进行精确查询',
        [LANGUAGES.EN]: 'Please enter the complete phone number for precise query'
    },
    'email.reverse.search.button': {
        [LANGUAGES.ZH]: '查询',
        [LANGUAGES.EN]: 'Query'
    },
    'email.reverse.results': {
        [LANGUAGES.ZH]: '搜索结果',
        [LANGUAGES.EN]: 'Search Results'
    },
    'email.reverse.return': {
        [LANGUAGES.ZH]: '返回',
        [LANGUAGES.EN]: 'Return'
    },
    'email.reverse.load.more': {
        [LANGUAGES.ZH]: '加载更多',
        [LANGUAGES.EN]: 'Load More'
    },
    'email.reverse.no.results': {
        [LANGUAGES.ZH]: '未找到相关结果，请确认输入的邮箱或电话是否正确',
        [LANGUAGES.EN]: 'No relevant results found. Please confirm if the email or phone number is correct.'
    },
    'email.reverse.feature.email': {
        [LANGUAGES.ZH]: '邮箱反查',
        [LANGUAGES.EN]: 'Email Lookup'
    },
    'email.reverse.feature.email.desc': {
        [LANGUAGES.ZH]: '输入完整邮箱地址查询对应的企业和个人信息',
        [LANGUAGES.EN]: 'Input complete email address to query corresponding company and personal information'
    },
    'email.reverse.feature.phone': {
        [LANGUAGES.ZH]: '电话反查',
        [LANGUAGES.EN]: 'Phone Lookup'
    },
    'email.reverse.feature.phone.desc': {
        [LANGUAGES.ZH]: '输入电话号码查询对应的企业和个人联系记录',
        [LANGUAGES.EN]: 'Input phone number to query corresponding company and personal contact records'
    },
    'email.reverse.feature.multi.source': {
        [LANGUAGES.ZH]: '多源数据',
        [LANGUAGES.EN]: 'Multi-source Data'
    },
    'email.reverse.feature.multi.source.desc': {
        [LANGUAGES.ZH]: '整合多个数据源信息，提供更全面的查询结果',
        [LANGUAGES.EN]: 'Integrate information from multiple data sources for more comprehensive query results'
    },
    'email.reverse.feature.export': {
        [LANGUAGES.ZH]: '快速导出',
        [LANGUAGES.EN]: 'Quick Export'
    },
    'email.reverse.feature.export.desc': {
        [LANGUAGES.ZH]: '一键复制所需信息，方便后续联系跟进',
        [LANGUAGES.EN]: 'One-click copy of required information for convenient follow-up'
    },
    'email.reverse.copy': {
        [LANGUAGES.ZH]: '复制',
        [LANGUAGES.EN]: 'Copy'
    },
    'email.reverse.unknown': {
        [LANGUAGES.ZH]: '未知名称',
        [LANGUAGES.EN]: 'Unknown Name'
    },
    'email.reverse.source.canton.exhibitor': {
        [LANGUAGES.ZH]: '广交会参展商',
        [LANGUAGES.EN]: 'Canton Fair Exhibitor'
    },
    'email.reverse.source.canton.purchaser': {
        [LANGUAGES.ZH]: '广交会采购商',
        [LANGUAGES.EN]: 'Canton Fair Purchaser'
    },
    'email.reverse.source.facebook': {
        [LANGUAGES.ZH]: 'Facebook数据',
        [LANGUAGES.EN]: 'Facebook Data'
    },
    'email.reverse.source.linkedin': {
        [LANGUAGES.ZH]: 'LinkedIn数据',
        [LANGUAGES.EN]: 'LinkedIn Data'
    },
    'email.reverse.source.enterprise': {
        [LANGUAGES.ZH]: '全球企业库',
        [LANGUAGES.EN]: 'Global Enterprise Library'
    },
    'email.reverse.source.users': {
        [LANGUAGES.ZH]: '用户数据',
        [LANGUAGES.EN]: 'User Data'
    },
    'email.reverse.source.unknown': {
        [LANGUAGES.ZH]: '未知来源',
        [LANGUAGES.EN]: 'Unknown Source'
    },

    // 邮箱反查字段标签翻译
    'email.reverse.field.birthdate': {
        [LANGUAGES.ZH]: '出生日期',
        [LANGUAGES.EN]: 'Birth Date'
    },
    'email.reverse.field.workplace': {
        [LANGUAGES.ZH]: '工作地点',
        [LANGUAGES.EN]: 'Workplace'
    },
    'email.reverse.field.workplacecity': {
        [LANGUAGES.ZH]: '工作城市',
        [LANGUAGES.EN]: 'Workplace City'
    },
    'email.reverse.field.birthplace': {
        [LANGUAGES.ZH]: '出生地',
        [LANGUAGES.EN]: 'Birth Place'
    },
    'email.reverse.field.uid': {
        [LANGUAGES.ZH]: '用户ID',
        [LANGUAGES.EN]: 'User ID'
    },
    'email.reverse.field.first_name': {
        [LANGUAGES.ZH]: '名',
        [LANGUAGES.EN]: 'First Name'
    },
    'email.reverse.field.last_name': {
        [LANGUAGES.ZH]: '姓',
        [LANGUAGES.EN]: 'Last Name'
    },
    'email.reverse.field.gender': {
        [LANGUAGES.ZH]: '性别',
        [LANGUAGES.EN]: 'Gender'
    },
    'email.reverse.field.email': {
        [LANGUAGES.ZH]: '邮箱',
        [LANGUAGES.EN]: 'Email'
    },
    'email.reverse.field.phonenumber': {
        [LANGUAGES.ZH]: '电话号码',
        [LANGUAGES.EN]: 'Phone Number'
    },
    'email.reverse.field.joindate': {
        [LANGUAGES.ZH]: '加入日期',
        [LANGUAGES.EN]: 'Join Date'
    },
    'email.reverse.field.id': {
        [LANGUAGES.ZH]: 'ID',
        [LANGUAGES.EN]: 'ID'
    },
    // 中文字段名的翻译
    'email.reverse.field.名': {
        [LANGUAGES.ZH]: '名',
        [LANGUAGES.EN]: 'First Name'
    },
    'email.reverse.field.姓': {
        [LANGUAGES.ZH]: '姓',
        [LANGUAGES.EN]: 'Last Name'
    },
    'email.reverse.field.性别': {
        [LANGUAGES.ZH]: '性别',
        [LANGUAGES.EN]: 'Gender'
    },
    'email.reverse.field.邮箱': {
        [LANGUAGES.ZH]: '邮箱',
        [LANGUAGES.EN]: 'Email'
    },

    // 更多常用字段翻译
    'email.reverse.field.middle_name': {
        [LANGUAGES.ZH]: '中间名',
        [LANGUAGES.EN]: 'Middle Name'
    },
    'email.reverse.field.title': {
        [LANGUAGES.ZH]: '职位',
        [LANGUAGES.EN]: 'Title'
    },
    'email.reverse.field.company_name': {
        [LANGUAGES.ZH]: '公司名称',
        [LANGUAGES.EN]: 'Company Name'
    },
    'email.reverse.field.mailing_address': {
        [LANGUAGES.ZH]: '邮寄地址',
        [LANGUAGES.EN]: 'Mailing Address'
    },
    'email.reverse.field.primary_city': {
        [LANGUAGES.ZH]: '主要城市',
        [LANGUAGES.EN]: 'Primary City'
    },
    'email.reverse.field.primary_state': {
        [LANGUAGES.ZH]: '主要州',
        [LANGUAGES.EN]: 'Primary State'
    },
    'email.reverse.field.zip_code': {
        [LANGUAGES.ZH]: '邮政编码',
        [LANGUAGES.EN]: 'Zip Code'
    },
    'email.reverse.field.country': {
        [LANGUAGES.ZH]: '国家',
        [LANGUAGES.EN]: 'Country'
    },
    'email.reverse.field.phone': {
        [LANGUAGES.ZH]: '电话',
        [LANGUAGES.EN]: 'Phone'
    },
    'email.reverse.field.web_address': {
        [LANGUAGES.ZH]: '网站',
        [LANGUAGES.EN]: 'Website'
    },
    'email.reverse.field.industry': {
        [LANGUAGES.ZH]: '行业',
        [LANGUAGES.EN]: 'Industry'
    },
    'email.reverse.field.location': {
        [LANGUAGES.ZH]: '位置',
        [LANGUAGES.EN]: 'Location'
    },
    'email.reverse.field.name': {
        [LANGUAGES.ZH]: '姓名',
        [LANGUAGES.EN]: 'Name'
    },
    'email.reverse.field.position': {
        [LANGUAGES.ZH]: '职位',
        [LANGUAGES.EN]: 'Position'
    },
    'email.reverse.field.dob': {
        [LANGUAGES.ZH]: '出生日期',
        [LANGUAGES.EN]: 'Date of Birth'
    },
    'email.reverse.field.Full_name': {
        [LANGUAGES.ZH]: '全名',
        [LANGUAGES.EN]: 'Full Name'
    },
    'email.reverse.field.Job_title': {
        [LANGUAGES.ZH]: '职位头衔',
        [LANGUAGES.EN]: 'Job Title'
    },
    'email.reverse.field.Company_Name': {
        [LANGUAGES.ZH]: '公司名称',
        [LANGUAGES.EN]: 'Company Name'
    },
    'email.reverse.field.Phone_numbers': {
        [LANGUAGES.ZH]: '电话号码',
        [LANGUAGES.EN]: 'Phone Numbers'
    },
    'email.reverse.field.Emails': {
        [LANGUAGES.ZH]: '邮箱',
        [LANGUAGES.EN]: 'Emails'
    },
    'email.reverse.field.mobile_phone': {
        [LANGUAGES.ZH]: '手机',
        [LANGUAGES.EN]: 'Mobile Phone'
    },
    'email.reverse.field.telephone': {
        [LANGUAGES.ZH]: '电话',
        [LANGUAGES.EN]: 'Telephone'
    },
    // 域名反查页面翻译键
    'domain.reverse.title': {
        [LANGUAGES.ZH]: '域名反查',
        [LANGUAGES.EN]: 'Domain Reverse Check'
    },
    'domain.reverse.description': {
        [LANGUAGES.ZH]: '通过已知域名查询关联的企业和个人详细信息',
        [LANGUAGES.EN]: 'Query associated companies and personal details through known domain'
    },
    'domain.reverse.search.placeholder': {
        [LANGUAGES.ZH]: '请输入域名进行查询，如: example.com',
        [LANGUAGES.EN]: 'Please enter domain for query, e.g.: example.com'
    },
    'domain.reverse.search.button': {
        [LANGUAGES.ZH]: '查询',
        [LANGUAGES.EN]: 'Query'
    },
    'domain.reverse.results': {
        [LANGUAGES.ZH]: '搜索结果',
        [LANGUAGES.EN]: 'Search Results'
    },
    'domain.reverse.return': {
        [LANGUAGES.ZH]: '返回',
        [LANGUAGES.EN]: 'Return'
    },
    'domain.reverse.load.more': {
        [LANGUAGES.ZH]: '加载更多',
        [LANGUAGES.EN]: 'Load More'
    },
    'domain.reverse.no.results': {
        [LANGUAGES.ZH]: '未找到相关结果，请确认输入的域名是否正确',
        [LANGUAGES.EN]: 'No relevant results found. Please confirm if the domain is correct.'
    },
    'domain.reverse.copy': {
        [LANGUAGES.ZH]: '复制',
        [LANGUAGES.EN]: 'Copy'
    },
    'domain.reverse.unknown': {
        [LANGUAGES.ZH]: '未知名称',
        [LANGUAGES.EN]: 'Unknown Name'
    },
    'domain.reverse.feature.domain': {
        [LANGUAGES.ZH]: '域名反查',
        [LANGUAGES.EN]: 'Domain Lookup'
    },
    'domain.reverse.feature.domain.desc': {
        [LANGUAGES.ZH]: '输入域名查询对应的企业和个人信息',
        [LANGUAGES.EN]: 'Input domain to query corresponding company and personal information'
    },
    'domain.reverse.feature.precision': {
        [LANGUAGES.ZH]: '精准匹配',
        [LANGUAGES.EN]: 'Precise Matching'
    },
    'domain.reverse.feature.precision.desc': {
        [LANGUAGES.ZH]: '系统会自动处理域名格式，支持www.example.com或example.com等多种格式',
        [LANGUAGES.EN]: 'System automatically processes domain formats, supporting www.example.com or example.com formats'
    },
    'domain.reverse.feature.multi.source': {
        [LANGUAGES.ZH]: '多源数据',
        [LANGUAGES.EN]: 'Multi-source Data'
    },
    'domain.reverse.feature.multi.source.desc': {
        [LANGUAGES.ZH]: '整合多个数据源信息，提供更全面的查询结果',
        [LANGUAGES.EN]: 'Integrate information from multiple data sources for more comprehensive query results'
    },
    'domain.reverse.feature.export': {
        [LANGUAGES.ZH]: '快速导出',
        [LANGUAGES.EN]: 'Quick Export'
    },
    'domain.reverse.feature.export.desc': {
        [LANGUAGES.ZH]: '一键复制所需信息，方便后续联系跟进',
        [LANGUAGES.EN]: 'One-click copy of required information for convenient follow-up'
    },
    'domain.reverse.source.canton.exhibitor': {
        [LANGUAGES.ZH]: '广交会参展商',
        [LANGUAGES.EN]: 'Canton Fair Exhibitor'
    },
    'domain.reverse.source.canton.buyer': {
        [LANGUAGES.ZH]: '广交会采购商',
        [LANGUAGES.EN]: 'Canton Fair Buyer'
    },
    'domain.reverse.source.facebook': {
        [LANGUAGES.ZH]: 'Facebook数据',
        [LANGUAGES.EN]: 'Facebook Data'
    },
    'domain.reverse.source.linkedin': {
        [LANGUAGES.ZH]: 'LinkedIn数据',
        [LANGUAGES.EN]: 'LinkedIn Data'
    },
    'domain.reverse.source.twitter': {
        [LANGUAGES.ZH]: 'Twitter数据',
        [LANGUAGES.EN]: 'Twitter Data'
    },
    'domain.reverse.source.enterprise': {
        [LANGUAGES.ZH]: '全球企业库',
        [LANGUAGES.EN]: 'Global Enterprise Library'
    },
    'domain.reverse.source.customs': {
        [LANGUAGES.ZH]: '海关数据',
        [LANGUAGES.EN]: 'Customs Data'
    },
    'domain.reverse.source.unknown': {
        [LANGUAGES.ZH]: '未知来源',
        [LANGUAGES.EN]: 'Unknown Source'
    },

    // 社媒反查页面翻译键
    'social.media.title': {
        [LANGUAGES.ZH]: '社媒反查',
        [LANGUAGES.EN]: 'Social Media Reverse Check'
    },
    'social.media.description': {
        [LANGUAGES.ZH]: '通过社交媒体账号查询关联的企业和个人信息',
        [LANGUAGES.EN]: 'Query associated companies and personal information through social media accounts'
    },
    'social.media.linkedin.data': {
        [LANGUAGES.ZH]: '领英数据',
        [LANGUAGES.EN]: 'LinkedIn Data'
    },
    'social.media.twitter.data': {
        [LANGUAGES.ZH]: '推特数据',
        [LANGUAGES.EN]: 'Twitter Data'
    },
    'social.media.linkedin.placeholder': {
        [LANGUAGES.ZH]: '请输入领英URL进行搜索',
        [LANGUAGES.EN]: 'Please enter LinkedIn URL for search'
    },
    'social.media.twitter.placeholder': {
        [LANGUAGES.ZH]: '请输入推特用户名进行搜索',
        [LANGUAGES.EN]: 'Please enter Twitter username for search'
    },
    'social.media.search.button': {
        [LANGUAGES.ZH]: '搜索',
        [LANGUAGES.EN]: 'Search'
    },
    'social.media.results': {
        [LANGUAGES.ZH]: '搜索结果',
        [LANGUAGES.EN]: 'Search Results'
    },
    'social.media.return': {
        [LANGUAGES.ZH]: '返回',
        [LANGUAGES.EN]: 'Return'
    },
    'social.media.load.more': {
        [LANGUAGES.ZH]: '加载更多',
        [LANGUAGES.EN]: 'Load More'
    },
    'social.media.no.results': {
        [LANGUAGES.ZH]: '未找到相关结果，请确认输入的关键词是否正确',
        [LANGUAGES.EN]: 'No relevant results found. Please confirm if the keyword is correct.'
    },
    'social.media.copy': {
        [LANGUAGES.ZH]: '复制',
        [LANGUAGES.EN]: 'Copy'
    },
    'social.media.visit': {
        [LANGUAGES.ZH]: '访问',
        [LANGUAGES.EN]: 'Visit'
    },
    'social.media.unknown': {
        [LANGUAGES.ZH]: '未知名称',
        [LANGUAGES.EN]: 'Unknown Name'
    },
    'social.media.feature.linkedin': {
        [LANGUAGES.ZH]: '领英数据',
        [LANGUAGES.EN]: 'LinkedIn Data'
    },
    'social.media.feature.linkedin.desc': {
        [LANGUAGES.ZH]: '查询领英相关的个人资料信息',
        [LANGUAGES.EN]: 'Query personal profile information related to LinkedIn'
    },
    'social.media.feature.twitter': {
        [LANGUAGES.ZH]: '推特数据',
        [LANGUAGES.EN]: 'Twitter Data'
    },
    'social.media.feature.twitter.desc': {
        [LANGUAGES.ZH]: '获取推特用户关联的联系方式和基本资料',
        [LANGUAGES.EN]: 'Get contact information and basic details associated with Twitter users'
    },
    'social.media.feature.multi.dimension': {
        [LANGUAGES.ZH]: '多维度查询',
        [LANGUAGES.EN]: 'Multi-dimensional Query'
    },
    'social.media.feature.multi.dimension.desc': {
        [LANGUAGES.ZH]: '支持通过URL或关键词进行精准社媒账号查询',
        [LANGUAGES.EN]: 'Support precise social media account queries via URL or keywords'
    },
    'social.media.feature.complete.info': {
        [LANGUAGES.ZH]: '完整资料',
        [LANGUAGES.EN]: 'Complete Information'
    },
    'social.media.feature.complete.info.desc': {
        [LANGUAGES.ZH]: '提供社交账号关联的电话、邮箱等核心联系方式',
        [LANGUAGES.EN]: 'Provide core contact methods such as phone, email associated with social accounts'
    },
    'social.media.source.linkedin': {
        [LANGUAGES.ZH]: 'LinkedIn数据',
        [LANGUAGES.EN]: 'LinkedIn Data'
    },
    'social.media.source.twitter': {
        [LANGUAGES.ZH]: 'Twitter数据',
        [LANGUAGES.EN]: 'Twitter Data'
    },
    'social.media.source.unknown': {
        [LANGUAGES.ZH]: '未知来源',
        [LANGUAGES.EN]: 'Unknown Source'
    },

    // SMS页面相关
    'sms.feature.unavailable': {
        [LANGUAGES.ZH]: '功能暂未开放',
        [LANGUAGES.EN]: 'Feature Not Available'
    },
    'sms.feature.coming.soon': {
        [LANGUAGES.ZH]: '短信中心功能正在建设中，敬请期待！',
        [LANGUAGES.EN]: 'SMS Center feature is under development, stay tuned!'
    },
    'sms.back.home': {
        [LANGUAGES.ZH]: '返回首页',
        [LANGUAGES.EN]: 'Back to Home'
    },

    // Email页面相关
    'email.tab.send.record': {
        [LANGUAGES.ZH]: '发送记录',
        [LANGUAGES.EN]: 'Send Records'
    },
    'email.tab.send.email': {
        [LANGUAGES.ZH]: '发邮件',
        [LANGUAGES.EN]: 'Send Email'
    },
    'email.tab.templates': {
        [LANGUAGES.ZH]: '邮件模板',
        [LANGUAGES.EN]: 'Email Templates'
    },
    'email.tab.domains': {
        [LANGUAGES.ZH]: '域名配置',
        [LANGUAGES.EN]: 'Domain Settings'
    },
    'email.tab.emailConfig': {
        [LANGUAGES.ZH]: '邮箱配置',
        [LANGUAGES.EN]: 'Email Settings'
    },
    'email.tab.emailGuide': {
        [LANGUAGES.ZH]: '邮箱配置使用说明',
        [LANGUAGES.EN]: 'Email Configuration Guide'
    },
    // 邮箱配置使用说明翻译
    'email.guide.title': {
        [LANGUAGES.ZH]: '邮箱配置使用说明',
        [LANGUAGES.EN]: 'Email Configuration Guide'
    },
    'email.guide.subtitle': {
        [LANGUAGES.ZH]: '详细配置指南，助您快速上手',
        [LANGUAGES.EN]: 'Detailed configuration guide to help you get started quickly'
    },
    'email.guide.supportedTypes': {
        [LANGUAGES.ZH]: '支持邮箱类型',
        [LANGUAGES.EN]: 'Supported Email Types'
    },
    'email.guide.supportedList': {
        [LANGUAGES.ZH]: '本系统支持：Outlook（OAuth 2.0）、QQ（SMTP）、163/126（SMTP）、Gmail（SMTP）、Yahoo（SMTP）',
        [LANGUAGES.EN]: 'This system supports: Outlook (OAuth 2.0), QQ (SMTP), 163/126 (SMTP), Gmail (SMTP), Yahoo (SMTP)'
    },
    'email.guide.importantPrinciple': {
        [LANGUAGES.ZH]: '重要原则：绝大多数邮箱需开启两步验证并使用"授权码/应用专用密码"；Outlook 使用 OAuth 2.0 授权登录，不填写密码。',
        [LANGUAGES.EN]: 'Important Principle: Most mailboxes require two-step verification and use "authorization code/app-specific password"; Outlook uses OAuth 2.0 authorization login without password.'
    },
    'email.guide.outlook.title': {
        [LANGUAGES.ZH]: 'Outlook（OAuth 2.0｜Microsoft 365/Outlook.com）',
        [LANGUAGES.EN]: 'Outlook (OAuth 2.0 | Microsoft 365/Outlook.com)'
    },
    'email.guide.recommended': {
        [LANGUAGES.ZH]: '推荐',
        [LANGUAGES.EN]: 'Recommended'
    },
    'email.guide.coreLogic': {
        [LANGUAGES.ZH]: '核心原理',
        [LANGUAGES.EN]: 'Core Logic'
    },
    'email.guide.outlook.logic': {
        [LANGUAGES.ZH]: '通过微软账号登录并同意权限，系统使用 Access Token 发送邮件。',
        [LANGUAGES.EN]: 'Log in with your Microsoft account and grant permissions. The system uses Access Token to send emails.'
    },
    'email.guide.outlook.applicable': {
        [LANGUAGES.ZH]: '适用：Outlook.com/Hotmail/Live 个人邮箱、Microsoft 365 工作/学校邮箱。',
        [LANGUAGES.EN]: 'Applicable to: Outlook.com/Hotmail/Live personal mailboxes, Microsoft 365 work/school mailboxes.'
    },
    'email.guide.operationSteps': {
        [LANGUAGES.ZH]: '操作步骤',
        [LANGUAGES.EN]: 'Operation Steps'
    },
    'email.guide.outlook.step1': {
        [LANGUAGES.ZH]: '在系统内点击"使用 Outlook 登录（OAuth 2.0）"',
        [LANGUAGES.EN]: 'Click "Login with Outlook (OAuth 2.0)" in the system'
    },
    'email.guide.outlook.step2': {
        [LANGUAGES.ZH]: '跳转至微软登录页 → 使用您的邮箱登录',
        [LANGUAGES.EN]: 'Redirect to Microsoft login page → Log in with your email'
    },
    'email.guide.outlook.step3': {
        [LANGUAGES.ZH]: '在权限页选择"同意/允许"；如提示"需要管理员同意"，请联系组织管理员',
        [LANGUAGES.EN]: 'Select "Agree/Allow" on the permission page; if prompted "Admin consent required", contact your organization administrator'
    },
    'email.guide.outlook.step4': {
        [LANGUAGES.ZH]: '返回系统显示"授权成功"后即可发送邮件；令牌过期会自动续期或提示重新登录',
        [LANGUAGES.EN]: 'After returning to the system and displaying "Authorization successful", you can send emails; expired tokens will be automatically renewed or prompt for re-login'
    },
    'email.guide.precautions': {
        [LANGUAGES.ZH]: '注意事项',
        [LANGUAGES.EN]: 'Precautions'
    },
    'email.guide.outlook.note1': {
        [LANGUAGES.ZH]: '代表共享邮箱/公共邮箱发送：管理员需在 Exchange 中为您的账号授予"以其身份发送/代表发送"或"代表发送"。',
        [LANGUAGES.EN]: 'Send on behalf of shared/public mailbox: Administrator needs to grant "Send As/Send on Behalf" or "Send on Behalf" permissions in Exchange.'
    },
    'email.guide.outlook.note2': {
        [LANGUAGES.ZH]: '频繁要求重新登录：请允许微软登录域名的 Cookie；如为公司设备，可能受安全策略影响，请联系 IT。',
        [LANGUAGES.EN]: 'Frequent re-login requests: Allow cookies for Microsoft login domain; if it\'s a company device, it may be affected by security policies, please contact IT.'
    },
    'email.guide.qq.title': {
        [LANGUAGES.ZH]: 'QQ 邮箱（SMTP）',
        [LANGUAGES.EN]: 'QQ Mailbox (SMTP)'
    },
    'email.guide.qq.step1': {
        [LANGUAGES.ZH]: '登录网页版 QQ 邮箱 → 设置 → 账户 → 开启"POP3/IMAP/SMTP"',
        [LANGUAGES.EN]: 'Log in to QQ Mailbox web version → Settings → Account → Enable "POP3/IMAP/SMTP"'
    },
    'email.guide.qq.step2': {
        [LANGUAGES.ZH]: '按页面指引完成短信验证，获取"授权码"',
        [LANGUAGES.EN]: 'Complete SMS verification as instructed on the page to get "Authorization Code"'
    },
    'email.guide.qq.step3': {
        [LANGUAGES.ZH]: '在系统中填写邮箱账号和授权码（非登录密码）',
        [LANGUAGES.EN]: 'Enter mailbox account and authorization code (not login password) in the system'
    },
    'email.guide.configParams': {
        [LANGUAGES.ZH]: '系统配置参数',
        [LANGUAGES.EN]: 'System Configuration Parameters'
    },
    'email.guide.mailboxAccount': {
        [LANGUAGES.ZH]: '邮箱账号',
        [LANGUAGES.EN]: 'Mailbox Account'
    },
    'email.guide.password': {
        [LANGUAGES.ZH]: '密码',
        [LANGUAGES.EN]: 'Password'
    },
    'email.guide.qq.account': {
        [LANGUAGES.ZH]: '完整 QQ 邮箱',
        [LANGUAGES.EN]: 'Complete QQ Mailbox'
    },
    'email.guide.qq.password': {
        [LANGUAGES.ZH]: '授权码（非登录密码）',
        [LANGUAGES.EN]: 'Authorization Code (not login password)'
    },
    'email.guide.imap': {
        [LANGUAGES.ZH]: 'IMAP',
        [LANGUAGES.EN]: 'IMAP'
    },
    'email.guide.smtp': {
        [LANGUAGES.ZH]: 'SMTP',
        [LANGUAGES.EN]: 'SMTP'
    },
    'email.guide.qq.imap': {
        [LANGUAGES.ZH]: 'imap.qq.com 端口 993 SSL/TLS',
        [LANGUAGES.EN]: 'imap.qq.com port 993 SSL/TLS'
    },
    'email.guide.qq.smtp': {
        [LANGUAGES.ZH]: 'smtp.qq.com 端口 465 SSL/TLS 或 587 STARTTLS',
        [LANGUAGES.EN]: 'smtp.qq.com port 465 SSL/TLS or 587 STARTTLS'
    },
    'email.guide.commonIssues': {
        [LANGUAGES.ZH]: '常见问题',
        [LANGUAGES.EN]: 'Common Issues'
    },
    'email.guide.qq.issue1': {
        [LANGUAGES.ZH]: '认证失败：多因未使用授权码或端口/加密方式不匹配。',
        [LANGUAGES.EN]: 'Authentication failed: Often due to not using authorization code or port/encryption mismatch.'
    },
    'email.guide.qq.issue2': {
        [LANGUAGES.ZH]: '连接失败：检查防火墙/网络是否限制 465/587/993 端口。',
        [LANGUAGES.EN]: 'Connection failed: Check if firewall/network restricts ports 465/587/993.'
    },
    'email.guide.163.title': {
        [LANGUAGES.ZH]: '163/126 邮箱（SMTP）',
        [LANGUAGES.EN]: '163/126 Mailbox (SMTP)'
    },
    'email.guide.163.step1': {
        [LANGUAGES.ZH]: '登录网页版邮箱 → 设置 → POP3/SMTP/IMAP → 开启 IMAP 与 SMTP',
        [LANGUAGES.EN]: 'Log in to mailbox web version → Settings → POP3/SMTP/IMAP → Enable IMAP and SMTP'
    },
    'email.guide.163.step2': {
        [LANGUAGES.ZH]: '按指引生成"客户端授权密码/授权码"',
        [LANGUAGES.EN]: 'Generate "Client Authorization Password/Authorization Code" as instructed'
    },
    'email.guide.163.step3': {
        [LANGUAGES.ZH]: '在系统中填写邮箱账号和授权密码',
        [LANGUAGES.EN]: 'Enter mailbox account and authorization password in the system'
    },
    'email.guide.163.account': {
        [LANGUAGES.ZH]: '完整邮箱地址',
        [LANGUAGES.EN]: 'Complete Email Address'
    },
    'email.guide.163.password': {
        [LANGUAGES.ZH]: '授权密码',
        [LANGUAGES.EN]: 'Authorization Password'
    },
    'email.guide.163.imap': {
        [LANGUAGES.ZH]: 'imap.163.com / imap.126.com 端口 993 SSL/TLS',
        [LANGUAGES.EN]: 'imap.163.com / imap.126.com port 993 SSL/TLS'
    },
    'email.guide.163.smtp': {
        [LANGUAGES.ZH]: 'smtp.163.com / smtp.126.com 端口 465 SSL/TLS 或 587 STARTTLS',
        [LANGUAGES.EN]: 'smtp.163.com / smtp.126.com port 465 SSL/TLS or 587 STARTTLS'
    },
    'email.guide.gmail.title': {
        [LANGUAGES.ZH]: 'Gmail（SMTP）',
        [LANGUAGES.EN]: 'Gmail (SMTP)'
    },
    'email.guide.gmail.prerequisite': {
        [LANGUAGES.ZH]: '前置条件',
        [LANGUAGES.EN]: 'Prerequisites'
    },
    'email.guide.gmail.prerequisite.text': {
        [LANGUAGES.ZH]: '需开启"两步验证"。',
        [LANGUAGES.EN]: 'Two-step verification must be enabled.'
    },
    'email.guide.gmail.step1': {
        [LANGUAGES.ZH]: '进入 Google 账户安全 → 启用"两步验证"',
        [LANGUAGES.EN]: 'Go to Google Account Security → Enable "Two-Step Verification"'
    },
    'email.guide.gmail.step2': {
        [LANGUAGES.ZH]: '打开"应用专用密码" → 选择"邮件"并生成密码',
        [LANGUAGES.EN]: 'Open "App Passwords" → Select "Mail" and generate password'
    },
    'email.guide.gmail.step3': {
        [LANGUAGES.ZH]: '在系统中填写邮箱账号和应用专用密码',
        [LANGUAGES.EN]: 'Enter mailbox account and app-specific password in the system'
    },
    'email.guide.gmail.account': {
        [LANGUAGES.ZH]: '完整 Gmail 地址',
        [LANGUAGES.EN]: 'Complete Gmail Address'
    },
    'email.guide.gmail.password': {
        [LANGUAGES.ZH]: '应用专用密码',
        [LANGUAGES.EN]: 'App-Specific Password'
    },
    'email.guide.gmail.imap': {
        [LANGUAGES.ZH]: 'imap.gmail.com 端口 993 SSL/TLS',
        [LANGUAGES.EN]: 'imap.gmail.com port 993 SSL/TLS'
    },
    'email.guide.gmail.smtp': {
        [LANGUAGES.ZH]: 'smtp.gmail.com 端口 587 STARTTLS',
        [LANGUAGES.EN]: 'smtp.gmail.com port 587 STARTTLS'
    },
    'email.guide.gmail.tip': {
        [LANGUAGES.ZH]: '若看不到"应用专用密码"，通常为未启用两步验证或被组织策略禁用。',
        [LANGUAGES.EN]: 'If you cannot see "App Passwords", it is usually because two-step verification is not enabled or is disabled by organization policy.'
    },
    'email.guide.yahoo.title': {
        [LANGUAGES.ZH]: 'Yahoo 邮箱（SMTP）',
        [LANGUAGES.EN]: 'Yahoo Mailbox (SMTP)'
    },
    'email.guide.yahoo.step1': {
        [LANGUAGES.ZH]: '进入 Yahoo 账户安全 → 开启两步验证',
        [LANGUAGES.EN]: 'Go to Yahoo Account Security → Enable Two-Step Verification'
    },
    'email.guide.yahoo.step2': {
        [LANGUAGES.ZH]: '生成"应用密码"并妥善保存',
        [LANGUAGES.EN]: 'Generate "App Password" and save it securely'
    },
    'email.guide.yahoo.step3': {
        [LANGUAGES.ZH]: '在系统中填写邮箱账号和应用密码',
        [LANGUAGES.EN]: 'Enter mailbox account and app password in the system'
    },
    'email.guide.yahoo.account': {
        [LANGUAGES.ZH]: '完整 Yahoo 地址',
        [LANGUAGES.EN]: 'Complete Yahoo Address'
    },
    'email.guide.yahoo.password': {
        [LANGUAGES.ZH]: '应用密码',
        [LANGUAGES.EN]: 'App Password'
    },
    'email.guide.yahoo.imap': {
        [LANGUAGES.ZH]: 'imap.mail.yahoo.com 端口 993 SSL/TLS',
        [LANGUAGES.EN]: 'imap.mail.yahoo.com port 993 SSL/TLS'
    },
    'email.guide.yahoo.smtp': {
        [LANGUAGES.ZH]: 'smtp.mail.yahoo.com 端口 465 SSL/TLS 或 587 STARTTLS',
        [LANGUAGES.EN]: 'smtp.mail.yahoo.com port 465 SSL/TLS or 587 STARTTLS'
    },
    'email.guide.footer': {
        [LANGUAGES.ZH]: '配置完成后，您就可以开始使用邮件群发功能了！如有问题，请联系技术支持。',
        [LANGUAGES.EN]: 'After configuration is complete, you can start using the email mass sending feature! If you have any questions, please contact technical support.'
    },
    'email.send.overview': {
        [LANGUAGES.ZH]: '发送概览',
        [LANGUAGES.EN]: 'Send Overview'
    },
    'email.date.all': {
        [LANGUAGES.ZH]: '全部',
        [LANGUAGES.EN]: 'All'
    },
    'email.date.month': {
        [LANGUAGES.ZH]: '本月',
        [LANGUAGES.EN]: 'This Month'
    },
    'email.date.week': {
        [LANGUAGES.ZH]: '本周',
        [LANGUAGES.EN]: 'This Week'
    },
    'email.date.day': {
        [LANGUAGES.ZH]: '本日',
        [LANGUAGES.EN]: 'Today'
    },
    'email.date.year': {
        [LANGUAGES.ZH]: '年度',
        [LANGUAGES.EN]: 'Annual'
    },
    'email.stats.open.rate': {
        [LANGUAGES.ZH]: '邮件打开率',
        [LANGUAGES.EN]: 'Email Open Rate'
    },
    'email.stats.delivery.rate': {
        [LANGUAGES.ZH]: '邮件到达率',
        [LANGUAGES.EN]: 'Email Delivery Rate'
    },
    'email.stats.tasks': {
        [LANGUAGES.ZH]: '群发任务',
        [LANGUAGES.EN]: 'Mass Tasks'
    },
    'email.stats.total': {
        [LANGUAGES.ZH]: '邮件总数',
        [LANGUAGES.EN]: 'Total Emails'
    },
    'email.stats.sent': {
        [LANGUAGES.ZH]: '已发送',
        [LANGUAGES.EN]: 'Sent'
    },
    'email.status.overview': {
        [LANGUAGES.ZH]: '邮件情况概览',
        [LANGUAGES.EN]: 'Email Status Overview'
    },
    'email.chart.status.distribution': {
        [LANGUAGES.ZH]: '发送状态分布',
        [LANGUAGES.EN]: 'Send Status Distribution'
    },
    'email.chart.send.trend': {
        [LANGUAGES.ZH]: '发送趋势',
        [LANGUAGES.EN]: 'Send Trend'
    },
    'email.send.records': {
        [LANGUAGES.ZH]: '发送记录',
        [LANGUAGES.EN]: 'Send Records'
    },
    'email.table.subject': {
        [LANGUAGES.ZH]: '邮件主题',
        [LANGUAGES.EN]: 'Email Subject'
    },
    'email.table.recipients': {
        [LANGUAGES.ZH]: '收件人数量',
        [LANGUAGES.EN]: 'Recipients Count'
    },
    'email.table.send.time': {
        [LANGUAGES.ZH]: '发送时间',
        [LANGUAGES.EN]: 'Send Time'
    },
    'email.table.delivered': {
        [LANGUAGES.ZH]: '送达数量',
        [LANGUAGES.EN]: 'Delivered Count'
    },
    'email.table.status': {
        [LANGUAGES.ZH]: '状态',
        [LANGUAGES.EN]: 'Status'
    },
    'email.status.sent': {
        [LANGUAGES.ZH]: '已发送',
        [LANGUAGES.EN]: 'Sent'
    },
    'email.status.sending': {
        [LANGUAGES.ZH]: '发送中',
        [LANGUAGES.EN]: 'Sending'
    },
    'email.status.failed': {
        [LANGUAGES.ZH]: '发送失败',
        [LANGUAGES.EN]: 'Failed'
    },
    'email.action.details': {
        [LANGUAGES.ZH]: '详情',
        [LANGUAGES.EN]: 'Details'
    },
    'email.action.delete': {
        [LANGUAGES.ZH]: '删除',
        [LANGUAGES.EN]: 'Delete'
    },
    'email.no.data': {
        [LANGUAGES.ZH]: '暂无数据',
        [LANGUAGES.EN]: 'No Data Available'
    },
    'email.dialog.details': {
        [LANGUAGES.ZH]: '邮件详情',
        [LANGUAGES.EN]: 'Email Details'
    },
    'email.detail.sender': {
        [LANGUAGES.ZH]: '发件人',
        [LANGUAGES.EN]: 'Sender'
    },
    'email.detail.recipients': {
        [LANGUAGES.ZH]: '收件人',
        [LANGUAGES.EN]: 'Recipients'
    },
    'email.detail.no.recipients': {
        [LANGUAGES.ZH]: '无收件人',
        [LANGUAGES.EN]: 'No Recipients'
    },
    'email.detail.delivery': {
        [LANGUAGES.ZH]: '送达情况',
        [LANGUAGES.EN]: 'Delivery Status'
    },
    'email.detail.attachments': {
        [LANGUAGES.ZH]: '附件',
        [LANGUAGES.EN]: 'Attachments'
    },
    'email.detail.content': {
        [LANGUAGES.ZH]: '邮件内容',
        [LANGUAGES.EN]: 'Email Content'
    },

    // 发邮件表单相关
    'email.form.sender': {
        [LANGUAGES.ZH]: '发件人',
        [LANGUAGES.EN]: 'Sender'
    },
    'email.form.sender.placeholder': {
        [LANGUAGES.ZH]: '发件人邮箱',
        [LANGUAGES.EN]: 'Sender Email'
    },
    'email.form.use.default.email': {
        [LANGUAGES.ZH]: '使用系统默认邮箱',
        [LANGUAGES.EN]: 'Using system default email'
    },
    'email.form.recipients': {
        [LANGUAGES.ZH]: '收件人',
        [LANGUAGES.EN]: 'Recipients'
    },
    'email.form.recipients.placeholder': {
        [LANGUAGES.ZH]: '请输入收件人邮箱，多个邮箱请用分号(;)分隔',
        [LANGUAGES.EN]: 'Please enter recipient emails, separate multiple emails with semicolons (;)'
    },
    'email.form.reply.to': {
        [LANGUAGES.ZH]: '回邮地址',
        [LANGUAGES.EN]: 'Reply To'
    },
    'email.form.reply.to.placeholder': {
        [LANGUAGES.ZH]: '请输入回邮地址(用户接收客户回复信息)',
        [LANGUAGES.EN]: 'Please enter reply address (to receive customer replies)'
    },
    'email.form.subject': {
        [LANGUAGES.ZH]: '邮件主题',
        [LANGUAGES.EN]: 'Subject'
    },
    'email.form.subject.placeholder': {
        [LANGUAGES.ZH]: '请输入邮件主题',
        [LANGUAGES.EN]: 'Please enter email subject'
    },
    'email.form.send.time': {
        [LANGUAGES.ZH]: '发送时间',
        [LANGUAGES.EN]: 'Send Time'
    },
    'email.form.send.immediate': {
        [LANGUAGES.ZH]: '立即发送',
        [LANGUAGES.EN]: 'Send Immediately'
    },
    'email.form.send.scheduled': {
        [LANGUAGES.ZH]: '定时发送',
        [LANGUAGES.EN]: 'Schedule Send'
    },
    'email.form.select.time': {
        [LANGUAGES.ZH]: '选择发送时间',
        [LANGUAGES.EN]: 'Select send time'
    },
    'email.form.select.timezone': {
        [LANGUAGES.ZH]: '选择时区',
        [LANGUAGES.EN]: 'Select timezone'
    },
    'email.form.timezone.auto': {
        [LANGUAGES.ZH]: '自动匹配收件人时区',
        [LANGUAGES.EN]: 'Auto-match recipient timezone'
    },
    'email.form.timezone.beijing': {
        [LANGUAGES.ZH]: 'UTC+8 (北京时间)',
        [LANGUAGES.EN]: 'UTC+8 (Beijing Time)'
    },
    'email.form.timezone.gmt': {
        [LANGUAGES.ZH]: 'UTC+0 (格林威治时间)',
        [LANGUAGES.EN]: 'UTC+0 (Greenwich Mean Time)'
    },
    'email.form.timezone.est': {
        [LANGUAGES.ZH]: 'UTC-5 (美国东部时间)',
        [LANGUAGES.EN]: 'UTC-5 (Eastern Standard Time)'
    },
    'email.form.timezone.pst': {
        [LANGUAGES.ZH]: 'UTC-8 (美国西部时间)',
        [LANGUAGES.EN]: 'UTC-8 (Pacific Standard Time)'
    },
    'email.form.content': {
        [LANGUAGES.ZH]: '邮件内容',
        [LANGUAGES.EN]: 'Email Content'
    },
    'email.form.content.custom': {
        [LANGUAGES.ZH]: '自定义输入',
        [LANGUAGES.EN]: 'Custom Input'
    },
    'email.form.content.system': {
        [LANGUAGES.ZH]: '系统模板',
        [LANGUAGES.EN]: 'System Template'
    },
    'email.form.content.personal': {
        [LANGUAGES.ZH]: '个人模板',
        [LANGUAGES.EN]: 'Personal Template'
    },
    'email.form.select.template': {
        [LANGUAGES.ZH]: '请选择模板',
        [LANGUAGES.EN]: 'Please select template'
    },
    'email.form.manage.templates': {
        [LANGUAGES.ZH]: '管理模板',
        [LANGUAGES.EN]: 'Manage Templates'
    },
    'email.form.attachments': {
        [LANGUAGES.ZH]: '附件',
        [LANGUAGES.EN]: 'Attachments'
    },
    'email.form.add.attachment': {
        [LANGUAGES.ZH]: '添加附件',
        [LANGUAGES.EN]: 'Add Attachment'
    },
    'email.form.attachment.limit': {
        [LANGUAGES.ZH]: '单个文件不超过10MB，最多5个附件',
        [LANGUAGES.EN]: 'Single file not exceeding 10MB, maximum 5 attachments'
    },
    'email.form.reset': {
        [LANGUAGES.ZH]: '重置',
        [LANGUAGES.EN]: 'Reset'
    },
    'email.form.save.as.template': {
        [LANGUAGES.ZH]: '另存为模板',
        [LANGUAGES.EN]: 'Save as Template'
    },
    'email.form.send': {
        [LANGUAGES.ZH]: '发送',
        [LANGUAGES.EN]: 'Send'
    },

    // 邮件模板相关
    'email.template.system': {
        [LANGUAGES.ZH]: '系统模板',
        [LANGUAGES.EN]: 'System Templates'
    },
    'email.template.personal': {
        [LANGUAGES.ZH]: '自建模板',
        [LANGUAGES.EN]: 'Personal Templates'
    },
    'email.template.create': {
        [LANGUAGES.ZH]: '新建模板',
        [LANGUAGES.EN]: 'Create Template'
    },
    'email.template.delete': {
        [LANGUAGES.ZH]: '删除模板',
        [LANGUAGES.EN]: 'Delete Template'
    },
    'email.template.name': {
        [LANGUAGES.ZH]: '模板名称',
        [LANGUAGES.EN]: 'Template Name'
    },
    'email.template.subject': {
        [LANGUAGES.ZH]: '模板主题',
        [LANGUAGES.EN]: 'Template Subject'
    },
    'email.template.create.time': {
        [LANGUAGES.ZH]: '创建时间',
        [LANGUAGES.EN]: 'Creation Time'
    },
    'email.template.creator': {
        [LANGUAGES.ZH]: '创建用户',
        [LANGUAGES.EN]: 'Creator'
    },
    'email.template.actions': {
        [LANGUAGES.ZH]: '操作',
        [LANGUAGES.EN]: 'Actions'
    },
    'email.template.edit': {
        [LANGUAGES.ZH]: '编辑',
        [LANGUAGES.EN]: 'Edit'
    },
    'email.template.view': {
        [LANGUAGES.ZH]: '查看',
        [LANGUAGES.EN]: 'View'
    },
    'email.template.dialog.create': {
        [LANGUAGES.ZH]: '新建模板',
        [LANGUAGES.EN]: 'Create Template'
    },
    'email.template.dialog.edit': {
        [LANGUAGES.ZH]: '编辑模板',
        [LANGUAGES.EN]: 'Edit Template'
    },
    'email.template.dialog.view': {
        [LANGUAGES.ZH]: '查看模板',
        [LANGUAGES.EN]: 'View Template'
    },
    'email.template.form.name': {
        [LANGUAGES.ZH]: '模板名称',
        [LANGUAGES.EN]: 'Template Name'
    },
    'email.template.form.name.placeholder': {
        [LANGUAGES.ZH]: '请输入模板名称',
        [LANGUAGES.EN]: 'Please enter template name'
    },
    'email.template.form.subject': {
        [LANGUAGES.ZH]: '模板主题',
        [LANGUAGES.EN]: 'Template Subject'
    },
    'email.template.form.subject.placeholder': {
        [LANGUAGES.ZH]: '请输入邮件主题',
        [LANGUAGES.EN]: 'Please enter email subject'
    },
    'email.template.form.content': {
        [LANGUAGES.ZH]: '模板内容',
        [LANGUAGES.EN]: 'Template Content'
    },
    'email.template.form.content.placeholder': {
        [LANGUAGES.ZH]: '请输入邮件内容模板',
        [LANGUAGES.EN]: 'Please enter email content template'
    },
    'email.template.form.variables': {
        [LANGUAGES.ZH]: '变量说明',
        [LANGUAGES.EN]: 'Variable Instructions'
    },
    'email.template.form.available.variables': {
        [LANGUAGES.ZH]: '可用变量',
        [LANGUAGES.EN]: 'Available Variables'
    },
    'email.template.variable.name': {
        [LANGUAGES.ZH]: '收件人姓名',
        [LANGUAGES.EN]: 'Recipient Name'
    },
    'email.template.variable.company': {
        [LANGUAGES.ZH]: '收件人公司',
        [LANGUAGES.EN]: 'Recipient Company'
    },
    'email.template.variable.date': {
        [LANGUAGES.ZH]: '当前日期',
        [LANGUAGES.EN]: 'Current Date'
    },
    'email.template.variable.time': {
        [LANGUAGES.ZH]: '当前时间',
        [LANGUAGES.EN]: 'Current Time'
    },
    'email.template.close': {
        [LANGUAGES.ZH]: '关闭',
        [LANGUAGES.EN]: 'Close'
    },
    'email.template.cancel': {
        [LANGUAGES.ZH]: '取消',
        [LANGUAGES.EN]: 'Cancel'
    },
    'email.template.save': {
        [LANGUAGES.ZH]: '保存',
        [LANGUAGES.EN]: 'Save'
    },

    // 域名配置相关
    'email.domain.config': {
        [LANGUAGES.ZH]: '发件域名配置',
        [LANGUAGES.EN]: 'Sender Domain Configuration'
    },
    'email.domain.add': {
        [LANGUAGES.ZH]: '添加新的域名',
        [LANGUAGES.EN]: 'Add New Domain'
    },
    'email.domain.name': {
        [LANGUAGES.ZH]: '域名',
        [LANGUAGES.EN]: 'Domain'
    },
    'email.domain.status': {
        [LANGUAGES.ZH]: '状态',
        [LANGUAGES.EN]: 'Status'
    },
    'email.domain.add.time': {
        [LANGUAGES.ZH]: '添加时间',
        [LANGUAGES.EN]: 'Add Time'
    },
    'email.domain.actions': {
        [LANGUAGES.ZH]: '操作',
        [LANGUAGES.EN]: 'Actions'
    },
    'email.domain.verify': {
        [LANGUAGES.ZH]: '验证',
        [LANGUAGES.EN]: 'Verify'
    },
    'email.domain.reverify': {
        [LANGUAGES.ZH]: '重新验证',
        [LANGUAGES.EN]: 'Reverify'
    },
    'email.domain.set.default': {
        [LANGUAGES.ZH]: '设为默认',
        [LANGUAGES.EN]: 'Set as Default'
    },
    'email.domain.already.default': {
        [LANGUAGES.ZH]: '默认域名',
        [LANGUAGES.EN]: 'Default Domain'
    },
    'email.domain.delete': {
        [LANGUAGES.ZH]: '删除',
        [LANGUAGES.EN]: 'Delete'
    },
    'email.domain.no.data': {
        [LANGUAGES.ZH]: '暂无域名配置',
        [LANGUAGES.EN]: 'No Domain Configuration'
    },
    'email.domain.add.first': {
        [LANGUAGES.ZH]: '添加第一个域名',
        [LANGUAGES.EN]: 'Add First Domain'
    },
    'email.domain.dialog.add': {
        [LANGUAGES.ZH]: '添加新域名',
        [LANGUAGES.EN]: 'Add New Domain'
    },
    'email.domain.form.name': {
        [LANGUAGES.ZH]: '域名名称',
        [LANGUAGES.EN]: 'Domain Name'
    },
    'email.domain.form.name.placeholder': {
        [LANGUAGES.ZH]: '请输入域名，例如：example.com',
        [LANGUAGES.EN]: 'Please enter domain, e.g.: example.com'
    },
    'email.domain.form.name.tip': {
        [LANGUAGES.ZH]: '只需要输入域名本身，无需包含http://或www',
        [LANGUAGES.EN]: 'Only enter the domain itself, no need to include http:// or www'
    },
    'email.domain.cancel': {
        [LANGUAGES.ZH]: '取消',
        [LANGUAGES.EN]: 'Cancel'
    },
    'email.domain.confirm': {
        [LANGUAGES.ZH]: '确认',
        [LANGUAGES.EN]: 'Confirm'
    },
    'email.domain.dialog.verify': {
        [LANGUAGES.ZH]: '域名验证',
        [LANGUAGES.EN]: 'Domain Verification'
    },
    'email.domain.verify.instructions': {
        [LANGUAGES.ZH]: '域名验证说明',
        [LANGUAGES.EN]: 'Domain Verification Instructions'
    },
    'email.domain.verify.description': {
        [LANGUAGES.ZH]: '请按照以下步骤验证您对此域名的所有权',
        [LANGUAGES.EN]: 'Please follow these steps to verify your ownership of this domain'
    },
    'email.domain.verify.dns': {
        [LANGUAGES.ZH]: 'DNS验证方式',
        [LANGUAGES.EN]: 'DNS Verification Method'
    },
    'email.domain.verify.add.records': {
        [LANGUAGES.ZH]: '请在您的域名DNS管理后台添加以下记录',
        [LANGUAGES.EN]: 'Please add the following records in your domain DNS management panel'
    },
    'email.domain.record.type': {
        [LANGUAGES.ZH]: '记录类型',
        [LANGUAGES.EN]: 'Record Type'
    },
    'email.domain.record.host': {
        [LANGUAGES.ZH]: '主机记录',
        [LANGUAGES.EN]: 'Host'
    },
    'email.domain.record.value': {
        [LANGUAGES.ZH]: '记录值',
        [LANGUAGES.EN]: 'Value'
    },
    'email.domain.copy': {
        [LANGUAGES.ZH]: '复制',
        [LANGUAGES.EN]: 'Copy'
    },
    'email.domain.later': {
        [LANGUAGES.ZH]: '稍后验证',
        [LANGUAGES.EN]: 'Verify Later'
    },
    'email.domain.check.now': {
        [LANGUAGES.ZH]: '立即验证',
        [LANGUAGES.EN]: 'Verify Now'
    },
    'email.domain.status.verified': {
        [LANGUAGES.ZH]: '已验证',
        [LANGUAGES.EN]: 'Verified'
    },
    'email.domain.status.pending': {
        [LANGUAGES.ZH]: '待验证',
        [LANGUAGES.EN]: 'Pending'
    },
    'email.domain.status.failed': {
        [LANGUAGES.ZH]: '验证失败',
        [LANGUAGES.EN]: 'Failed'
    },
    'email.domain.error.empty': {
        [LANGUAGES.ZH]: '请输入域名',
        [LANGUAGES.EN]: 'Please enter a domain'
    },
    'email.domain.error.format': {
        [LANGUAGES.ZH]: '请输入有效的域名格式',
        [LANGUAGES.EN]: 'Please enter a valid domain format'
    },
    'email.domain.error.exists': {
        [LANGUAGES.ZH]: '此域名已存在',
        [LANGUAGES.EN]: 'This domain already exists'
    },
    'email.domain.error.not.verified': {
        [LANGUAGES.ZH]: '只有已验证的域名才能设为默认',
        [LANGUAGES.EN]: 'Only verified domains can be set as default'
    },
    'email.domain.error.delete.default': {
        [LANGUAGES.ZH]: '不能删除默认域名',
        [LANGUAGES.EN]: 'Cannot delete the default domain'
    },
    'email.domain.add.success': {
        [LANGUAGES.ZH]: '域名添加成功',
        [LANGUAGES.EN]: 'Domain added successfully'
    },
    'email.domain.verify.success': {
        [LANGUAGES.ZH]: '域名验证成功',
        [LANGUAGES.EN]: 'Domain verified successfully'
    },
    'email.domain.default.success': {
        [LANGUAGES.ZH]: '已设为默认域名',
        [LANGUAGES.EN]: 'Set as default domain successfully'
    },
    'email.domain.delete.confirm': {
        [LANGUAGES.ZH]: '确定要删除该域名吗？',
        [LANGUAGES.EN]: 'Are you sure you want to delete this domain?'
    },
    'email.domain.delete.title': {
        [LANGUAGES.ZH]: '删除确认',
        [LANGUAGES.EN]: 'Delete Confirmation'
    },
    'email.domain.delete.yes': {
        [LANGUAGES.ZH]: '确定',
        [LANGUAGES.EN]: 'Yes'
    },
    'email.domain.delete.no': {
        [LANGUAGES.ZH]: '取消',
        [LANGUAGES.EN]: 'No'
    },
    'email.domain.delete.success': {
        [LANGUAGES.ZH]: '域名删除成功',
        [LANGUAGES.EN]: 'Domain deleted successfully'
    },
    'email.domain.copy.success': {
        [LANGUAGES.ZH]: '已复制到剪贴板',
        [LANGUAGES.EN]: 'Copied to clipboard'
    },
    'email.domain.copy.failed': {
        [LANGUAGES.ZH]: '复制失败',
        [LANGUAGES.EN]: 'Copy failed'
    },

    // Facebook筛选栏翻译键
    'facebook.filter.reset': {
        [LANGUAGES.ZH]: '重置筛选条件',
        [LANGUAGES.EN]: 'Reset Filters'
    },
    'facebook.gender.male': {
        [LANGUAGES.ZH]: '男',
        [LANGUAGES.EN]: 'Male'
    },
    'facebook.gender.female': {
        [LANGUAGES.ZH]: '女',
        [LANGUAGES.EN]: 'Female'
    },
    'facebook.filter.searchRelationship': {
        [LANGUAGES.ZH]: '搜索关系状态',
        [LANGUAGES.EN]: 'Search Relationship Status'
    },
    'facebook.filter.searchWorkplace': {
        [LANGUAGES.ZH]: '搜索工作地点',
        [LANGUAGES.EN]: 'Search Workplace'
    },

    // 工作台页面翻译键
    'dashboard.export.search.stats': {
        [LANGUAGES.ZH]: '导出记录条数 / 搜索记录条数',
        [LANGUAGES.EN]: 'Export Records / Search Records'
    },
    'dashboard.search.count': {
        [LANGUAGES.ZH]: '搜索记录条数',
        [LANGUAGES.EN]: 'Search Records Count'
    },
    'dashboard.export.count': {
        [LANGUAGES.ZH]: '导出记录条数',
        [LANGUAGES.EN]: 'Export Records Count'
    },
    'dashboard.items': {
        [LANGUAGES.ZH]: '条',
        [LANGUAGES.EN]: ' items'
    },
    'dashboard.leads': {
        [LANGUAGES.ZH]: '获客',
        [LANGUAGES.EN]: 'Leads'
    },
    'dashboard.load.stats.failed': {
        [LANGUAGES.ZH]: '加载工作台统计数据失败',
        [LANGUAGES.EN]: 'Failed to load dashboard statistics'
    },
    'dashboard.customsData': {
        [LANGUAGES.ZH]: '海关数据',
        [LANGUAGES.EN]: 'Customs Data'
    },
    'dashboard.enterprise': {
        [LANGUAGES.ZH]: '全球企业库',
        [LANGUAGES.EN]: 'Global Enterprise Database'
    },
    'dashboard.global': {
        [LANGUAGES.ZH]: '全球企业',
        [LANGUAGES.EN]: 'GlobalEnterprise'
    },
    'dashboard.enterprises': {
        [LANGUAGES.ZH]: '库',
        [LANGUAGES.EN]: 'Database'
    },
    'dashboard.canton': {
        [LANGUAGES.ZH]: '广交会',
        [LANGUAGES.EN]: 'Canton'
    },
    'dashboard.fairBuyers': {
        [LANGUAGES.ZH]: '采购商',
        [LANGUAGES.EN]: 'Fair Buyers'
    },
    'dashboard.cantonFairExhibitors': {
        [LANGUAGES.ZH]: '广交会参展商',
        [LANGUAGES.EN]: 'Canton Fair Exhibitors'
    },
    'dashboard.fairExhibitors': {
        [LANGUAGES.ZH]: '参展商',
        [LANGUAGES.EN]: 'Fair Exhibitors'
    },

    // 个人中心页面翻译键
    'personalCenter.myAccount': {
        [LANGUAGES.ZH]: '我的账户',
        [LANGUAGES.EN]: 'My Account'
    },
    'personalCenter.memberCenter': {
        [LANGUAGES.ZH]: '会员中心',
        [LANGUAGES.EN]: 'Membership Center'
    },
    'personalCenter.profileSettings': {
        [LANGUAGES.ZH]: '资料设置',
        [LANGUAGES.EN]: 'Profile Settings'
    },
    'personalCenter.promotion': {
        [LANGUAGES.ZH]: '推广返佣',
        [LANGUAGES.EN]: 'Promotion & Rewards'
    },
    'personalCenter.functionService': {
        [LANGUAGES.ZH]: '功能服务',
        [LANGUAGES.EN]: 'Services'
    },
    'personalCenter.needLogin': {
        [LANGUAGES.ZH]: '需要登录',
        [LANGUAGES.EN]: 'Login Required'
    },
    'personalCenter.pleaseLoginFirst': {
        [LANGUAGES.ZH]: '请先登录后查看账户信息',
        [LANGUAGES.EN]: 'Please login to view your account information'
    },
    'personalCenter.pleaseLoginToEditProfile': {
        [LANGUAGES.ZH]: '请先登录后修改资料',
        [LANGUAGES.EN]: 'Please login to edit your profile'
    },
    'personalCenter.goToLogin': {
        [LANGUAGES.ZH]: '去登录',
        [LANGUAGES.EN]: 'Go to Login'
    },
    'personalCenter.refresh': {
        [LANGUAGES.ZH]: '刷新',
        [LANGUAGES.EN]: 'Refresh'
    },

    // 个人中心 - 资料设置
    'personalCenter.userInfo': {
        [LANGUAGES.ZH]: '个人资料',
        [LANGUAGES.EN]: 'Personal Information'
    },
    'personalCenter.security': {
        [LANGUAGES.ZH]: '安全设置',
        [LANGUAGES.EN]: 'Security Settings'
    },
    'personalCenter.userInfoDesc': {
        [LANGUAGES.ZH]: '您可以在这里修改个人基本信息',
        [LANGUAGES.EN]: 'You can modify your basic information here'
    },
    'personalCenter.securityDesc': {
        [LANGUAGES.ZH]: '管理您的账户安全信息',
        [LANGUAGES.EN]: 'Manage your account security information'
    },
    'personalCenter.username': {
        [LANGUAGES.ZH]: '用户名',
        [LANGUAGES.EN]: 'Username'
    },
    'personalCenter.phoneNumber': {
        [LANGUAGES.ZH]: '手机号码',
        [LANGUAGES.EN]: 'Phone Number'
    },
    'personalCenter.enterUsername': {
        [LANGUAGES.ZH]: '请输入用户名',
        [LANGUAGES.EN]: 'Please enter username'
    },
    'personalCenter.enterPhoneNumber': {
        [LANGUAGES.ZH]: '请输入手机号码',
        [LANGUAGES.EN]: 'Please enter phone number'
    },
    'personalCenter.saveChanges': {
        [LANGUAGES.ZH]: '保存修改',
        [LANGUAGES.EN]: 'Save Changes'
    },
    'personalCenter.reset': {
        [LANGUAGES.ZH]: '重置',
        [LANGUAGES.EN]: 'Reset'
    },
    'personalCenter.passwordChange': {
        [LANGUAGES.ZH]: '密码修改',
        [LANGUAGES.EN]: 'Change Password'
    },
    'personalCenter.passwordChangeDesc': {
        [LANGUAGES.ZH]: '定期更换密码可以提高账户安全性',
        [LANGUAGES.EN]: 'Regularly changing your password improves account security'
    },
    'personalCenter.currentPassword': {
        [LANGUAGES.ZH]: '当前密码',
        [LANGUAGES.EN]: 'Current Password'
    },
    'personalCenter.newPassword': {
        [LANGUAGES.ZH]: '新密码',
        [LANGUAGES.EN]: 'New Password'
    },
    'personalCenter.confirmPassword': {
        [LANGUAGES.ZH]: '确认新密码',
        [LANGUAGES.EN]: 'Confirm New Password'
    },
    'personalCenter.enterCurrentPassword': {
        [LANGUAGES.ZH]: '请输入当前密码',
        [LANGUAGES.EN]: 'Please enter current password'
    },
    'personalCenter.enterNewPassword': {
        [LANGUAGES.ZH]: '请输入新密码',
        [LANGUAGES.EN]: 'Please enter new password'
    },
    'personalCenter.confirmNewPassword': {
        [LANGUAGES.ZH]: '请再次输入新密码',
        [LANGUAGES.EN]: 'Please confirm new password'
    },
    'personalCenter.phoneBinding': {
        [LANGUAGES.ZH]: '手机绑定',
        [LANGUAGES.EN]: 'Phone Binding'
    },
    'personalCenter.phoneBindingDesc': {
        [LANGUAGES.ZH]: '绑定手机可以提高账户安全性，并用于接收重要通知',
        [LANGUAGES.EN]: 'Binding your phone enhances account security and allows you to receive important notifications'
    },
    'personalCenter.boundPhone': {
        [LANGUAGES.ZH]: '已绑定手机',
        [LANGUAGES.EN]: 'Bound Phone'
    },
    'personalCenter.confirmChange': {
        [LANGUAGES.ZH]: '确认修改',
        [LANGUAGES.EN]: 'Confirm Changes'
    },

    // 个人中心 - 我的账户
    'personalCenter.userBalanceInfo': {
        [LANGUAGES.ZH]: '用户余额信息',
        [LANGUAGES.EN]: 'User Balance Information'
    },
    'personalCenter.memberLevel': {
        [LANGUAGES.ZH]: '会员等级',
        [LANGUAGES.EN]: 'Membership Level'
    },
    'personalCenter.remainingExportRecords': {
        [LANGUAGES.ZH]: '剩余可导出记录数',
        [LANGUAGES.EN]: 'Remaining Export Records'
    },
    'personalCenter.remainingViewRecords': {
        [LANGUAGES.ZH]: '剩余可查看记录数',
        [LANGUAGES.EN]: 'Remaining View Records'
    },
    'personalCenter.remainingLookupRecords': {
        [LANGUAGES.ZH]: '剩余可反查记录数',
        [LANGUAGES.EN]: 'Remaining Lookup Records'
    },
    'personalCenter.remainingEmailSend': {
        [LANGUAGES.ZH]: '剩余可发送邮件数',
        [LANGUAGES.EN]: 'Remaining Email Send Count'
    },
    'personalCenter.emailPackageLevel': {
        [LANGUAGES.ZH]: '邮件套餐等级',
        [LANGUAGES.EN]: 'Email Package Level'
    },
    'personalCenter.vipExpireDate': {
        [LANGUAGES.ZH]: 'VIP到期时间',
        [LANGUAGES.EN]: 'VIP Expiry Date'
    },
    'personalCenter.updateTime': {
        [LANGUAGES.ZH]: '更新时间',
        [LANGUAGES.EN]: 'Update Time'
    },
    'personalCenter.rechargeRecords': {
        [LANGUAGES.ZH]: '充值记录',
        [LANGUAGES.EN]: 'Recharge Records'
    },
    'personalCenter.all': {
        [LANGUAGES.ZH]: '全部',
        [LANGUAGES.EN]: 'All'
    },
    'personalCenter.customRecharge': {
        [LANGUAGES.ZH]: '自定义充值',
        [LANGUAGES.EN]: 'Custom Recharge'
    },
    'personalCenter.vipPackage': {
        [LANGUAGES.ZH]: 'VIP套餐',
        [LANGUAGES.EN]: 'VIP Package'
    },
    'personalCenter.emailPackage': {
        [LANGUAGES.ZH]: '邮件套餐',
        [LANGUAGES.EN]: 'Email Package'
    },
    'personalCenter.totalRecords': {
        [LANGUAGES.ZH]: '共 {count} 条记录',
        [LANGUAGES.EN]: 'Total {count} Records'
    },
    'personalCenter.id': {
        [LANGUAGES.ZH]: 'ID',
        [LANGUAGES.EN]: 'ID'
    },
    'personalCenter.phone': {
        [LANGUAGES.ZH]: '手机号',
        [LANGUAGES.EN]: 'Phone'
    },
    'personalCenter.type': {
        [LANGUAGES.ZH]: '类型',
        [LANGUAGES.EN]: 'Type'
    },
    'personalCenter.packageId': {
        [LANGUAGES.ZH]: '套餐ID',
        [LANGUAGES.EN]: 'Package ID'
    },
    'personalCenter.amount': {
        [LANGUAGES.ZH]: '金额(元)',
        [LANGUAGES.EN]: 'Amount (Yuan)'
    },
    'personalCenter.quotaAdded': {
        [LANGUAGES.ZH]: '增加额度',
        [LANGUAGES.EN]: 'Quota Added'
    },
    'personalCenter.description': {
        [LANGUAGES.ZH]: '描述',
        [LANGUAGES.EN]: 'Description'
    },
    'personalCenter.orderNo': {
        [LANGUAGES.ZH]: '订单号',
        [LANGUAGES.EN]: 'Order No.'
    },
    'personalCenter.paymentMethod': {
        [LANGUAGES.ZH]: '支付方式',
        [LANGUAGES.EN]: 'Payment Method'
    },
    'personalCenter.status': {
        [LANGUAGES.ZH]: '状态',
        [LANGUAGES.EN]: 'Status'
    },
    'personalCenter.creationTime': {
        [LANGUAGES.ZH]: '创建时间',
        [LANGUAGES.EN]: 'Creation Time'
    },
    'personalCenter.noRechargeRecords': {
        [LANGUAGES.ZH]: '暂无充值记录',
        [LANGUAGES.EN]: 'No Recharge Records'
    },
    'personalCenter.rechargeShowHere': {
        [LANGUAGES.ZH]: '账户充值后会显示在这里',
        [LANGUAGES.EN]: 'Account recharges will be displayed here'
    },

    // 个人中心 - 会员中心
    'personalCenter.memberVersion': {
        [LANGUAGES.ZH]: '会员版本',
        [LANGUAGES.EN]: 'Membership Version'
    },
    'personalCenter.memberPrivilegeDesc': {
        [LANGUAGES.ZH]: '会员特权说明：',
        [LANGUAGES.EN]: 'Membership Privileges:'
    },
    'personalCenter.privilegeAutoAdd': {
        [LANGUAGES.ZH]: '购买的会员权益自动叠加，额度立即生效',
        [LANGUAGES.EN]: 'Purchased membership benefits are automatically stacked, with quotas taking effect immediately'
    },
    'personalCenter.validityPeriod': {
        [LANGUAGES.ZH]: '会员有效期按最长剩余时长计算（例如：原剩余30天，新购90天，最终为90天）',
        [LANGUAGES.EN]: 'Membership validity is calculated based on the longest remaining duration (e.g.: original 30 days + new 90 days = final 90 days)'
    },
    'personalCenter.upgradeBenefits': {
        [LANGUAGES.ZH]: '会员等级升级后，可享受更多高级功能和更大配额',
        [LANGUAGES.EN]: 'After upgrading your membership level, you can enjoy more advanced features and larger quotas'
    },
    'personalCenter.quotaContinuedUse': {
        [LANGUAGES.ZH]: '所有查询和导出次数在会员有效期内可持续使用',
        [LANGUAGES.EN]: 'All query and export counts can be used continuously during the membership period'
    },
    'personalCenter.validUntil': {
        [LANGUAGES.ZH]: '有效期至',
        [LANGUAGES.EN]: 'Valid until'
    },
    'personalCenter.remainingDays': {
        [LANGUAGES.ZH]: '剩余 {days} 天',
        [LANGUAGES.EN]: '{days} days remaining'
    },
    'personalCenter.currentPlan': {
        [LANGUAGES.ZH]: '当前方案',
        [LANGUAGES.EN]: 'Current Plan'
    },
    'personalCenter.currentMember': {
        [LANGUAGES.ZH]: '当前会员',
        [LANGUAGES.EN]: 'Current Member'
    },
    'personalCenter.upgrade': {
        [LANGUAGES.ZH]: '立即升级',
        [LANGUAGES.EN]: 'Upgrade Now'
    },
    'personalCenter.downgrade': {
        [LANGUAGES.ZH]: '降级',
        [LANGUAGES.EN]: 'Downgrade'
    },
    'personalCenter.includedFeatures': {
        [LANGUAGES.ZH]: '包含功能:',
        [LANGUAGES.EN]: 'Included Features:'
    },
    'personalCenter.basicQuery': {
        [LANGUAGES.ZH]: '基础查询',
        [LANGUAGES.EN]: 'Basic Query'
    },
    'personalCenter.allData': {
        [LANGUAGES.ZH]: '全部数据',
        [LANGUAGES.EN]: 'All Data'
    },
    'personalCenter.viewData': {
        [LANGUAGES.ZH]: '查看数据({count}条)',
        [LANGUAGES.EN]: 'View Data ({count} bars)'
    },
    'personalCenter.exportData': {
        [LANGUAGES.ZH]: '导出数据',
        [LANGUAGES.EN]: 'Export Data'
    },
    'personalCenter.exportTimes': {
        [LANGUAGES.ZH]: '导出({count}条)',
        [LANGUAGES.EN]: 'Export ({count} bars)'
    },
    'personalCenter.lookupFeature': {
        [LANGUAGES.ZH]: '反查功能',
        [LANGUAGES.EN]: 'Lookup Feature'
    },
    'personalCenter.lookupTimes': {
        [LANGUAGES.ZH]: '反查({count}条)',
        [LANGUAGES.EN]: 'Lookup ({count} bars)'
    },
    'personalCenter.prioritySupport': {
        [LANGUAGES.ZH]: '优先技术支持',
        [LANGUAGES.EN]: 'Priority Technical Support'
    },
    'personalCenter.standardSupport': {
        [LANGUAGES.ZH]: '标准技术支持',
        [LANGUAGES.EN]: 'Standard Support'
    },

    // 通用状态和操作
    'common.success': {
        [LANGUAGES.ZH]: '成功',
        [LANGUAGES.EN]: 'Success'
    },
    'common.pending': {
        [LANGUAGES.ZH]: '处理中',
        [LANGUAGES.EN]: 'Pending'
    },
    'common.failed': {
        [LANGUAGES.ZH]: '失败',
        [LANGUAGES.EN]: 'Failed'
    },
    'common.close': {
        [LANGUAGES.ZH]: '关闭',
        [LANGUAGES.EN]: 'Close'
    },

    // ... existing translations ...
    'personalCenter.months': {
        [LANGUAGES.ZH]: '个月',
        [LANGUAGES.EN]: 'months'
    },
    'personalCenter.accountRecharge': {
        [LANGUAGES.ZH]: '账户充值',
        [LANGUAGES.EN]: 'Account Recharge'
    },
    'personalCenter.rechargeAmount': {
        [LANGUAGES.ZH]: '充值金额',
        [LANGUAGES.EN]: 'Recharge Amount'
    },
    'personalCenter.rechargeNow': {
        [LANGUAGES.ZH]: '立即充值',
        [LANGUAGES.EN]: 'Recharge Now'
    },

    // 个人中心 - 推广返佣标签页
    'personalCenter.promotionRebate': {
        [LANGUAGES.ZH]: '推广返佣',
        [LANGUAGES.EN]: 'Promotion Rebate'
    },
    'personalCenter.customerService': {
        [LANGUAGES.ZH]: '专属客服',
        [LANGUAGES.EN]: 'Customer Service'
    },
    'personalCenter.promotionSystem': {
        [LANGUAGES.ZH]: '推广返佣系统',
        [LANGUAGES.EN]: 'Promotion & Rebate System'
    },
    'personalCenter.promotionSystemDesc': {
        [LANGUAGES.ZH]: '通过您的专属链接推广，获得订单金额30%的佣金奖励',
        [LANGUAGES.EN]: 'Promote via your unique link and earn 30% commission on order amounts'
    },
    'personalCenter.promotionShareDesc': {
        [LANGUAGES.ZH]: '通过您在推广系统分享的筛查系统链接注册，您将获得新用户订单金额的 30% 作为奖励。',
        [LANGUAGES.EN]: 'By sharing your screening system link, you will receive 30% of new users’ order amounts as rewards.'
    },
    'personalCenter.promotionLink': {
        [LANGUAGES.ZH]: '推广系统链接',
        [LANGUAGES.EN]: 'Promotion Link'
    },
    'personalCenter.copy': {
        [LANGUAGES.ZH]: '复制',
        [LANGUAGES.EN]: 'Copy'
    },
    'personalCenter.copy.success': {
        [LANGUAGES.ZH]: '复制成功',
        [LANGUAGES.EN]: 'Copied'
    },
    'personalCenter.scanQrcode': {
        [LANGUAGES.ZH]: '扫描二维码联系我们的专属客服',
        [LANGUAGES.EN]: 'Scan the QR code to contact our dedicated customer service'
    },
    'personalCenter.wechatId': {
        [LANGUAGES.ZH]: '微信号',
        [LANGUAGES.EN]: 'WeChat ID'
    },
    'personalCenter.noCustomerService': {
        [LANGUAGES.ZH]: '暂无客服信息',
        [LANGUAGES.EN]: 'No customer service information'
    },

    // 个人中心 - 功能服务标签页（邮件套餐）
    'personalCenter.servicesTitle': {
        [LANGUAGES.ZH]: '功能服务',
        [LANGUAGES.EN]: 'Services'
    },
    'personalCenter.emailPackagesTitle': {
        [LANGUAGES.ZH]: '邮件套餐',
        [LANGUAGES.EN]: 'Email Packages'
    },
    'personalCenter.refreshPackages': {
        [LANGUAGES.ZH]: '刷新套餐',
        [LANGUAGES.EN]: 'Refresh Packages'
    },
    'personalCenter.packageDescriptionTitle': {
        [LANGUAGES.ZH]: '套餐说明：',
        [LANGUAGES.EN]: 'Package Notes:'
    },
    'personalCenter.packageIntro1': {
        [LANGUAGES.ZH]: '购买的邮件额度会自动叠加到您的账户',
        [LANGUAGES.EN]: 'Purchased email credits will be automatically stacked to your account'
    },
    'personalCenter.packageIntro2': {
        [LANGUAGES.ZH]: '套餐有效期按最长剩余时长计算（例如：原剩余30天，新购90天，最终为90天）',
        [LANGUAGES.EN]: 'Package validity is based on the longest remaining duration (e.g., originally 30 days + new 90 days = 90 days)'
    },
    'personalCenter.packageIntro3': {
        [LANGUAGES.ZH]: '支持HTML邮件、附件和模板设计，提供详细的发送报告',
        [LANGUAGES.EN]: 'Supports HTML emails, attachments, and template design with detailed sending reports'
    },
    'personalCenter.packageIntro4': {
        [LANGUAGES.ZH]: '未使用完的邮件额度在有效期内可继续使用',
        [LANGUAGES.EN]: 'Unused email credits can continue to be used within the validity period'
    },
    'personalCenter.packageLabel': {
        [LANGUAGES.ZH]: '套餐{label}',
        [LANGUAGES.EN]: 'Package {label}'
    },
    'personalCenter.packagePricePerUse': {
        [LANGUAGES.ZH]: '¥ {price}/次',
        [LANGUAGES.EN]: '¥ {price}/use'
    },
    'personalCenter.packageEmails': {
        [LANGUAGES.ZH]: '{count}封邮件',
        [LANGUAGES.EN]: '{count} emails'
    },
    'personalCenter.packageValidDays': {
        [LANGUAGES.ZH]: '有效期: {days}天',
        [LANGUAGES.EN]: 'Validity: {days} days'
    },
    'personalCenter.packageValidForever': {
        [LANGUAGES.ZH]: '永久有效',
        [LANGUAGES.EN]: 'Valid Forever'
    },
    'personalCenter.professionalEmailService': {
        [LANGUAGES.ZH]: '专业邮件服务',
        [LANGUAGES.EN]: 'Professional Email Service'
    },
    'personalCenter.buyNow': {
        [LANGUAGES.ZH]: '立即购买',
        [LANGUAGES.EN]: 'Buy Now'
    },
    'personalCenter.noPackages': {
        [LANGUAGES.ZH]: '暂无可用套餐',
        [LANGUAGES.EN]: 'No packages available'
    },
    'personalCenter.noPackagesDesc': {
        [LANGUAGES.ZH]: '暂无可用邮件套餐，请稍后再试',
        [LANGUAGES.EN]: 'No email packages available. Please try again later.'
    },
    'personalCenter.refreshPackagesBtn': {
        [LANGUAGES.ZH]: '刷新套餐',
        [LANGUAGES.EN]: 'Refresh Packages'
    },

    // 个人中心 - 购买与支付对话框
    'personalCenter.buyExportTitle': {
        [LANGUAGES.ZH]: '购买导出次数',
        [LANGUAGES.EN]: 'Buy Export Quota'
    },
    'personalCenter.buySearchTitle': {
        [LANGUAGES.ZH]: '购买查看更多次数',
        [LANGUAGES.EN]: 'Buy View More Quota'
    },
    'personalCenter.buyQuantity': {
        [LANGUAGES.ZH]: '购买数量',
        [LANGUAGES.EN]: 'Quantity'
    },
    'personalCenter.unitPrice': {
        [LANGUAGES.ZH]: '单价',
        [LANGUAGES.EN]: 'Unit Price'
    },
    'personalCenter.unitPrice.yuan': {
        [LANGUAGES.ZH]: '元',
        [LANGUAGES.EN]: 'yuan'
    },
    'personalCenter.unitPrice.unit': {
        [LANGUAGES.ZH]: '元/次',
        [LANGUAGES.EN]: 'yuan/use'
    },
    'personalCenter.totalPrice': {
        [LANGUAGES.ZH]: '总价',
        [LANGUAGES.EN]: 'Total'
    },
    'personalCenter.confirmPurchase': {
        [LANGUAGES.ZH]: '确认购买',
        [LANGUAGES.EN]: 'Confirm Purchase'
    },
    'personalCenter.payTitle': {
        [LANGUAGES.ZH]: '支付',
        [LANGUAGES.EN]: 'Payment'
    },
    'personalCenter.confirmPay': {
        [LANGUAGES.ZH]: '确认支付',
        [LANGUAGES.EN]: 'Confirm Payment'
    },
    'personalCenter.paymentQrTitle': {
        [LANGUAGES.ZH]: '支付二维码',
        [LANGUAGES.EN]: 'Payment QR Code'
    },
    'canton.purchaser.purchaseList': {
        [LANGUAGES.ZH]: '采购清单',
        [LANGUAGES.EN]: 'Purchase List'
    },
    'canton.purchaser.demandInfo': {
        [LANGUAGES.ZH]: '需求信息',
        [LANGUAGES.EN]: 'Demand Info'
    },
    'canton.exhibitor.establish.recentExhibitionHistory': {
        [LANGUAGES.ZH]: '参展历史',
        [LANGUAGES.EN]: 'Recent Exhibition History'
    },
    'canton.exhibitor.establish.registeredCapital': {
        [LANGUAGES.ZH]: '注册资金',
        [LANGUAGES.EN]: 'Registered Capital'
    },
    'canton.exhibitor.establish.city': {
        [LANGUAGES.ZH]: '城市',
        [LANGUAGES.EN]: 'City'
    },
    'canton.exhibitor.establish.province': {
        [LANGUAGES.ZH]: '省份',
        [LANGUAGES.EN]: 'Province'
    },
    'canton.exhibitor.establish.postalCode': {
        [LANGUAGES.ZH]: '邮编',
        [LANGUAGES.EN]: 'Postal Code'
    },
    'canton.exhibitor.establish.address': {
        [LANGUAGES.ZH]: '地址',
        [LANGUAGES.EN]: 'Address'
    },
    'canton.exhibitor.establish.website': {
        [LANGUAGES.ZH]: '网站',
        [LANGUAGES.EN]: 'Website'
    },
    'canton.exhibitor.establish.email': {
        [LANGUAGES.ZH]: '邮箱',
        [LANGUAGES.EN]: 'Email'
    },
    'canton.exhibitor.establish.fax': {
        [LANGUAGES.ZH]: '传真',
        [LANGUAGES.EN]: 'Fax'
    },
    'canton.exhibitor.principalPhone': {
        [LANGUAGES.ZH]: '负责人电话',
        [LANGUAGES.EN]: 'Principal Phone'
    },
    'canton.exhibitor.principalMobile': {
        [LANGUAGES.ZH]: '负责人手机号',
        [LANGUAGES.EN]: 'Principal Mobile'
    },
    'canton.exhibitor.contact.principalName': {
        [LANGUAGES.ZH]: '负责人',
        [LANGUAGES.EN]: 'Principal Name'
    },
    'canton.exhibitor.contact.principalPosition': {
        [LANGUAGES.ZH]: '负责人职位',
        [LANGUAGES.EN]: 'Principal Position'
    },
    'canton.exhibitor.companyProfile': {
        [LANGUAGES.ZH]: '简介',
        [LANGUAGES.EN]: 'Profile'
    },
    'canton.exhibitor.booth.period': {
        [LANGUAGES.ZH]: '期数',
        [LANGUAGES.EN]: 'Period'
    },
    'canton.exhibitor.category': {
        [LANGUAGES.ZH]: '行业',
        [LANGUAGES.EN]: 'Category'
    },
    'canton.exhibitor.establishmentYear': {
        [LANGUAGES.ZH]: '成立年份',
        [LANGUAGES.EN]: 'Establishment Year'
    },

    // 国家名称翻译
    'country.USA': {
        [LANGUAGES.ZH]: '美国',
        [LANGUAGES.EN]: 'USA'
    },
    'country.China': {
        [LANGUAGES.ZH]: '中国',
        [LANGUAGES.EN]: 'China'
    },
    'country.Japan': {
        [LANGUAGES.ZH]: '日本',
        [LANGUAGES.EN]: 'Japan'
    },
    'country.United Kingdom': {
        [LANGUAGES.ZH]: '英国',
        [LANGUAGES.EN]: 'United Kingdom'
    },
    'country.Germany': {
        [LANGUAGES.ZH]: '德国',
        [LANGUAGES.EN]: 'Germany'
    },
    'country.France': {
        [LANGUAGES.ZH]: '法国',
        [LANGUAGES.EN]: 'France'
    },
    'country.Canada': {
        [LANGUAGES.ZH]: '加拿大',
        [LANGUAGES.EN]: 'Canada'
    },
    'country.Australia': {
        [LANGUAGES.ZH]: '澳大利亚',
        [LANGUAGES.EN]: 'Australia'
    },
    'country.India': {
        [LANGUAGES.ZH]: '印度',
        [LANGUAGES.EN]: 'India'
    },
    'country.Brazil': {
        [LANGUAGES.ZH]: '巴西',
        [LANGUAGES.EN]: 'Brazil'
    },
    'country.Russia': {
        [LANGUAGES.ZH]: '俄罗斯',
        [LANGUAGES.EN]: 'Russia'
    },
    'country.South Korea': {
        [LANGUAGES.ZH]: '韩国',
        [LANGUAGES.EN]: 'South Korea'
    },
    'country.Italy': {
        [LANGUAGES.ZH]: '意大利',
        [LANGUAGES.EN]: 'Italy'
    },
    'country.Spain': {
        [LANGUAGES.ZH]: '西班牙',
        [LANGUAGES.EN]: 'Spain'
    },
    'country.Mexico': {
        [LANGUAGES.ZH]: '墨西哥',
        [LANGUAGES.EN]: 'Mexico'
    },
    'country.Indonesia': {
        [LANGUAGES.ZH]: '印度尼西亚',
        [LANGUAGES.EN]: 'Indonesia'
    },
    'country.Netherlands': {
        [LANGUAGES.ZH]: '荷兰',
        [LANGUAGES.EN]: 'Netherlands'
    },
    'country.Saudi Arabia': {
        [LANGUAGES.ZH]: '沙特阿拉伯',
        [LANGUAGES.EN]: 'Saudi Arabia'
    },
    'country.Switzerland': {
        [LANGUAGES.ZH]: '瑞士',
        [LANGUAGES.EN]: 'Switzerland'
    },
    'country.Turkey': {
        [LANGUAGES.ZH]: '土耳其',
        [LANGUAGES.EN]: 'Turkey'
    },
    'country.Taiwan': {
        [LANGUAGES.ZH]: '台湾',
        [LANGUAGES.EN]: 'Taiwan'
    },
    'country.Sweden': {
        [LANGUAGES.ZH]: '瑞典',
        [LANGUAGES.EN]: 'Sweden'
    },
    'country.Poland': {
        [LANGUAGES.ZH]: '波兰',
        [LANGUAGES.EN]: 'Poland'
    },
    'country.Belgium': {
        [LANGUAGES.ZH]: '比利时',
        [LANGUAGES.EN]: 'Belgium'
    },
    'country.Thailand': {
        [LANGUAGES.ZH]: '泰国',
        [LANGUAGES.EN]: 'Thailand'
    },
    'country.Ireland': {
        [LANGUAGES.ZH]: '爱尔兰',
        [LANGUAGES.EN]: 'Ireland'
    },
    'country.Austria': {
        [LANGUAGES.ZH]: '奥地利',
        [LANGUAGES.EN]: 'Austria'
    },
    'country.Norway': {
        [LANGUAGES.ZH]: '挪威',
        [LANGUAGES.EN]: 'Norway'
    },
    'country.United Arab Emirates': {
        [LANGUAGES.ZH]: '阿联酋',
        [LANGUAGES.EN]: 'United Arab Emirates'
    },
    'country.Israel': {
        [LANGUAGES.ZH]: '以色列',
        [LANGUAGES.EN]: 'Israel'
    },
    'country.Denmark': {
        [LANGUAGES.ZH]: '丹麦',
        [LANGUAGES.EN]: 'Denmark'
    },
    'country.Singapore': {
        [LANGUAGES.ZH]: '新加坡',
        [LANGUAGES.EN]: 'Singapore'
    },
    'country.Malaysia': {
        [LANGUAGES.ZH]: '马来西亚',
        [LANGUAGES.EN]: 'Malaysia'
    },
    'country.Hong Kong': {
        [LANGUAGES.ZH]: '香港',
        [LANGUAGES.EN]: 'Hong Kong'
    },
    'country.Philippines': {
        [LANGUAGES.ZH]: '菲律宾',
        [LANGUAGES.EN]: 'Philippines'
    },
    'country.Vietnam': {
        [LANGUAGES.ZH]: '越南',
        [LANGUAGES.EN]: 'Vietnam'
    },

    // 行业名称翻译
    'industry.Technology': {
        [LANGUAGES.ZH]: '科技',
        [LANGUAGES.EN]: 'Technology'
    },
    'industry.Healthcare': {
        [LANGUAGES.ZH]: '医疗保健',
        [LANGUAGES.EN]: 'Healthcare'
    },
    'industry.Finance': {
        [LANGUAGES.ZH]: '金融',
        [LANGUAGES.EN]: 'Finance'
    },
    'industry.Manufacturing': {
        [LANGUAGES.ZH]: '制造业',
        [LANGUAGES.EN]: 'Manufacturing'
    },
    'industry.Retail': {
        [LANGUAGES.ZH]: '零售',
        [LANGUAGES.EN]: 'Retail'
    },
    'industry.Real Estate': {
        [LANGUAGES.ZH]: '房地产',
        [LANGUAGES.EN]: 'Real Estate'
    },
    'industry.Media & Entertainment': {
        [LANGUAGES.ZH]: '媒体与娱乐',
        [LANGUAGES.EN]: 'Media & Entertainment'
    },
    'industry.Telecommunications': {
        [LANGUAGES.ZH]: '电信',
        [LANGUAGES.EN]: 'Telecommunications'
    },
    'industry.Automotive': {
        [LANGUAGES.ZH]: '汽车',
        [LANGUAGES.EN]: 'Automotive'
    },
    'industry.Agriculture': {
        [LANGUAGES.ZH]: '农业',
        [LANGUAGES.EN]: 'Agriculture'
    },
    'industry.Construction': {
        [LANGUAGES.ZH]: '建筑',
        [LANGUAGES.EN]: 'Construction'
    },
    'industry.Transportation': {
        [LANGUAGES.ZH]: '交通运输',
        [LANGUAGES.EN]: 'Transportation'
    },
    'industry.Hospitality': {
        [LANGUAGES.ZH]: '酒店服务',
        [LANGUAGES.EN]: 'Hospitality'
    },
    'industry.Wholesale & Distribution': {
        [LANGUAGES.ZH]: '批发与分销',
        [LANGUAGES.EN]: 'Wholesale & Distribution'
    },
    'industry.Energy & Utilities': {
        [LANGUAGES.ZH]: '能源与公用事业',
        [LANGUAGES.EN]: 'Energy & Utilities'
    },
    'industry.Business Services': {
        [LANGUAGES.ZH]: '商业服务',
        [LANGUAGES.EN]: 'Business Services'
    },
    'industry.Education': {
        [LANGUAGES.ZH]: '教育',
        [LANGUAGES.EN]: 'Education'
    },
    'industry.科技': {
        [LANGUAGES.ZH]: '科技',
        [LANGUAGES.EN]: 'Technology'
    },
    'enterprise.matched.count': {
        [LANGUAGES.ZH]: '总企业数量: {count} ',
        [LANGUAGES.EN]: 'Total number of enterprises: {count}'
    },
    'email.email.add': {
        [LANGUAGES.ZH]: '添加邮箱',
        [LANGUAGES.EN]: 'Add Email'
    },
    'email.email.config': {
        [LANGUAGES.ZH]: '邮箱配置',
        [LANGUAGES.EN]: 'Email Configuration'
    },
    'email.email.provider': {
        [LANGUAGES.ZH]: '邮箱发行商',
        [LANGUAGES.EN]: 'Email Provider'
    },
    'email.email.address': {
        [LANGUAGES.ZH]: '邮箱地址',
        [LANGUAGES.EN]: 'Email Address'
    },
    'email.email.status': {
        [LANGUAGES.ZH]: '状态',
        [LANGUAGES.EN]: 'Status'
    },
    'email.email.status.verified': {
        [LANGUAGES.ZH]: '已验证',
        [LANGUAGES.EN]: 'Verified'
    },
    'email.email.status.pending': {
        [LANGUAGES.ZH]: '待验证',
        [LANGUAGES.EN]: 'Pending'
    },
    'email.email.status.failed': {
        [LANGUAGES.ZH]: '验证失败',
        [LANGUAGES.EN]: 'Failed'
    },
    'email.email.add.time': {
        [LANGUAGES.ZH]: '添加时间',
        [LANGUAGES.EN]: 'Add Time'
    },
    'email.email.actions': {
        [LANGUAGES.ZH]: '操作',
        [LANGUAGES.EN]: 'Actions'
    },
    'email.email.verify': {
        [LANGUAGES.ZH]: '验证',
        [LANGUAGES.EN]: 'Verify'
    },
    'email.email.verify.again': {
        [LANGUAGES.ZH]: '重新验证',
        [LANGUAGES.EN]: 'Verify Again'
    },
    'email.email.set.default': {
        [LANGUAGES.ZH]: '设为默认',
        [LANGUAGES.EN]: 'Set as Default'
    },
    'email.email.delete': {
        [LANGUAGES.ZH]: '删除',
        [LANGUAGES.EN]: 'Delete'
    },
    'email.email.set.default.success': {
        [LANGUAGES.ZH]: '已设置为默认邮箱',
        [LANGUAGES.EN]: 'Set as default email successfully'
    },
    'email.email.delete.confirm': {
        [LANGUAGES.ZH]: '确定要删除该邮箱配置吗？',
        [LANGUAGES.EN]: 'Are you sure you want to delete this email configuration?'
    },
    'email.email.delete.title': {
        [LANGUAGES.ZH]: '删除确认',
        [LANGUAGES.EN]: 'Delete Confirmation'
    },
    'email.email.delete.yes': {
        [LANGUAGES.ZH]: '确定',
        [LANGUAGES.EN]: 'Confirm'
    },
    'email.email.delete.no': {
        [LANGUAGES.ZH]: '取消',
        [LANGUAGES.EN]: 'Cancel'
    },
    'email.email.delete.success': {
        [LANGUAGES.ZH]: '邮箱配置删除成功',
        [LANGUAGES.EN]: 'Email configuration deleted successfully'
    },
    'email.email.delete.error': {
        [LANGUAGES.ZH]: '删除邮箱配置失败，请稍后重试',
        [LANGUAGES.EN]: 'Failed to delete email configuration, please try again later'
    },
    'email.email.deleting': {
        [LANGUAGES.ZH]: '正在删除...',
        [LANGUAGES.EN]: 'Deleting...'
    },
    'email.email.verify.instructions': {
        [LANGUAGES.ZH]: '请验证您的邮箱',
        [LANGUAGES.EN]: 'Please Verify Your Mailbox'
    },
    'email.email.verify.description': {
        [LANGUAGES.ZH]: '请输入您的邮箱授权码进行验证。对于不同的邮箱服务商，您可能需要在邮箱设置中获取专用授权码。',
        [LANGUAGES.EN]: 'Please enter your mailbox authorization code for verification. For different mailbox service providers, you may need to obtain a dedicated authorization code in your mailbox settings.'
    },
    'email.email.form.auth.placeholder': {
        [LANGUAGES.ZH]: '请输入邮箱授权码',
        [LANGUAGES.EN]: 'Please enter mailbox authorization code'
    },
    'email.email.auth.tip': {
        [LANGUAGES.ZH]: '授权码通常在邮箱服务商的安全设置中获取，不同于您的登录密码。',
        [LANGUAGES.EN]: 'The authorization code is usually obtained from the security settings of your mailbox service provider, which is different from your login password.'
    },
    'email.email.verify.now': {
        [LANGUAGES.ZH]: '立即验证',
        [LANGUAGES.EN]: 'Verify Now'
    },
    'email.email.later': {
        [LANGUAGES.ZH]: '稍后验证',
        [LANGUAGES.EN]: 'Verify Later'
    },
    'email.email.dialog.add': {
        [LANGUAGES.ZH]: '添加邮箱',
        [LANGUAGES.EN]: 'Add Mailbox'
    },
    'email.email.emailProvider': {
        [LANGUAGES.ZH]: '邮箱发行商',
        [LANGUAGES.EN]: 'Mailbox Provider'
    },
    'email.email.select.provider': {
        [LANGUAGES.ZH]: '请选择邮箱发行商',
        [LANGUAGES.EN]: 'Please choose mailbox provider'
    },
    'email.email.QQmailbox': {
        [LANGUAGES.ZH]: 'QQ邮箱',
        [LANGUAGES.EN]: 'QQ mailbox'
    },
    'email.email.163mailbox': {
        [LANGUAGES.ZH]: '163邮箱',
        [LANGUAGES.EN]: '163 mailbox'
    },
    'email.email.126mailbox': {
        [LANGUAGES.ZH]: '126邮箱',
        [LANGUAGES.EN]: '126 mailbox'
    },
    'email.email.Sinamailbox': {
        [LANGUAGES.ZH]: '新浪邮箱',
        [LANGUAGES.EN]: 'Sian mailbox'
    },
    'email.email.Googlemailbox': {
        [LANGUAGES.ZH]: '谷歌邮箱',
        [LANGUAGES.EN]: 'Google mailbox'
    },
    'email.email.Yahoomailbox': {
        [LANGUAGES.ZH]: 'Yahoo 邮箱',
        [LANGUAGES.EN]: 'Yahoo mailbox'
    },
    'email.email.Outlookmailbox': {
        [LANGUAGES.ZH]: 'Outlook 邮箱',
        [LANGUAGES.EN]: 'Outlook mailbox'
    },
    'email.outlook.oauth.verify': {
        [LANGUAGES.ZH]: 'Outlook OAuth认证',
        [LANGUAGES.EN]: 'Outlook OAuth Verification'
    },
    'email.outlook.oauth.info': {
        [LANGUAGES.ZH]: '请填写Outlook OAuth认证信息',
        [LANGUAGES.EN]: 'Please fill in your outlook authentication information'
    },
    'email.outlook.oauth.azure.info': {
        [LANGUAGES.ZH]: '您需要在Azure Portal中注册应用并获取以下信息',
        [LANGUAGES.EN]: 'You need to register the app in azure portal and get the following information'
    },
    'email.outlook.oauth.clientId.info': {
        [LANGUAGES.ZH]: '请输入Client ID',
        [LANGUAGES.EN]: 'Please enter client id'
    },
    'email.outlook.oauth.tenantId.info': {
        [LANGUAGES.ZH]: '请输入Tenant ID',
        [LANGUAGES.EN]: 'Please enter tenant id'
    },
    'email.outlook.oauth.redirectUri.info': {
        [LANGUAGES.ZH]: '可选',
        [LANGUAGES.EN]: 'Optional'
    },
    'email.outlook.oauth.clientSecret.info': {
        [LANGUAGES.ZH]: '请输入Client Secret',
        [LANGUAGES.EN]: 'Please enter client secret'
    },
    'email.outlook.oauth.hint.info': {
        [LANGUAGES.ZH]: '提示: 您需要在Azure Portal中注册应用并获取以上信息。',
        [LANGUAGES.EN]: 'Hint: You need to register the app in azure portal and get the above information'
    },
    'email.email.form.eamailConfig.select': {
        [LANGUAGES.ZH]: '选择邮箱发行商可以帮助系统更准确地配置邮箱服务器',
        [LANGUAGES.EN]: 'Choosing an mailbox provider can help the system configure the email server more accurately.'
    },
    'eamil.detial.preview': {
        [LANGUAGES.ZH]: '预览文件',
        [LANGUAGES.EN]: 'Preview File'
    },
    'eamil.detial.download': {
        [LANGUAGES.ZH]: '下载文件',
        [LANGUAGES.EN]: 'Download File'
    },
    'email.import.recipients': {
        [LANGUAGES.ZH]: '导入收件人',
        [LANGUAGES.EN]: 'Import Recipients'
    },
    'email.confirm': {
        [LANGUAGES.ZH]: '确定',
        [LANGUAGES.EN]: 'Confirm'
    },
    'email.verify': {
        [LANGUAGES.ZH]: '验证',
        [LANGUAGES.EN]: 'Verification'
    },
    'email.cancel': {
        [LANGUAGES.ZH]: '取消',
        [LANGUAGES.EN]: 'Cancel'
    },
    'email.email.form.address': {
        [LANGUAGES.ZH]: '邮箱地址',
        [LANGUAGES.EN]: 'Mailbox Address'
    },
    'email.email.form.address.placeholder': {
        [LANGUAGES.ZH]: '请输入邮箱地址',
        [LANGUAGES.EN]: 'Please enter mailbox address'
    },
    'email.email.form.address.tip': {
        [LANGUAGES.ZH]: '请输入您想要用于发送邮件的邮箱地址，添加后需要进行验证。',
        [LANGUAGES.EN]: 'Please enter the mailbox address you want to use for sending emails. Verification is required after adding.'
    },
    'email.email.cancel': {
        [LANGUAGES.ZH]: '取消',
        [LANGUAGES.EN]: 'Cancel'
    },
    'email.email.confirm': {
        [LANGUAGES.ZH]: '确定',
        [LANGUAGES.EN]: 'Confirm'
    },
    'email.email.add.success': {
        [LANGUAGES.ZH]: '邮箱添加成功',
        [LANGUAGES.EN]: 'Mailbox added successfully'
    },
    'email.email.error.format': {
        [LANGUAGES.ZH]: '邮箱格式不正确',
        [LANGUAGES.EN]: 'Incorrect mailbox format'
    },
    'email.email.error.empty.auth': {
        [LANGUAGES.ZH]: '授权码不能为空',
        [LANGUAGES.EN]: 'Authorization code cannot be empty'
    },
    'email.email.verify.success': {
        [LANGUAGES.ZH]: '邮箱验证成功',
        [LANGUAGES.EN]: 'Mailbox verified successfully'
    },
    'email.email.verify.failed': {
        [LANGUAGES.ZH]: '邮箱验证失败',
        [LANGUAGES.EN]: 'Mailbox verification failed'
    },
    'email.email.verify.error': {
        [LANGUAGES.ZH]: '验证过程中出现错误',
        [LANGUAGES.EN]: 'Error occurred during verification'
    },
    'email.email.no.data': {
        [LANGUAGES.ZH]: '暂无邮箱配置',
        [LANGUAGES.EN]: 'No mailbox configuration yet'
    },
    'email.email.add.first': {
        [LANGUAGES.ZH]: '添加第一个邮箱',
        [LANGUAGES.EN]: 'Add your first mailbox'
    },
    'email.email.edit': {
        [LANGUAGES.ZH]: '修改',
        [LANGUAGES.EN]: 'Edit'
    },
    'email.email.dialog.edit': {
        [LANGUAGES.ZH]: '修改邮箱配置',
        [LANGUAGES.EN]: 'Edit Mailbox Configuration'
    },
    // 'email.email.dialog.verify.again': {
    //   [LANGUAGES.ZH]: '重新验证邮箱',
    //   [LANGUAGES.EN]: 'Verify Email Again'
    // },
    'email.email.update.success': {
        [LANGUAGES.ZH]: '邮箱配置修改成功',
        [LANGUAGES.EN]: 'Mailbox configuration updated successfully'
    },
    'email.email.update.error': {
        [LANGUAGES.ZH]: '修改邮箱配置失败，请稍后重试',
        [LANGUAGES.EN]: 'Failed to update mailbox configuration, please try again later'
    },
    'email.email.edit.tip': {
        [LANGUAGES.ZH]: '修改邮箱配置后，需要重新验证邮箱',
        [LANGUAGES.EN]: 'After modifying the mailbox configuration, you need to verify the mailbox again'
    },
    'email.email.verify.again.tip': {
        [LANGUAGES.ZH]: '重新验证邮箱可确保您的邮箱配置正常工作',
        [LANGUAGES.EN]: 'Verifying your mailbox again ensures your mailbox configuration works properly'
    },
    'email.email.address.required': {
        [LANGUAGES.ZH]: '请输入邮箱地址',
        [LANGUAGES.EN]: 'Please enter mailbox address'
    },
    'email.email.auth.required': {
        [LANGUAGES.ZH]: '请输入授权码',
        [LANGUAGES.EN]: 'Please enter authorization code'
    },
    'email.email.form.auth': {
        [LANGUAGES.ZH]: '授权码',
        [LANGUAGES.EN]: 'Authorization Code'
    },
    'email.send.error.no.auth.code': {
        [LANGUAGES.ZH]: '找不到选定邮箱的授权码，请重新验证邮箱',
        [LANGUAGES.EN]: 'Cannot find authorization code for the selected mailbox, please verify your mailbox again'
    },
    'email.send.error.recipient': {
        [LANGUAGES.ZH]: '请输入收件人邮箱地址',
        [LANGUAGES.EN]: 'Please enter recipient mailbox address'
    },
    'email.email.dialog.verify.again': {
        [LANGUAGES.ZH]: '重新验证邮箱',
        [LANGUAGES.EN]: 'Verify Email Again'
    },
    'email.email.dialog.verify': {
        [LANGUAGES.ZH]: '请验证您的邮箱',
        [LANGUAGES.EN]: 'Please Verify Your Mailbox'
    },
    'email.email.verify.instructions': {
        [LANGUAGES.ZH]: '请输入您的邮箱授权码进行验证。对于不同的邮箱服务商，您可能需要在邮箱设置中获取专用授权码。',
        [LANGUAGES.EN]: 'Please enter your mailbox authorization code for verification. For different mailbox providers, you may need to get a specific authorization code from your mailbox settings.'
    },
    'email.email.verify.auth': {
        [LANGUAGES.ZH]: '请输入邮箱授权码',
        [LANGUAGES.EN]: 'Please Enter Mailbox Authorization Code'
    },
    'email.email.verify.auth.tip': {
        [LANGUAGES.ZH]: '授权码通常在邮箱服务商的安全设置中获取，不同于您的登录密码。',
        [LANGUAGES.EN]: 'The authorization code is usually obtained from the security settings of your mailbox provider, different from your login password.'
    },
    'email.email.verify.wait.tip': {
        [LANGUAGES.ZH]: '验证过程可能需要1-2分钟，请耐心等待，不要关闭窗口。',
        [LANGUAGES.EN]: 'The verification process may take 1-2 minutes, please wait patiently and do not close the window.'
    },
    'email.detail.delivered.count': {
        [LANGUAGES.ZH]: '发送件数',
        [LANGUAGES.EN]: 'Sent Count'
    },
    'email.detail.total.recipients': {
        [LANGUAGES.ZH]: '总收件人数',
        [LANGUAGES.EN]: 'Total Recipients'
    },
})

/**
 * 获取当前语言
 * @returns {string} 当前语言代码
 */
export function getCurrentLanguage() {
    return currentLanguage.value
}

/**
 * 切换语言
 * @param {string} lang - 要切换的语言代码
 * @returns {boolean} 是否切换成功
 */
export function changeLanguage(lang) {
    // 验证语言有效性
    if (!Object.values(LANGUAGES).includes(lang)) {
        return false
    }

    try {
        // 更新当前语言
        currentLanguage.value = lang

        // 保存到本地存储 - 使用统一的键名
        localStorage.setItem(STORAGE_KEY, lang)

        // 更新HTML文档的lang属性
        document.documentElement.setAttribute('lang', lang)

        // 分发语言改变事件
        const event = new CustomEvent('language-changed', {detail: {lang}})
        document.dispatchEvent(event)

        return true
    } catch (error) {
        return false
    }
}

/**
 * 翻译文本
 * @param {string} key - 翻译键
 * @param {Object} [params] - 可选的参数替换
 * @returns {string} 翻译后的文本
 */
export function t(key, params = {}) {
    // 获取当前语言的翻译
    const translation = translations[key]?.[currentLanguage.value]

    // 如果没有找到翻译，返回键名
    if (!translation) {
        return key
    }

    // 替换参数
    let result = translation
    Object.entries(params).forEach(([param, value]) => {
        result = result.replace(new RegExp(`{${param}}`, 'g'), value)
    })

    return result
}

/**
 * 添加新的翻译
 * @param {string} key - 翻译键
 * @param {Object} translationObj - 翻译对象 { [lang]: value }
 */
export function addTranslation(key, translationObj) {
    if (translations[key]) {
    }

    translations[key] = translationObj
}

// 导出响应式的currentLanguage变量，供组件直接使用
export {currentLanguage}

export default {
    t,
    getCurrentLanguage,
    changeLanguage,
    addTranslation,
    LANGUAGES,
    currentLanguage
}

// 自诊断函数
export function diagnoseTranslationSystem() {
    // 测试几个关键翻译
    const testKeys = [
        'dashboard.myMarketing',
        'dashboard.emailStats',
        'dashboard.updateTime'
    ]

    testKeys.forEach(key => {
        const translation = t(key)
    })

    return {
        defaultLanguage: DEFAULT_LANGUAGE,
        currentLanguage: currentLanguage.value,
        storageLanguage: localStorage.getItem(STORAGE_KEY),
        translationKeysCount: Object.keys(translations).length,
        testResults: testKeys.map(key => ({key, result: t(key)}))
    }
}

// 将翻译数据暴露到全局，供LinkedIn页面的中文翻译函数使用
if (typeof window !== 'undefined') {
    window.i18nTranslations = translations;
}